import * as THREE from "three"
import { OrbitControls } from "three/addons/controls/OrbitControls.js"
import { TransformControls } from "three/addons/controls/TransformControls.js"
import { frameFormatRatio, normalizeFrameFormat } from "@/lib/frame-format-core"
import {
  MAX_PATH_DURATION_MS,
  SAMPLE_INTERVAL_MS,
  appendCameraSample,
  aimSampleAtLook,
  aimSamplesAtLook,
  applyLookKeys,
  attachLookKeys,
  cameraPathDurationMs,
  clipBrokenCameraPath,
  cloneCameraPath,
  clonePathLook,
  emptyCameraPath,
  hasCameraPath,
  interpolateCameraPath,
  lookTimeMs,
  type PlacedAim,
  recordedLookHasTarget,
  remapStoredLookKeys,
  replaceLookRange,
  retimeCameraPath,
  sampleLookKeys,
  scaleLookTargets,
  type CameraPath,
  type CameraPathSample,
  type PathEditUi,
  type PathLook,
  type PathLookKey,
  type PathPoint,
  type PathVec3,
} from "@/lib/location-scene-3d/camera-path"
import { scaleSpeedProfile, type CameraSpeedProfile } from "@/lib/location-scene-3d/camera-speed"
import {
  cameraPathTakesFull,
  cloneCameraPathTakeSet,
  newCameraPathTakeId,
  nextCameraPathTakeNumber,
  restoreCameraPathsForHistory,
  type CameraPathHistoryPatch,
  type CameraPathTake,
} from "@/lib/location-scene-3d/camera-path-takes"
import { startRecordedPathFit } from "@/lib/location-scene-3d/camera-path-fit-client"
import { createPathFitGate, pathNeedsFit } from "@/lib/location-scene-3d/camera-path-fit-gate"
import { CameraPathOverlay, pathCurveHex } from "@/lib/location-scene-3d/camera-path-overlay"
import {
  CAMERA_PLACE_DOT_LIFT,
  CAMERA_PLACE_DOT_OPACITY,
  CAMERA_PLACE_DOT_RADIUS,
  CAMERA_PLACE_HOVER_SCALE,
  cameraPlaceAimChanged,
  cameraPlaceAimFromDrag,
  cameraPlaceEyeY,
  cameraPlaceHitIndex,
  cameraPlaceMarkerIndex,
  cameraPlaceMarkers,
  cameraPlaceMonitorFrameKey,
  cameraPoseAtPlace,
  replaceFirstPathPoint,
  trajectoryDrawWithSeed,
  type CameraPlaceMarker,
} from "@/lib/location-scene-3d/camera-place"
import {
  DEFAULT_SPLINE_DURATION_MS,
  MAX_PATH_POINTS,
  MIN_SPLINE_DURATION_MS,
  RECORDED_FIT_EPSILON_M,
  authoredPositionAt,
  bakeSplinePath,
  clampSplineDuration,
  clampTangent,
  axisDragOffset,
  displayedTangent,
  handleDragTangent,
  closestOnCurve,
  deletePoint,
  directionFromYawPitch,
  drawPointFromClick,
  rayHorizontalPlaneHit,
  insertPoint,
  lookYawPitch,
  splineSampleLook,
  yawPitchFromDirection,
  curvePolyline,
  resetPointTangent,
} from "@/lib/location-scene-3d/camera-path-spline"
import {
  bakeObjectPath,
  cloneObjectPath,
  defaultObjectPathDuration,
  objectPathAsCameraPath,
  objectPathDurationMs,
  objectPathFollows,
  objectPathHasContent,
  objectPathPolyline,
  objectPathStart,
  posedObjectPosition,
  sceneClockMs,
  shiftObjectPath,
  type ObjectPath,
  type ObjectPathCarrier,
} from "@/lib/location-scene-3d/object-path"
import {
  createRecordMotionClock,
  markSampleAppended,
  recordMotionEpsPos,
  tickRecordMotionClock,
  type RecordMotionClock,
} from "@/lib/location-scene-3d/record-motion-clock"
import { FLOOR_EXTENT, PRIMITIVE_COLOR } from "@/lib/location-scene-3d/palette"
import {
  applyLightPaletteRenderSettings,
  createLightPaletteRig,
  diagnose as diagnoseLightPalette,
  hexToNumber,
  measureReference as measureLightPaletteReference,
  PALETTE as LIGHT_PALETTE,
} from "@/lib/location-scene-3d/light-palette"
import type { SceneCharacterFigure } from "@/lib/location-scene-3d/character-figure"
import { createCharacterVisual, preloadCharacterFigures } from "@/lib/location-scene-3d/character-humanoid"
import { createPrimitiveMesh } from "@/lib/location-scene-3d/primitive-mesh"
import { DEVICE_LOOK_PITCH_MAX } from "@/lib/location-scene-3d/device-orientation-look"
import {
  containFitSize,
  frameDisplayVFov,
  frameViewfinderRect,
  previzExportSize,
  sceneStillExportSize,
} from "@/lib/location-scene-3d/previz-export-size"
import { effectiveFlySpeed, FLY_SPEED_REFERENCE_SPAN } from "@/lib/location-scene-3d/fly-speed"
import {
  skyAmbientFactorForLampIntensity,
  lightMarkerMinY,
  lightMarkerRadius,
  pointLightIntensityRange,
  pointLightParams,
  POINT_SHADOW_BIAS,
  pointShadowNearForSpan,
} from "@/lib/location-scene-3d/light-marker"
import {
  applyWallTileColors,
  faceTileRectangle,
  faceSideFromKey,
  faceShiftArrowFlip,
  objectFaceKey,
  outwardStep,
  parseCellFaceKey,
  platformIsSet,
  sectorPaintCells,
  sectorRectangle,
  sectorSizeMeters,
  sectorWorldCenter,
  walkableFloorSectors,
  worldToSectorIndex,
  type FaceTile,
} from "@/lib/location-scene-3d/platform"
import {
  createPlatformLayer,
  defaultLightPosition,
  lightPosition,
  type PlatformHit,
  type PlatformLayer,
  type PlatformLayerState,
  type PlatformLightSide,
  type PlatformTool,
} from "@/lib/location-scene-3d/platform-layer"
import {
  classifyPlatformDown,
  isPathPending,
  isPlatformVoidHit,
  resolvePlatformMove,
  resolvePlatformUp,
  type PlatformPending,
  type PlatformUpEffect,
} from "@/lib/location-scene-3d/platform-pointer"
import { createSceneThemeRig, sceneThemeById } from "@/lib/location-scene-3d/scene-theme"
import { createSkyDome } from "@/lib/location-scene-3d/sky-dome"
import {
  matchViewPreset,
  sceneToWidget,
  VIEW_CUBE_ORBIT_RAD_PER_PX,
  VIEW_ORBIT_PITCH_MAX,
  VIEW_PRESET_DIRS,
  type SceneViewPreset,
  type Vec3,
} from "@/lib/location-scene-3d/view-presets"
import {
  DEFAULT_CAMERA_STATE,
  defaultSceneEnvironment,
  normalizeCameraState,
  normalizeSceneEnvironment,
  normalizeSceneLayout,
  type DropViewHint,
  type SceneCameraState,
  type SceneEnvironmentState,
  type SceneGizmoMode,
  type SceneLayoutState,
  type SceneLightState,
  type SceneNavMode,
  type SceneObjectState,
  type ScenePrimitiveShape,
} from "@/lib/location-scene-3d/scene-state"
import {
  applyGroup,
  applyUngroup,
  groupPeerUids,
  isCompleteGroup,
  plainClickSelection,
  selectionPopoverAction,
  shiftClickSelection,
} from "@/lib/location-scene-3d/object-groups"
import {
  CAMERA_REFERENCE_SPAN,
  cameraNearForSpan,
  cameraPoseForSpan,
  cameraPoseMatchesDefault,
  flyMinY,
  lightDragMetersPerPx,
  lightGizmoGap,
  sceneTravelBound,
  spanShiftNeedsReframe,
} from "@/lib/location-scene-3d/scene-scale"

const PITCH_MAX = VIEW_ORBIT_PITCH_MAX
/** Ignore tiny orientation noise when arming record from device look. */
const DEVICE_LOOK_RECORD_EPS = 0.01
const LOOK_YAW_RATE_RECORD_EPS = 1e-4

const KEYMAP: Record<string, string> = {
  KeyW: "f",
  ArrowUp: "f",
  KeyS: "b",
  ArrowDown: "b",
  KeyA: "l",
  ArrowLeft: "l",
  KeyD: "r",
  ArrowRight: "r",
  KeyR: "u",
  KeyF: "d",
}

export type { SceneViewPreset } from "@/lib/location-scene-3d/view-presets"

export type CameraPathChangeOpts = {
  /** Линию показать, в историю не писать — ждём подгонку. */
  pendingFit?: boolean
  /** Создание, удаление или переключение: шаг истории не писать. */
  skipHistory?: boolean
  /** Создание или удаление: сбросить повтор. Переключение его не трогает. */
  clearRedo?: boolean
}

export type CreateScene3dEngineOptions = {
  /** Свой холст (постер вне экрана). Иначе движок создаёт холст и кладёт его в stage. */
  canvas?: HTMLCanvasElement
  stage: HTMLElement
  onObjectsChange?: (objects: SceneObjectState[]) => void
  onSelectionChange?: (uids: string[]) => void
  onLightSelectionChange?: (selected: boolean) => void
  onPlatformSectors?: (sectors: { x: number; z: number }[], additive: boolean, opts?: { prelude?: boolean }) => void
  onPlatformFace?: (faceKey: string | null, additive?: boolean) => void
  onPlatformFaceTiles?: (tiles: FaceTile[], additive: boolean) => void
  /** Intentional clear of platform selection (not marquee prelude). */
  onPlatformClear?: () => void
  onPlatformToolCleared?: () => void
  onPlatformToolActivated?: (tool: "sector" | "surface") => void
  onLightChange?: (light: SceneLightState) => void
  /** Short-click on void sky/backdrop; null dismisses environment chrome. */
  onEnvironmentActivate?: (screen: { x: number; y: number } | null) => void
  onCameraPathChange?: (path: CameraPath, opts?: CameraPathChangeOpts) => void
  onPlaybackChange?: (playing: boolean) => void
  onRecordingChange?: (recording: boolean) => void
  /** Запись взведена: таймер запустится с первым нажатием клавиши движения (W). */
  onRecordArmChange?: (armed: boolean) => void
  /** Имя нового дубля перед записью. n — следующий номер после «Дубль N» / Take N. */
  cameraPathTakeName?: (n: number) => string
  /** Запись или «+» при 32 дублях: слот не создаётся. */
  onCameraPathTakesFull?: () => void
  /** Клик по точке пола поставил стартовую позу. Выше записи пути, чтобы автосохранение увидело старт. */
  onCameraPlaced?: (cam: SceneCameraState) => void
  /** Движок сам выключил режим (траектория, запись, телефон). */
  onCameraPlaceModeChange?: (active: boolean) => void
  /** Живое превью поставленной камеры появилось или исчезло. Не на каждый сдвиг мыши. */
  onCameraPlacePreview?: (active: boolean) => void
  /** В выбранном дубле уже есть пролёт: запись не стартует, пока UI не подтвердит перезапись. */
  onRecordOverwriteNeeded?: () => void
  /** Каждый кадр при воспроизведении/скрабе: позиция плейхеда в мс. */
  onPathTimeChange?: (ms: number) => void
  /** Каждый кадр при записи: прошедшее время в мс. */
  onRecordTick?: (elapsedMs: number) => void
  /** Права на запись пролёта: без него пробел не тоглит запись (viewer). */
  canRecord?: boolean
  /** Права на редактирование сцены: direct drag объектов, delete. */
  canEdit?: boolean
  /** Project/shot frame format — fixed MediaRecorder buffer aspect on export. */
  frameFormat?: string | null
  /**
   * Each frame: camera orientation in view-cube Z-up space, plus the preset the look matches.
   * Must not set React state — the cube reads it imperatively.
   */
  onViewOrientation?: (direction: Vec3, up: Vec3, matched: SceneViewPreset | null) => void
  onPathEditUi?: (ui: PathEditUi) => void
  /** Orbit gesture inside trajectory mode finished — persist the editor camera. */
  onCameraPoseCommit?: () => void
}

export type PathPlayRange = { fromMs?: number; toMs?: number }

export type PlatformSelectionToolbarAnchorScreen = {
  x: number
  y: number
  kind: "sector" | "face"
  side?: "left" | "right"
  /** True when wall outward points right on screen — put extrude on →. */
  faceArrowFlip?: boolean
}

export type Scene3dEngine = {
  addObject: (obj: SceneObjectState) => void
  removeObject: (uid: string) => void
  /** Drop every uid, then one objects emit. Group unlink runs after the whole batch. */
  removeObjects: (uids: readonly string[]) => void
  /** Paint the object's materials. `emit` persists once; drag preview omits it. */
  setObjectColor: (uid: string, hex: string, opts?: { emit?: boolean }) => void
  /** Swap a hero's mesh. Pose, scale, color, and label stay. */
  setObjectFigure: (uid: string, figure: SceneCharacterFigure) => void
  selectObject: (uid: string | null) => void
  groupSelection: () => void
  ungroupSelection: () => void
  /** Select an object and ease the camera onto it from the current side. */
  frameObject: (uid: string) => void
  getSelectedUid: () => string | null
  getSelectedUids: () => string[]
  /**
   * Stage-local CSS px of a point just above the selected object's name label
   * (for React gizmo stack). Null if nothing selected / labels hidden / off-screen.
   */
  getSelectionGizmoAnchorScreen: () => { x: number; y: number } | null
  getLightGizmoAnchorScreen: () => { x: number; y: number } | null
  getPlatformSelectionToolbarAnchorScreen: () => PlatformSelectionToolbarAnchorScreen | null
  getLightIntensity: () => number
  setLightIntensity: (intensity: number, opts?: { emit?: boolean }) => void
  getLightIntensityRange: () => { min: number; max: number; base: number }
  setGizmoMode: (mode: SceneGizmoMode) => void
  getGizmoMode: () => SceneGizmoMode
  setNavMode: (mode: SceneNavMode) => void
  getNavMode: () => SceneNavMode
  setFov: (fov: number) => void
  getFov: () => number
  setFlySpeed: (speed: number) => void
  getFlySpeed: () => number
  setCameraHeight: (y: number) => void
  getCameraHeight: () => number
  /** Placement hint: free floor near look hit, preferring frustum-visible sectors. */
  dropViewHint: () => DropViewHint
  /** Still at project frame aspect. Labels and gizmo stay off. Light marker only for the tile poster. */
  captureStillBlob: (opts?: { includeLightMarker?: boolean; maxEdge?: number }) => Promise<Blob | null>
  /** Ease the camera onto a cube view around the scene focus. Stops path playback. */
  animateToView: (preset: SceneViewPreset) => void
  /**
   * Ease onto a saved pose (start camera). Same duration as a cube view.
   * `onDone` fires when the flight arrives or is interrupted.
   */
  animateToCameraState: (cam: SceneCameraState, onDone?: () => void) => void
  /** Orbit around the scene focus. `dx`/`dy` are CSS pixels from the view cube. */
  orbitAroundFocus: (dx: number, dy: number) => void
  isViewAnimating: () => boolean
  /** Целевой взгляд fly-камеры (рад), как pointer-look. */
  setFlyLook: (yaw: number, pitch: number) => void
  getFlyLook: () => { yaw: number; pitch: number }
  /** Оси полёта без клавиатуры (паритет с WASD / R F для записи). */
  setFlyInput: (input: SceneFlyInput) => void
  /** Удержание одной оси с оверлея WASD / R F. Не сбрасывает клавиатуру. */
  setFlyHeld: (axis: SceneFlyAxis, held: boolean) => void
  clearFlyInput: () => void
  /** Analog strafe/vertical thrust (speed units); mobile left stick. */
  setFlyAnalogAxes: (axes: { strafe?: number; vertical?: number }) => void
  /** Удержание «вперёд» без клавиатуры (обёртка над setFlyInput). */
  setFlyThrust: (forward: boolean) => void
  /** Непрерывный yaw rad/s (правый стик); 0 = выкл. */
  setLookYawRate: (rateRadPerSec: number) => void
  /** Режим телефон-камеры: fly + без pointer-drag look на canvas. */
  setDeviceLookActive: (active: boolean) => void
  /** Проигрывание в модалке вида из камеры ведёт камеру по дорожке. Полёт не включает. */
  setDeviceLookFollowsPath: (active: boolean) => void
  setPlatformState: (state: PlatformLayerState) => void
  patchSolidFaceColors: (faceKeys: ReadonlyArray<string>, hex: string) => void
  patchWallTileColors: (tiles: ReadonlyArray<FaceTile>, hex: string | null) => void
  clearSolidFaceColors: (faceKeys: ReadonlyArray<string>) => void
  setPlatformTool: (tool: PlatformTool) => void
  setPlatformLight: (side: PlatformLightSide) => void
  setLightState: (light: SceneLightState) => void
  applyEnvironment: (env: SceneEnvironmentState) => void
  getEnvironment: () => SceneEnvironmentState
  getSceneState: () => Pick<
    SceneLayoutState,
    "objects" | "camera_state" | "camera_path" | "camera_paths" | "active_camera_path_id" | "environment"
  >
  applySceneState: (
    state: Pick<SceneLayoutState, "objects" | "camera_state" | "camera_path"> &
      Partial<Pick<SceneLayoutState, "environment" | "camera_paths" | "active_camera_path_id">>,
  ) => void
  /** Только поза камеры (старт пролёта / go-to-start), без смены objects/path. */
  applyCameraState: (cam: SceneCameraState) => void
  startRecording: (opts?: { overwrite?: boolean }) => void
  /** returnHome — плавно в позу первого кадра и включить вид из камеры. */
  stopRecording: (opts?: { returnHome?: boolean }) => void
  isRecording: () => boolean
  isRecordingArmed: () => boolean
  playPath: (range?: PathPlayRange) => void
  stopPlayback: () => void
  /** Плей/пауза как пробел: с текущего места, с начала если пролёт уже у конца. */
  togglePathPlayback: () => void
  /** Проигрывание само дошло до конца (те же 50 мс, что у повторного старта). */
  isPathAtEnd: () => boolean
  isPlaying: () => boolean
  seekPath: (ms: number) => void
  placeAtPathStart: () => void
  replacePath: (path: CameraPath) => void
  getCameraPathTakes: () => { takes: CameraPathTake[]; activeId: string | null }
  addCameraPathTake: (name: string) => string | null
  selectCameraPathTake: (id: string) => void
  renameCameraPathTake: (id: string, name: string) => void
  deleteCameraPathTake: (id: string) => void
  /** Линия шага — в существующий дубль. Без id — только если дубль один. */
  restoreCameraPathTakeFromHistory: (entry: { id: string | null; path: CameraPath }) => void
  /** Несколько линий за один сигнал. Сглаживание, которое ещё шло, сбрасывается. */
  restoreCameraPathTakesFromHistory: (args: { patches: CameraPathHistoryPatch[]; activeId: string | null }) => void
  exportPathVideo: (range?: PathPlayRange & { restoreView?: boolean }) => Promise<Blob>
  /** Идёт запись ролика превиза. Снимок постера в этот момент портит тот же кадр. */
  isExporting: () => boolean
  /** Редактор уже размонтирован, ролик дописывается на скрытом холсте. */
  isExportDetached: () => boolean
  /** Не уничтожать движок: холст уходит из сцены, запись продолжается. */
  detachExport: () => void
  clearPath: () => void
  getCameraPath: () => CameraPath
  /** Текущий подбор кривой закончился или его отменили. */
  whenPathFitSettled: () => Promise<void>
  setTrajectoryMode: (active: boolean) => void
  isTrajectoryMode: () => boolean
  /** Точки по центрам секторов пола. Повторный вызов выключает. false — режим не открылся. */
  setCameraPlaceMode: (active: boolean) => boolean
  /** Рыскание и тангаж одной поставленной точки. null — значок смотрит в −Z. */
  setCameraPlaceAim: (aim: { yaw: number; pitch: number } | null) => void
  /** Маленький кадр поставленной камеры. null — не рисовать. */
  setCameraPlaceMonitor: (canvas: HTMLCanvasElement | null) => void
  isCameraPlaceMode: () => boolean
  /** Путь выделенного объекта. Повтор того же uid выключает. null выключает. */
  setObjectPathEdit: (uid: string | null) => void
  getObjectPathEditUid: () => string | null
  /** Линия пути объекта без точек и правки. Тот же uid ещё раз — скрыть. null — скрыть. */
  showObjectPath: (uid: string | null) => void
  hasSceneMotion: () => boolean
  getSceneClockMs: () => number
  /** Phone layout has no trajectory mode. */
  setPathEditAllowed: (allowed: boolean) => void
  setPathDraw: (draw: boolean) => void
  setPathSmooth: (smooth: boolean) => void
  setPathLook: (look: PathLook) => void
  setPathDuration: (ms: number) => void
  setCameraSpeed: (profile: CameraSpeedProfile) => void
  /** Перепекает путь объекта, даже если режим точек выключен. */
  setObjectSpeed: (uid: string, profile: CameraSpeedProfile) => void
  setObjectPathDuration: (uid: string, ms: number) => void
  /** Снимает путь объекта. Позиция и пролёт камеры остаются. */
  clearObjectPath: (uid: string) => void
  setPathLookThrough: (on: boolean) => void
  isPathLookThrough: () => boolean
  /** Сбросить превью взгляда, не выходя из вида из камеры. */
  discardLookPreview: () => void
  beginLookRecording: () => void
  /** После подтверждения перезаписи: кадр в начале, запись по Пробелу или клику. */
  armLookRewrite: () => void
  /** Сразу пролёт с записью взгляда: то же, что «Перезаписать взгляд» и подтверждение. */
  startLookRewrite: () => void
  cancelLookPass: () => void
  armObservePick: () => void
  resize: () => void
  destroy: () => void
}

export type SceneFlyAxis = "f" | "b" | "l" | "r" | "u" | "d"

export type SceneFlyInput = {
  forward?: boolean
  backward?: boolean
  left?: boolean
  right?: boolean
  up?: boolean
  down?: boolean
}

const FLY_HELD_AXES = new Set<SceneFlyAxis>(["f", "b", "l", "r", "u", "d"])

function clamp(v: number, a: number, b: number) {
  return Math.min(b, Math.max(a, v))
}

function isTypingTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  if (tag === "TEXTAREA" || tag === "SELECT") return true
  if (tag === "INPUT") {
    const type = (target as HTMLInputElement).type
    return type !== "range" && type !== "checkbox" && type !== "radio" && type !== "button" && type !== "submit"
  }
  return false
}

function yawPitchFromCamera(camera: THREE.Camera): { yaw: number; pitch: number } {
  const euler = new THREE.Euler().setFromQuaternion(camera.quaternion, "YXZ")
  return { yaw: euler.y, pitch: euler.x }
}

const LABEL_SPRITE_SCALE = { x: 1.4, y: 0.35 }

function makeLabelSprite(text: string, color: string) {
  const c = document.createElement("canvas")
  c.width = 256
  c.height = 64
  const g = c.getContext("2d")
  if (!g) return null
  g.clearRect(0, 0, 256, 64)
  g.fillStyle = "rgba(20,22,26,0.72)"
  g.roundRect(8, 12, 240, 40, 10)
  g.fill()
  g.fillStyle = color
  g.font = "600 22px ui-sans-serif, system-ui, sans-serif"
  g.textAlign = "center"
  g.textBaseline = "middle"
  g.fillText(text.slice(0, 22), 128, 32)
  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false })
  const sprite = new THREE.Sprite(mat)
  sprite.scale.set(LABEL_SPRITE_SCALE.x, LABEL_SPRITE_SCALE.y, 1)
  sprite.userData.isLabel = true
  return sprite
}

export function createScene3dEngine(opts: CreateScene3dEngineOptions): Scene3dEngine {
  // Humanoid is the spare and the default when figure is missing.
  // Quadruped and biped start in createCharacterVisual when that hero is mounted.
  void preloadCharacterFigures(["humanoid"])
  const { canvas: givenCanvas, stage, canRecord = false, canEdit = false, frameFormat = null } = opts

  let uiMuted = false
  function listen<Args extends unknown[]>(fn: ((...args: Args) => void) | undefined): (...args: Args) => void {
    return (...args) => {
      if (uiMuted) return
      fn?.(...args)
    }
  }
  const onObjectsChange = listen(opts.onObjectsChange)
  const onSelectionChange = listen(opts.onSelectionChange)
  const onLightSelectionChange = listen(opts.onLightSelectionChange)
  const onPlatformSectors = listen(opts.onPlatformSectors)
  const onPlatformFace = listen(opts.onPlatformFace)
  const onPlatformFaceTiles = listen(opts.onPlatformFaceTiles)
  const onPlatformClear = listen(opts.onPlatformClear)
  const onPlatformToolCleared = listen(opts.onPlatformToolCleared)
  const onPlatformToolActivated = listen(opts.onPlatformToolActivated)
  const onLightChange = listen(opts.onLightChange)
  const onEnvironmentActivate = listen(opts.onEnvironmentActivate)
  const onCameraPathChange = listen(opts.onCameraPathChange)
  const onPlaybackChange = listen(opts.onPlaybackChange)
  const onRecordingChange = listen(opts.onRecordingChange)
  const onRecordArmChange = listen(opts.onRecordArmChange)
  const cameraPathTakeName = opts.cameraPathTakeName ?? ((n: number) => `Take ${n}`)
  const onCameraPathTakesFull = listen(opts.onCameraPathTakesFull)
  const onCameraPlaced = listen(opts.onCameraPlaced)
  const onCameraPlaceModeChange = listen(opts.onCameraPlaceModeChange)
  const onCameraPlacePreview = listen(opts.onCameraPlacePreview)
  const onRecordOverwriteNeeded = listen(opts.onRecordOverwriteNeeded)
  const onPathTimeChange = listen(opts.onPathTimeChange)
  const onRecordTick = listen(opts.onRecordTick)
  const onViewOrientation = listen(opts.onViewOrientation)
  const onPathEditUi = listen(opts.onPathEditUi)
  const onCameraPoseCommit = listen(opts.onCameraPoseCommit)

  const canvas = givenCanvas ?? document.createElement("canvas")
  if (!givenCanvas) {
    canvas.tabIndex = 0
    canvas.className = "block h-full w-full outline-none select-none [-webkit-touch-callout:none]"
    canvas.style.touchAction = "none"
    canvas.style.width = "100%"
    canvas.style.height = "100%"
    canvas.addEventListener("contextmenu", (event) => event.preventDefault())
    stage.appendChild(canvas)
  }

  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true })
  renderer.shadowMap.enabled = true
  renderer.shadowMap.type = THREE.PCFSoftShadowMap

  const scene = new THREE.Scene()
  // Render/colorspace settings, fog/background clear, RoomEnvironment PMREM — see docs/location_scene_3d_palette.md.
  const paletteEnvTexture = applyLightPaletteRenderSettings(scene, renderer)
  let environmentState: SceneEnvironmentState = defaultSceneEnvironment()
  let environmentChromeOpen = false
  let themeRig: ReturnType<typeof createSceneThemeRig> | null = null
  let themeKey = ""
  const skyDome = createSkyDome(scene)

  function dismissEnvironmentChrome() {
    environmentChromeOpen = false
    onEnvironmentActivate?.(null)
  }

  let cameraPlaceMonitorFrame = ""
  let cameraPlaceMonitorSceneDirty = true

  function markCameraPlaceMonitorDirty() {
    cameraPlaceMonitorSceneDirty = true
  }

  function syncSceneTheme() {
    markCameraPlaceMonitorDirty()
    if (!themeRig) return
    const theme = sceneThemeById(environmentState.theme)
    const span = platformLayer.span()
    const key = `${theme.id}:${span}`
    if (key === themeKey) {
      themeRig.setMarkup(environmentState.sky_markup)
      return
    }
    themeKey = key
    themeRig.apply(theme, span, {
      hemi: lightRig.hemi,
      key: lightRig.key,
      fill: lightRig.fill,
    })
    const floorMat = floor.material as THREE.MeshStandardMaterial
    floorMat.color.set(theme.blockTop)
    const gridMats = Array.isArray(grid.material) ? grid.material : [grid.material]
    for (const mat of gridMats) {
      ;(mat as THREE.LineBasicMaterial).color.set(theme.blockGrid)
    }
    platformLayer.applyThemeColors({ top: theme.blockTop, side: theme.blockSide, grid: theme.blockGrid })
    themeRig.setMarkup(environmentState.sky_markup)
    paintLightMarker(theme)
    if (pathOverlayMounted) syncPathOverlay()
  }

  function applyEnvironment(env: SceneEnvironmentState) {
    environmentState = normalizeSceneEnvironment(env)
    syncSceneTheme()
  }

  const camera = new THREE.PerspectiveCamera(DEFAULT_CAMERA_STATE.fov, 16 / 9, 0.05, 400)
  camera.rotation.order = "YXZ"
  camera.position.set(...DEFAULT_CAMERA_STATE.pos)
  const pathOverlay = new CameraPathOverlay()
  scene.add(pathOverlay.group)
  let pathOverlayMounted = false
  let overlaySyncX = Number.NaN
  let overlaySyncY = Number.NaN
  let overlaySyncZ = Number.NaN
  // Marker-only key light lives on this layer so PointLight scene lighting stays unchanged.
  const LIGHT_MARKER_LAYER = 1
  camera.layers.enable(LIGHT_MARKER_LAYER)

  // Palette hemi plus the theme sun. Key and fill stay off so they don't stack with the sun.
  // The orb's markerKey (gizmo-only, layer 1) and the user lamp (`point`) stay separate.
  const lightRig = createLightPaletteRig()
  scene.add(lightRig.group)
  const fallbackLampParams = pointLightParams(FLY_SPEED_REFERENCE_SPAN)
  // Starts at 0 until applyPointLightParams; omit intensity → range.min (same as slider left).
  // A saved lightState.intensity > 0 still applies via applyPointLightParams.
  const point = new THREE.PointLight(0xffffff, 0, fallbackLampParams.distance, fallbackLampParams.decay)
  point.position.set(6, 12, 8)
  scene.add(point)

  // Fixed key for specular glint on the orb; must not follow the marker (co-located light kills N·L).
  const markerKey = new THREE.DirectionalLight(0xffffff, 0.45)
  markerKey.position.set(6, 12, 8)
  markerKey.layers.set(LIGHT_MARKER_LAYER)
  scene.add(markerKey)

  const lightMarker = new THREE.Mesh(
    new THREE.SphereGeometry(1, 64, 32),
    new THREE.MeshStandardMaterial({
      color: 0xffe08a,
      emissive: 0xf0c040,
      emissiveIntensity: 0.55,
      roughness: 0.25,
      // Drop guides are transparent (depthTest off) and would paint after an opaque orb.
      // Same late queue, later group: the orb covers them. depthTest still hides it behind walls.
      transparent: true,
      opacity: 1,
      depthWrite: false,
    }),
  )
  lightMarker.layers.set(LIGHT_MARKER_LAYER)
  lightMarker.scale.setScalar(lightMarkerRadius(FLY_SPEED_REFERENCE_SPAN))
  lightMarker.visible = false
  lightMarker.userData = { kind: "lightMarker" }
  // Group.renderOrder becomes the sort group for every child. The path overlay
  // group is 2, so a mesh renderOrder cannot draw the orb after the line.
  const lightMarkerGroup = new THREE.Group()
  lightMarkerGroup.renderOrder = 3
  lightMarkerGroup.add(lightMarker)
  scene.add(lightMarkerGroup)
  applyEnvironment(defaultSceneEnvironment())
  let lightState: SceneLightState = { preset: "south", position: [0, 7.2, 8] }

  function paintLightMarker(theme: ReturnType<typeof sceneThemeById>) {
    const mat = lightMarker.material
    if (!(mat instanceof THREE.MeshStandardMaterial)) return
    mat.color.set(theme.lampColor)
    mat.emissive.set(theme.lampGlow)
  }

  function applyPointLightParams(span: number) {
    const params = pointLightParams(span)
    point.distance = params.distance
    point.decay = params.decay
    const stored = lightState.intensity
    const hasExplicitIntensity = typeof stored === "number" && Number.isFinite(stored) && stored > 0
    const intensityRange = pointLightIntensityRange(span)
    // Omit / invalid → range.min (same clamp as setLightIntensity). Leaving ambient at 1 while
    // point=0 made new scenes look palette-bright until the slider pointerUp re-applied min.
    // Omit stays omit until the slider emits. An explicit value outside the span range is clamped
    // so a lamp tuned for a large scene does not blow out a centimeter set.
    const effective = hasExplicitIntensity ? clamp(stored, intensityRange.min, intensityRange.max) : intensityRange.min
    if (hasExplicitIntensity && effective !== stored) {
      lightState = { ...lightState, intensity: effective }
    }
    point.intensity = effective
    point.castShadow = true
    point.shadow.mapSize.set(512, 512)
    const shadowCam = point.shadow.camera
    shadowCam.near = pointShadowNearForSpan(span)
    shadowCam.updateProjectionMatrix()
    const safeSpan = Number.isFinite(span) && span > 0 ? span : FLY_SPEED_REFERENCE_SPAN
    point.shadow.normalBias = safeSpan * 0.001
    point.shadow.bias = POINT_SHADOW_BIAS
    // Theme owns hemi + sun. The lamp stays a point light and does not scale that rig.
    skyDome.setAmbientFactor(skyAmbientFactorForLampIntensity(effective, intensityRange))
  }

  const floor = new THREE.Mesh(
    new THREE.PlaneGeometry(FLOOR_EXTENT, FLOOR_EXTENT),
    new THREE.MeshStandardMaterial({ color: hexToNumber(LIGHT_PALETTE.ground), roughness: 0.95, metalness: 0 }),
  )
  floor.rotation.x = -Math.PI / 2
  floor.receiveShadow = true
  floor.userData.ignorePick = true
  scene.add(floor)

  const grid = new THREE.GridHelper(
    FLOOR_EXTENT,
    16,
    hexToNumber(LIGHT_PALETTE.markMaj),
    hexToNumber(LIGHT_PALETTE.mark),
  )
  grid.position.y = 0.01
  scene.add(grid)

  const objectsRoot = new THREE.Group()
  scene.add(objectsRoot)

  const platformLayer: PlatformLayer = createPlatformLayer(scene, camera, canvas, (visible) => {
    floor.visible = visible
    grid.visible = visible
  })
  themeRig = createSceneThemeRig(scene, camera)
  syncSceneTheme()

  // Temporary dev-console hooks for the light-palette color check — see docs/location_scene_3d_palette.md.
  // Call window.__locationScenePalette.measure() once a location with a platform is loaded.
  // MODE (not DEV): this project's dev workflow runs `vite build --mode development` on demand
  // (config/vite.json autoBuild) rather than a persistent `vite dev` server, so DEV is false there.
  if (import.meta.env.MODE !== "production" && typeof window !== "undefined") {
    ;(window as unknown as { __locationScenePalette?: unknown }).__locationScenePalette = {
      diagnose: () => diagnoseLightPalette(scene, renderer),
      measure: () => {
        const platformRoot = scene.getObjectByName("locationScenePlatformRoot")
        if (!platformRoot) {
          console.warn("[light-palette] no platform in scene yet — open a location with a built platform")
          return null
        }
        return measureLightPaletteReference(scene, renderer, platformRoot)
      },
    }
  }
  let platformTool: PlatformTool = null
  type PlatformPaint =
    | { kind: "sector"; anchor: { x: number; z: number }; current: { x: number; z: number }; shift: boolean }
    | {
        kind: "opening"
        anchor: FaceTile
        current: FaceTile
        shift: boolean
      }
  let platformPaint: PlatformPaint | null = null
  /** Unified platform gesture pending (idle clear / sector paint / activate). */
  let platformPending: PlatformPending | null = null
  let platformConsumed = false
  /** Surface tool with selection: click selects face/tile; drag past threshold starts marquee. */
  type PlatformFacePending = {
    tile: FaceTile | null
    faceKey: string
    shift: boolean
  }
  let platformFacePending: PlatformFacePending | null = null
  let platformSelectedSectorCount = 0
  let platformSelectedFaceCount = 0
  /** Last platform layer snapshot — used by dropViewHint for walkable frustum. */
  let platformDropState: PlatformLayerState | null = null
  /** true = Тайлы (UV grid); false = Стена (whole face runs). */
  let platformSurfaceGrid = false

  function hasSectorSelection() {
    return platformTool === "sector" && platformSelectedSectorCount > 0
  }

  function hasFaceSelection() {
    return platformTool === "surface" && platformSelectedFaceCount > 0
  }

  type SelectionKind = "object" | "light" | "sector" | "face"

  function currentSelectionKind(): SelectionKind | null {
    if (lightSelected) return "light"
    if (selectedUid) return "object"
    if (hasSectorSelection()) return "sector"
    if (hasFaceSelection()) return "face"
    return null
  }

  function clearPlatformPaintPreviews() {
    platformLayer.previewSectors(new Set())
    platformLayer.previewOpenings(new Set())
  }

  function clearPlatformSelection() {
    platformPaint = null
    platformPending = null
    platformFacePending = null
    platformTool = null
    platformConsumed = false
    clearPlatformPaintPreviews()
    if (platformDropState) {
      const next = {
        ...platformDropState,
        selectedSectors: [],
        selectedFaceTiles: [],
        activeFaceKey: null,
      }
      platformLayer.setState(next)
      platformDropState = next
    }
    platformSelectedSectorCount = 0
    platformSelectedFaceCount = 0
    onPlatformClear?.()
    onPlatformToolCleared?.()
  }

  function clearAllSelection() {
    selectObject(null)
    clearPlatformSelection()
  }

  /** Short click on another kind: clear only, do not select. Returns true if handled. */
  function clearOnlyIfCrossType(hitKind: SelectionKind): boolean {
    const cur = currentSelectionKind()
    if (cur == null || cur === hitKind) return false
    clearAllSelection()
    return true
  }

  let lightDragging = false
  let lightDragPointerId: number | null = null
  let lightDragStartY = 0
  let lightDragStartClientX = 0
  let lightDragStartClientY = 0
  let lightDragShift = false
  const lightDragStartPos = new THREE.Vector3()
  const lightDragOffset = new THREE.Vector3()
  const lightDragHit = new THREE.Vector3()
  let lightDragMoved = false
  let lightDragPending = false
  let lightSelected = false
  let pathPointDragIndex: number | null = null
  let pathPointDragShift = false
  let pathPointDragStartClientY = 0
  const pathPointDragStartPos = new THREE.Vector3()

  function setLightSelected(selected: boolean) {
    if (lightSelected === selected) return
    lightSelected = selected
    onLightSelectionChange?.(selected)
  }

  function projectWorldToStage(world: THREE.Vector3, opts?: { clamp?: boolean }): { x: number; y: number } | null {
    gizmoAnchorWorld.copy(world).project(camera)
    const z = gizmoAnchorWorld.z
    if (z > 1 || (opts?.clamp && z < -1)) return null
    const w = stage.clientWidth
    const h = stage.clientHeight
    if (w < 32 || h < 32) return null
    const x = (gizmoAnchorWorld.x * 0.5 + 0.5) * w
    const y = (-gizmoAnchorWorld.y * 0.5 + 0.5) * h
    if (x >= -48 && x <= w + 48 && y >= -48 && y <= h + 48) return { x, y }
    if (!opts?.clamp) return null
    const inset = 8
    return {
      x: Math.min(Math.max(x, inset), w - inset),
      y: Math.min(Math.max(y, inset), h - inset),
    }
  }

  const orbit = new OrbitControls(camera, canvas)
  orbit.enabled = false
  orbit.enableDamping = true
  orbit.dampingFactor = 0.08
  orbit.target.set(...(DEFAULT_CAMERA_STATE.target ?? [0, 0.5, 0]))
  orbit.maxPolarAngle = Math.PI * 0.49
  orbit.minDistance = 0.6
  orbit.maxDistance = sceneTravelBound(0)

  function syncOrbitTravelBound(span: number) {
    orbit.maxDistance = sceneTravelBound(span)
  }

  /** Span last used to frame the camera. 0 until a platform is applied. */
  let fittedSpan = 0
  /** Light position we already replaced, so a later echo of it snaps again. */
  let lightFitFrom: [number, number, number] | null = null

  const transform = new TransformControls(camera, canvas)
  transform.setSize(0.85)
  scene.add(transform.getHelper())
  /** Translate handle for a whole group. Lives in the scene so the gizmo has a parent; not saved. */
  const groupPivot = new THREE.Object3D()
  groupPivot.name = "group-pivot"
  scene.add(groupPivot)
  const groupPivotLast = new THREE.Vector3()
  /** Members are parented to the pivot only while a rotate drag is in progress. */
  let groupRotateHeld = false
  const groupPivotBox = new THREE.Box3()
  const groupMemberBox = new THREE.Box3()
  const groupGeometryBox = new THREE.Box3()

  const raycaster = new THREE.Raycaster()
  raycaster.layers.enable(LIGHT_MARKER_LAYER)
  const pointerNdc = new THREE.Vector2()
  const fwd = new THREE.Vector3()
  const rgt = new THREE.Vector3()
  const want = new THREE.Vector3()
  const vel = new THREE.Vector3()
  const worldUp = new THREE.Vector3(0, 1, 0)
  const lastGoodPos = new THREE.Vector3(...DEFAULT_CAMERA_STATE.pos)

  let navMode: SceneNavMode = "fly"
  let gizmoMode: SceneGizmoMode = "translate"
  let selectedUid: string | null = null
  let selectedUids: string[] = []
  let flySpeed = 4.5
  let flyYaw = 0
  let flyPitch = -0.4
  let tYaw = 0
  let tPitch = -0.4
  let viewAnim: {
    t0: number
    theta0: number
    phi0: number
    dTheta: number
    dPhi: number
    radius0: number
    radius1: number
    focus: THREE.Vector3
  } | null = null
  let poseAnim: {
    t0: number
    pos0: THREE.Vector3
    pos1: THREE.Vector3
    yaw0: number
    dYaw: number
    pitch0: number
    dPitch: number
    fov0: number
    fov1: number
    target: [number, number, number] | null
    mode: SceneNavMode | null
    onDone?: () => void
  } | null = null
  const poseLerp = new THREE.Vector3()
  /** Straight up/down cannot be stored as yaw/pitch. Hold the lookAt pose until the user flies or looks. */
  let axisHold = false
  const axisHoldFocus = new THREE.Vector3()
  const axisHoldUp = new THREE.Vector3()
  const orientDir: Vec3 = [0, 0, 0]
  const widgetDir: Vec3 = [0, 0, 0]
  const widgetUp: Vec3 = [0, 0, 0]
  let draggingLook = false
  let deviceLookActive = false
  let deviceLookFollowsPath = false
  let lookYawRate = 0
  /** Mobile left stick: +right / -left (speed units). */
  let analogStrafe = 0
  /** Mobile left stick: +up / -down (speed units). */
  let analogVertical = 0
  let lastX = 0
  let lastY = 0
  let pointerDownX = 0
  let pointerDownY = 0
  let transformDragging = false
  /** Height held while dragging the figure itself along the floor. */
  let translateLockY: number | null = null
  let objectDragging = false
  let objectDragPendingUid: string | null = null
  let objectDragUid: string | null = null
  let objectDragPointerId: number | null = null
  let objectDragMoved = false
  /** Last XZ of the dragged member, so group peers get the same delta. */
  let groupSync: { uid: string; x: number; z: number } | null = null
  /** Heights at drag start, so a floor lift does not stack every pointer move. */
  let groupDragBase: Map<string, number> | null = null
  const objectDragOffset = new THREE.Vector3()
  const objectDragHit = new THREE.Vector3()
  const objectDragFallbackPlane = new THREE.Plane()
  const objectDragAnchor = new THREE.Vector3()
  const OBJECT_DRAG_CLICK_PX = 4
  const keys = new Set<string>()
  const pointerKeys = new Set<SceneFlyAxis>()

  let cameraPath: CameraPath = emptyCameraPath()
  let cameraPathTakes: CameraPathTake[] = []
  let activeCameraPathTakeId: string | null = null

  function commitActiveTake() {
    if (!activeCameraPathTakeId) return
    const index = cameraPathTakes.findIndex((take) => take.id === activeCameraPathTakeId)
    if (index < 0) return
    const current = cameraPathTakes[index]
    cameraPathTakes[index] = { id: current.id, name: current.name, path: cloneCameraPath(cameraPath) }
  }

  function addCameraPathTake(name: string): string | null {
    if (pathLookThrough) applyPathLookThrough(false)
    commitActiveTake()
    if (cameraPathTakesFull(cameraPathTakes)) return null
    const trimmed = name.trim().slice(0, 120)
    if (!trimmed) return null
    const id = newCameraPathTakeId()
    cameraPathTakes = [...cameraPathTakes, { id, name: trimmed, path: emptyCameraPath() }]
    activeCameraPathTakeId = id
    pathLook = { mode: "tangent" }
    pathObserveArmed = false
    clearObserveHover()
    lookRewriteArmed = false
    lookRewriteDown = false
    replacePath(emptyCameraPath(), { skipHistory: true, clearRedo: true })
    return id
  }

  function selectCameraPathTake(id: string) {
    const take = cameraPathTakes.find((item) => item.id === id)
    if (!take || take.id === activeCameraPathTakeId) return
    commitActiveTake()
    activeCameraPathTakeId = take.id
    replacePath(cloneCameraPath(take.path), { skipHistory: true })
  }

  function renameCameraPathTake(id: string, name: string) {
    const trimmed = name.trim().slice(0, 120)
    if (!trimmed) return
    const index = cameraPathTakes.findIndex((take) => take.id === id)
    if (index < 0) return
    const current = cameraPathTakes[index]
    cameraPathTakes[index] = { id: current.id, name: trimmed, path: current.path }
    onCameraPathChange?.(cameraPath)
  }

  function deleteCameraPathTake(id: string) {
    commitActiveTake()
    const index = cameraPathTakes.findIndex((take) => take.id === id)
    if (index < 0) return
    const wasActive = activeCameraPathTakeId === id
    cameraPathTakes = cameraPathTakes.filter((take) => take.id !== id)
    if (!wasActive) {
      onCameraPathChange?.(cameraPath, { skipHistory: true, clearRedo: true })
      return
    }
    const neighbor = cameraPathTakes[Math.min(index, cameraPathTakes.length - 1)]
    if (!neighbor) {
      activeCameraPathTakeId = null
      replacePath(emptyCameraPath(), { skipHistory: true, clearRedo: true })
      return
    }
    activeCameraPathTakeId = neighbor.id
    replacePath(cloneCameraPath(neighbor.path), { skipHistory: true, clearRedo: true })
  }

  function restoreCameraPathTakeFromHistory(entry: { id: string | null; path: CameraPath }) {
    restoreCameraPathTakesFromHistory({
      patches: [{ takeId: entry.id, path: entry.path }],
      activeId: entry.id,
    })
  }

  function restoreCameraPathTakesFromHistory(args: { patches: CameraPathHistoryPatch[]; activeId: string | null }) {
    const next = restoreCameraPathsForHistory({
      takes: cameraPathTakes,
      patches: args.patches,
      activeId: args.activeId,
      fallbackActiveId: activeCameraPathTakeId,
    })
    if (!next) return
    dropPathFit()
    cameraPathTakes = next.takes
    activeCameraPathTakeId = next.activeId
    cameraPath = next.cameraPath
    pathTimeMs = 0
    onPathTimeChange?.(0)
    adoptPathSettings()
    onCameraPathChange?.(cameraPath, { skipHistory: true })
    syncPathOverlay()
  }
  const pathFit = createPathFitGate()
  let cancelPathFitJob = () => {}
  let trajectoryMode = false
  let cameraPlaceMode = false
  let cameraPlaceMarkersState: CameraPlaceMarker[] = []
  let cameraPlaceHover = -1
  let cameraPlaceSelected = -1
  let cameraPlacePending: number | null = null
  let cameraPlaceDrag: {
    index: number
    yaw0: number
    pitch0: number
    y0: number
    x0: number
    yClient0: number
    shift: boolean
  } | null = null
  let cameraPlaceAim: { yaw: number; pitch: number } | null = null
  let cameraPlacePreview: { pos: PathVec3; yaw: number; pitch: number } | null = null
  let cameraPlaceKey = ""
  let cameraPlaceMesh: THREE.InstancedMesh | null = null
  let cameraPlaceOpacity: THREE.InstancedBufferAttribute | null = null
  let cameraPlaceMonitorCanvas: HTMLCanvasElement | null = null
  let cameraPlaceMonitorTarget: THREE.WebGLRenderTarget | null = null
  let cameraPlaceMonitorPixels: Uint8Array | null = null
  let cameraPlaceMonitorImage: ImageData | null = null
  const cameraPlaceMonitorCam = new THREE.PerspectiveCamera(camera.fov, 1, camera.near, camera.far)
  cameraPlaceMonitorCam.rotation.order = "YXZ"
  const cameraPlaceDummy = new THREE.Object3D()
  let objectPathUid: string | null = null
  let previewObjectPathUid: string | null = null
  let objectPosesApplied = false
  let pathEditAllowed = true
  let pathDraw = true
  let pathInsertHover: PathVec3 | null = null
  let pathSmooth = true
  let pathLook: PathLook = { mode: "tangent" }
  let pathLookThrough = false
  /** Поза свободной камеры до включения вида из камеры. */
  let lookThroughReturn: SceneCameraState | null = null
  /** Смещение yaw/pitch на время просмотра из камеры. В дорожку не пишется. */
  let lookPreview: { yaw: number; pitch: number } | null = null
  let lookRecording = false
  let lookRewriteArmed = false
  let lookRewriteDown = false
  let lookRecordFrom = 0
  let lookRecordLastT = -Infinity
  let lookRecordBase: PathLookKey[] = []
  let lookRecorded: PathLookKey[] = []
  let pathDurationMs = DEFAULT_SPLINE_DURATION_MS
  let pathObserveArmed = false
  let observeHoverUid: string | null = null
  let selectedPathPoint: number | null = null
  let pathTimeMs = 0
  /** After a WASD stop: wait until the user lets go, then scrub back to the first frame. */
  let pathRewind: { t0: number; fromMs: number; started: boolean } | null = null
  /** После записи: прямая в позу первого кадра, когда клавиши отпущены. */
  let returnHome: SceneCameraState | null = null
  /** Идёт прямой возврат: жест пользователя его отменяет, вид из камеры остаётся. */
  let returnGlide = false
  let pathLastClickAt = 0
  let pathLastClickIndex = -1
  let recording = false
  let recordArmed = false
  let recordMotionClock: RecordMotionClock | null = null
  let playing = false
  let playStartedAt = 0
  let playFromMs = 0
  let playToMs = 0
  let exporting = false
  /** Still capture owns GPU offscreen — skip live canvas draw (do not reuse `exporting`: path video needs the loop). */
  let capturingStill = false
  /** Завершение экспорта по концу воспроизведения (stopPlayback). */
  let exportFinish: (() => void) | null = null
  /** Reject in-flight export (destroy / abort) — never resolve a partial blob. */
  let exportReject: ((err: Error) => void) | null = null
  let exportRecorder: MediaRecorder | null = null
  let exportStream: MediaStream | null = null
  /** Редактор размонтирован, холст дописывает ролик вне сцены. */
  let exportDetached = false
  let exportHolder: HTMLDivElement | null = null

  const meshes = new Map<string, THREE.Object3D>()

  function syncTransformAxisLocks() {
    transform.showX = true
    transform.showY = true
    transform.showZ = true
    transform.showXY = true
    transform.showYZ = true
    transform.showXZ = true
    transform.showXYZE = true
  }

  function updateOrbitEnabled() {
    orbit.enabled = false
  }

  function detachTransform() {
    if (!transform.object) return
    transform.detach()
    transform.getHelper().visible = false
  }

  function syncTransformAttachment(mesh?: THREE.Object3D) {
    if (objectDragging) return
    if (transformDragging && transform.object === groupPivot) return
    if (trajectoryMode || pathTargetsObject() || pathTimeMs > 0 || playing) {
      if (groupRotateHeld) releaseGroupRotate()
      detachTransform()
      return
    }
    const objects = selectionObjects()
    if (isCompleteGroup(selectedUids, objects)) {
      if (groupRotateHeld) return
      if (!placeGroupPivot()) {
        detachTransform()
        return
      }
      transform.setMode(gizmoMode)
      transform.attach(groupPivot)
      transform.getHelper().visible = true
      syncTransformAxisLocks()
      return
    }
    if (selectionPopoverAction(selectedUids, objects) === "group") {
      detachTransform()
      return
    }
    const target = mesh ?? (selectedUid ? meshes.get(selectedUid) : undefined)
    if (!target) {
      transform.detach()
      transform.getHelper().visible = false
      return
    }
    transform.attach(target)
    transform.getHelper().visible = true
    syncTransformAxisLocks()
  }

  function pathTargetsObject() {
    return objectPathUid != null && meshes.has(objectPathUid)
  }

  function pathPreviewing() {
    return previewObjectPathUid != null && meshes.has(previewObjectPathUid) && previewObjectPathUid !== objectPathUid
  }

  function focusedPathUid(): string | null {
    if (pathTargetsObject()) return objectPathUid
    if (pathPreviewing()) return previewObjectPathUid
    return null
  }

  function pathEditActive() {
    return trajectoryMode || pathTargetsObject()
  }

  function canDirectObjectDrag() {
    return (
      canEdit &&
      gizmoMode === "translate" &&
      !playing &&
      pathTimeMs <= 0 &&
      !deviceLookActive &&
      !trajectoryMode &&
      !pathTargetsObject()
    )
  }

  function selectionObjects(): { uid: string; group_id?: string }[] {
    const out: { uid: string; group_id?: string }[] = []
    meshes.forEach((obj, uid) => {
      const data = obj.userData as SceneObjectState
      out.push({ uid, group_id: data.group_id })
    })
    return out
  }

  function armGroupSync(uid: string) {
    const mesh = meshes.get(uid)
    const peers = groupPeerUids(selectionObjects(), selectedUids, uid)
    if (!mesh || peers.length === 0) {
      groupSync = null
      groupDragBase = null
      return
    }
    groupSync = { uid, x: mesh.position.x, z: mesh.position.z }
    groupDragBase = new Map()
    for (const memberUid of selectedUids) {
      const member = meshes.get(memberUid)
      if (member) groupDragBase.set(memberUid, member.position.y)
    }
  }

  function applyGroupSyncDelta() {
    if (!groupSync) return
    const mesh = meshes.get(groupSync.uid)
    if (!mesh) return
    const dx = mesh.position.x - groupSync.x
    const dz = mesh.position.z - groupSync.z
    if (dx === 0 && dz === 0) return
    groupSync = { uid: groupSync.uid, x: mesh.position.x, z: mesh.position.z }
    for (const peerUid of groupPeerUids(selectionObjects(), selectedUids, groupSync.uid)) {
      const peer = meshes.get(peerUid)
      if (!peer) continue
      peer.position.x += dx
      peer.position.z += dz
    }
  }

  function setPointerRay(e: PointerEvent) {
    setPointerRayAt(e.clientX, e.clientY)
  }

  function setPointerRayAt(clientX: number, clientY: number) {
    const rect = canvas.getBoundingClientRect()
    pointerNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1
    pointerNdc.y = -((clientY - rect.top) / rect.height) * 2 + 1
    raycaster.setFromCamera(pointerNdc, camera)
  }

  /** Ray → XZ at planeY; camera-facing fallback when ray is parallel to the floor. */
  function rayToGroundY(out: THREE.Vector3, planeY: number, anchor: THREE.Vector3) {
    const ray = raycaster.ray
    const dy = ray.direction.y
    if (Math.abs(dy) > 1e-5) {
      const t = (planeY - ray.origin.y) / dy
      if (t > 0) {
        out.copy(ray.origin).addScaledVector(ray.direction, t)
        return true
      }
    }
    camera.getWorldDirection(fwd)
    objectDragFallbackPlane.setFromNormalAndCoplanarPoint(fwd, anchor)
    if (ray.intersectPlane(objectDragFallbackPlane, out) == null) return false
    out.y = planeY
    return true
  }

  function beginObjectDrag(uid: string, e: PointerEvent) {
    const mesh = meshes.get(uid)
    if (!mesh) return
    if (!selectedUids.includes(uid)) selectObject(uid)
    objectDragging = true
    if (transform.dragging) transform.dragging = false
    transform.detach()
    transform.getHelper().visible = false
    objectDragUid = uid
    objectDragMoved = true
    objectDragPointerId = e.pointerId
    const y = mesh.position.y
    translateLockY = y
    objectDragAnchor.copy(mesh.position)
    setPointerRay(e)
    if (rayToGroundY(objectDragHit, y, objectDragAnchor)) {
      objectDragOffset.copy(objectDragHit).sub(mesh.position)
    } else {
      objectDragOffset.set(0, 0, 0)
    }
    updateOrbitEnabled()
    armGroupSync(uid)
    canvas.setPointerCapture(e.pointerId)
    canvas.focus()
  }

  function platformSurfaceY(worldX: number, worldZ: number): number {
    const state = platformDropState
    const platform = state?.platform
    if (!platform || !platformIsSet(platform)) return 0
    const sector = worldToSectorIndex(platform, worldX, worldZ)
    if (!sector) return 0
    const height = state.cells.find((cell) => cell.x === sector.x && cell.z === sector.z)?.height ?? 0
    if (height <= 0) return 0
    return height * sectorSizeMeters(platform)
  }

  function objectMinCenterY(mesh: THREE.Object3D): number {
    const data = mesh.userData as { size?: [number, number, number] }
    const sizeY = data.size?.[1] ?? 1
    return platformSurfaceY(mesh.position.x, mesh.position.z) + (sizeY * Math.abs(mesh.scale.y || 1)) / 2
  }

  function clampObjectToPlatform(mesh: THREE.Object3D) {
    const minY = objectMinCenterY(mesh)
    if (mesh.position.y < minY) mesh.position.y = minY
    if (translateLockY != null && translateLockY < minY) translateLockY = minY
  }

  // Visual only. Path points, samples, and userData.position stay as authored.
  function withObjectsAboveFloor(draw: () => void) {
    const lifted = new Map<THREE.Object3D, number>()
    meshes.forEach((mesh) => {
      const minY = objectMinCenterY(mesh)
      if (mesh.position.y >= minY) return
      lifted.set(mesh, mesh.position.y)
      mesh.position.y = minY
    })
    try {
      draw()
    } finally {
      lifted.forEach((y, mesh) => {
        mesh.position.y = y
      })
    }
  }

  function unionObjectBounds(root: THREE.Object3D, target: THREE.Box3) {
    root.updateWorldMatrix(true, true)
    root.traverse((child) => {
      if (child.userData.isLabel) return
      if (!(child instanceof THREE.Mesh) || !child.geometry) return
      if (!child.geometry.boundingBox) child.geometry.computeBoundingBox()
      const bb = child.geometry.boundingBox
      if (!bb || bb.isEmpty()) return
      groupGeometryBox.copy(bb).applyMatrix4(child.matrixWorld)
      target.union(groupGeometryBox)
    })
  }

  function placeGroupPivot(): boolean {
    groupPivotBox.makeEmpty()
    for (const uid of selectedUids) {
      const mesh = meshes.get(uid)
      if (!mesh) continue
      groupMemberBox.makeEmpty()
      unionObjectBounds(mesh, groupMemberBox)
      if (!groupMemberBox.isEmpty()) groupPivotBox.union(groupMemberBox)
    }
    if (groupPivotBox.isEmpty()) return false
    groupPivotBox.getCenter(groupPivot.position)
    if (!groupRotateHeld) groupPivot.quaternion.identity()
    groupPivot.updateMatrixWorld(true)
    groupPivotLast.copy(groupPivot.position)
    return true
  }

  function holdGroupForRotate() {
    if (groupRotateHeld) return
    if (!placeGroupPivot()) return
    groupPivot.updateMatrixWorld(true)
    for (const uid of selectedUids) {
      const mesh = meshes.get(uid)
      if (mesh) groupPivot.attach(mesh)
    }
    groupRotateHeld = true
  }

  function releaseGroupRotate() {
    if (!groupRotateHeld) return
    const held: THREE.Object3D[] = []
    for (const uid of selectedUids) {
      const mesh = meshes.get(uid)
      if (mesh && mesh.parent === groupPivot) held.push(mesh)
    }
    for (const mesh of held) objectsRoot.attach(mesh)
    groupRotateHeld = false
    groupPivot.quaternion.identity()
    groupPivot.updateMatrixWorld(true)
  }

  function rememberGroupHeights() {
    const bases = new Map<string, number>()
    for (const uid of selectedUids) {
      const member = meshes.get(uid)
      if (member) bases.set(uid, member.position.y)
    }
    groupDragBase = bases
  }

  function applyGroupPivotDelta() {
    const dx = groupPivot.position.x - groupPivotLast.x
    const dy = groupPivot.position.y - groupPivotLast.y
    const dz = groupPivot.position.z - groupPivotLast.z
    if (dx === 0 && dy === 0 && dz === 0) return
    const vertical = transform.axis === "Y"
    for (const uid of selectedUids) {
      const mesh = meshes.get(uid)
      if (!mesh) continue
      mesh.position.x += dx
      mesh.position.z += dz
      if (vertical) {
        mesh.position.y += dy
      } else {
        const base = groupDragBase?.get(uid)
        if (base != null) mesh.position.y = base
      }
    }
    groupPivotLast.copy(groupPivot.position)
  }

  function updateObjectDrag(e: PointerEvent) {
    if (!objectDragging || !objectDragUid || translateLockY == null) return
    const mesh = meshes.get(objectDragUid)
    if (!mesh) return
    setPointerRay(e)
    if (!rayToGroundY(objectDragHit, translateLockY, mesh.position)) return
    mesh.position.copy(objectDragHit).sub(objectDragOffset)
    if (groupSync && groupDragBase) {
      const bases = groupDragBase
      bases.forEach((y, uid) => {
        const member = meshes.get(uid)
        if (member) member.position.y = y
      })
      applyGroupSyncDelta()
    } else {
      mesh.position.y = translateLockY
      clampObjectToPlatform(mesh)
    }
  }

  function finishObjectDrag() {
    if (!objectDragging) return
    objectDragging = false
    if (objectDragPointerId != null) {
      try {
        canvas.releasePointerCapture(objectDragPointerId)
      } catch {
        // already released
      }
    }
    objectDragPointerId = null
    objectDragUid = null
    groupSync = null
    groupDragBase = null
    translateLockY = null
    updateOrbitEnabled()
    syncTransformAttachment()
    if (objectDragMoved) emitObjects()
    objectDragMoved = false
  }

  transform.addEventListener("dragging-changed", (event) => {
    transformDragging = Boolean((event as { value?: boolean }).value)
    updateOrbitEnabled()
    if (transformDragging) {
      objectDragPendingUid = null
      translateLockY = null
      if (transform.object === groupPivot) {
        groupPivotLast.copy(groupPivot.position)
        groupSync = null
        rememberGroupHeights()
        if (gizmoMode === "rotate") holdGroupForRotate()
        else if (gizmoMode === "translate") transform.getHelper().visible = false
      } else if (selectedUid) {
        const mesh = meshes.get(selectedUid)
        if (mesh) clampObjectToPlatform(mesh)
        armGroupSync(selectedUid)
      }
    } else if (!objectDragging) {
      translateLockY = null
      groupSync = null
      groupDragBase = null
      if (groupRotateHeld) releaseGroupRotate()
      if (transform.object === groupPivot) {
        placeGroupPivot()
        if (gizmoMode === "translate") transform.getHelper().visible = true
      }
      emitObjects()
    }
  })
  transform.addEventListener("objectChange", () => {
    if (transform.object === groupPivot) {
      if (groupRotateHeld) return
      applyGroupPivotDelta()
      return
    }
    if (!selectedUid) return
    const mesh = meshes.get(selectedUid)
    if (!mesh) return
    if (translateLockY != null) mesh.position.y = translateLockY
    clampObjectToPlatform(mesh)
    applyGroupSyncDelta()
  })

  let suppressEmit = false

  function emitObjects() {
    markCameraPlaceMonitorDirty()
    if (suppressEmit) return
    onObjectsChange?.(collectObjects())
  }

  function restPositionOf(obj: THREE.Object3D): [number, number, number] {
    const data = obj.userData as SceneObjectState
    if (objectPosesApplied && data.position) return [...data.position] as [number, number, number]
    return [obj.position.x, obj.position.y, obj.position.z]
  }

  function collectObject(uid: string, obj: THREE.Object3D): SceneObjectState {
    const data = obj.userData as SceneObjectState
    let position = restPositionOf(obj)
    let path = data.path ? cloneObjectPath(data.path) : undefined
    if (!objectPosesApplied && path && data.position) {
      const delta: [number, number, number] = [
        position[0] - data.position[0],
        position[1] - data.position[1],
        position[2] - data.position[2],
      ]
      if (delta.some((value) => Math.abs(value) > 1e-6)) {
        path = shiftObjectPath(path, delta)
        data.path = path
        const start = objectPathStart(path)
        if (start) {
          position = [...start] as [number, number, number]
          obj.position.set(position[0], position[1], position[2])
        }
      }
    }
    if (!objectPosesApplied) data.position = [...position] as [number, number, number]
    return {
      uid,
      kind: data.kind,
      shape: data.shape,
      entity_type: data.entity_type,
      entity_id: data.entity_id,
      label: data.label,
      color: data.color,
      figure: data.figure,
      size: data.size,
      position,
      rotation: [obj.rotation.x, obj.rotation.y, obj.rotation.z],
      scale: [obj.scale.x, obj.scale.y, obj.scale.z],
      missing: data.missing,
      height_explicit: data.height_explicit,
      thumb_url: data.thumb_url,
      ...(data.group_id ? { group_id: data.group_id } : {}),
      ...(path ? { path } : {}),
    }
  }

  function collectObjects(): SceneObjectState[] {
    const out: SceneObjectState[] = []
    meshes.forEach((obj, uid) => {
      out.push(collectObject(uid, obj))
    })
    return out
  }

  // Object-name sprites above meshes only (not React HUD: Эфир / close / record hint).
  // Hide while recording, previz play/export, or phone-fly.
  function labelsVisible() {
    return !recording && !playing && !exporting && !deviceLookActive
  }

  /** Yellow light orb is editor chrome — same hide rules as object labels. */
  function lightMarkerAllowed() {
    return Boolean(platformLayer.span()) && labelsVisible()
  }

  const gizmoAnchorWorld = new THREE.Vector3()
  /** Arrow point, kept aside because projectWorldToStage overwrites gizmoAnchorWorld. */
  const gizmoAnchorFallback = new THREE.Vector3()
  const gizmoAnchorBox = new THREE.Box3()
  const selectionMemberBox = new THREE.Box3()
  const faceArrowCameraRight = new THREE.Vector3()

  function findLabelSprite(root: THREE.Object3D): THREE.Sprite | null {
    let found: THREE.Sprite | null = null
    root.traverse((child) => {
      if (found) return
      if (child instanceof THREE.Sprite && child.userData.isLabel) found = child
    })
    return found
  }

  function getSelectionGizmoAnchorScreen(): { x: number; y: number } | null {
    if (selectedUids.length === 0 || !labelsVisible()) return null
    const single = selectedUids.length === 1 && selectedUid ? meshes.get(selectedUid) : undefined
    if (selectedUids.length === 1 && !single) return null

    if (single) {
      const labelSprite = findLabelSprite(single)
      if (labelSprite) {
        labelSprite.getWorldPosition(gizmoAnchorWorld)
        // Above the name pill (sprite center + half height + gap).
        gizmoAnchorWorld.y += LABEL_SPRITE_SCALE.y * 0.5 + 0.22
        gizmoAnchorFallback.copy(single.position)
        const aboveLabel = projectWorldToStage(gizmoAnchorWorld)
        if (aboveLabel) return aboveLabel
        return projectWorldToStage(gizmoAnchorFallback, { clamp: true })
      }
    }

    gizmoAnchorBox.makeEmpty()
    for (const uid of selectedUids) {
      const mesh = meshes.get(uid)
      if (!mesh) continue
      selectionMemberBox.setFromObject(mesh)
      if (!selectionMemberBox.isEmpty()) gizmoAnchorBox.union(selectionMemberBox)
    }
    if (gizmoAnchorBox.isEmpty()) return null
    const cx = (gizmoAnchorBox.min.x + gizmoAnchorBox.max.x) / 2
    const cz = (gizmoAnchorBox.min.z + gizmoAnchorBox.max.z) / 2
    gizmoAnchorWorld.set(cx, gizmoAnchorBox.max.y + 0.35, cz)
    if (single) gizmoAnchorFallback.copy(single.position)
    else gizmoAnchorFallback.set(cx, (gizmoAnchorBox.min.y + gizmoAnchorBox.max.y) / 2, cz)
    const top = projectWorldToStage(gizmoAnchorWorld)
    if (top) return top
    return projectWorldToStage(gizmoAnchorFallback, { clamp: true })
  }

  function getLightGizmoAnchorScreen(): { x: number; y: number } | null {
    if (!lightSelected || !lightMarker.visible) return null
    const radius = Math.max(lightMarker.scale.x, 0)
    gizmoAnchorWorld.copy(lightMarker.position)
    gizmoAnchorWorld.y += radius + lightGizmoGap(platformLayer.span())
    return projectWorldToStage(gizmoAnchorWorld)
  }

  function getPlatformSelectionToolbarAnchorScreen(): PlatformSelectionToolbarAnchorScreen | null {
    const anchor = platformLayer.selectionToolbarAnchor()
    if (!anchor) return null

    if (anchor.kind === "face") {
      const screen = projectWorldToStage(anchor.top)
      if (!screen) return null
      let faceArrowFlip = false
      if (anchor.face) {
        const step = outwardStep(anchor.face)
        if (step) {
          faceArrowCameraRight.setFromMatrixColumn(camera.matrixWorld, 0)
          faceArrowFlip = faceShiftArrowFlip(step, { x: faceArrowCameraRight.x, z: faceArrowCameraRight.z })
        }
      }
      return { x: screen.x + 14, y: screen.y - 14, kind: "face", faceArrowFlip }
    }

    let best: { x: number; y: number } | null = null
    for (const corner of anchor.sectorCorners) {
      const screen = projectWorldToStage(corner)
      if (!screen) continue
      if (!best || screen.x > best.x) best = screen
    }
    if (!best) return null
    return { x: best.x + 14, y: best.y, kind: "sector", side: "right" }
  }

  function getLightIntensityRange() {
    return pointLightIntensityRange(platformLayer.span())
  }

  function getLightIntensity() {
    return point.intensity
  }

  function setLightIntensity(intensity: number, opts?: { emit?: boolean }) {
    const range = getLightIntensityRange()
    const next = clamp(Number.isFinite(intensity) ? intensity : range.min, range.min, range.max)
    lightState = {
      preset: lightState.preset,
      position: [...lightState.position] as [number, number, number],
      intensity: next,
    }
    point.intensity = next
    skyDome.setAmbientFactor(skyAmbientFactorForLampIntensity(next, range))
    if (opts?.emit !== false) onLightChange?.(lightState)
  }

  function syncLabels(obj: THREE.Object3D, visible: boolean) {
    const sx = Math.max(Math.abs(obj.scale.x), 0.01)
    const sy = Math.max(Math.abs(obj.scale.y), 0.01)
    obj.traverse((child) => {
      if (!(child instanceof THREE.Sprite) || !child.userData.isLabel) return
      child.visible = visible
      child.scale.set(LABEL_SPRITE_SCALE.x / sx, LABEL_SPRITE_SCALE.y / sy, 1)
    })
  }

  function applyHighlight(obj: THREE.Object3D, selected: boolean) {
    obj.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return
      if (child.userData.isLabel) return
      const mat = child.material
      if (mat instanceof THREE.MeshStandardMaterial) {
        mat.emissive = new THREE.Color(selected ? 0xffffff : 0x000000)
        mat.emissiveIntensity = selected ? 0.18 : 0
      }
    })
  }

  function replaceSelection(uids: string[], opts?: { clearLight?: boolean }) {
    if (groupRotateHeld) releaseGroupRotate()
    if (deviceLookActive && uids.length > 0) return
    if (opts?.clearLight !== false) setLightSelected(false)
    if (uids.length > 0) clearPlatformSelection()
    for (const uid of selectedUids) {
      const mesh = meshes.get(uid)
      if (mesh) applyHighlight(mesh, false)
    }
    const known = new Set<string>()
    meshes.forEach((_obj, uid) => known.add(uid))
    selectedUids = uids.filter((uid, index) => known.has(uid) && uids.indexOf(uid) === index)
    selectedUid = selectedUids[0] ?? null
    const objects = selectionObjects()
    const action = selectionPopoverAction(selectedUids, objects)
    if (selectedUids.length === 0 || action === "ungroup") {
      gizmoMode = "translate"
      transform.setMode("translate")
    }
    for (const uid of selectedUids) {
      const mesh = meshes.get(uid)
      if (mesh) applyHighlight(mesh, true)
    }
    if (action === "group") detachTransform()
    else syncTransformAttachment()
    onSelectionChange?.(selectedUids.slice())
    if (previewObjectPathUid && !selectedUids.includes(previewObjectPathUid)) {
      previewObjectPathUid = null
      emitPathEditUi()
      syncPathOverlay()
    }
  }

  function selectObject(uid: string | null, opts?: { clearLight?: boolean; shift?: boolean }) {
    if (uid == null) {
      replaceSelection([], opts)
      return
    }
    if (opts?.shift) {
      replaceSelection(shiftClickSelection(selectionObjects(), selectedUids, uid), opts)
      return
    }
    replaceSelection(plainClickSelection(selectionObjects(), uid), opts)
  }

  function writeGroupIds(next: SceneObjectState[]) {
    const byUid = new Map(next.map((obj) => [obj.uid, obj]))
    meshes.forEach((mesh, uid) => {
      const updated = byUid.get(uid)
      if (!updated) return
      const data = mesh.userData as SceneObjectState
      if (updated.group_id) data.group_id = updated.group_id
      else delete data.group_id
    })
    const action = selectionPopoverAction(selectedUids, selectionObjects())
    if (action === "ungroup") {
      gizmoMode = "translate"
      transform.setMode("translate")
    }
    if (action === "group") detachTransform()
    else syncTransformAttachment()
    emitObjects()
  }

  function groupSelection() {
    if (selectionPopoverAction(selectedUids, selectionObjects()) !== "group") return
    writeGroupIds(applyGroup(collectObjects(), selectedUids))
  }

  function ungroupSelection() {
    if (selectionPopoverAction(selectedUids, selectionObjects()) !== "ungroup") return
    writeGroupIds(applyUngroup(collectObjects(), selectedUids))
  }

  function dissolveGroupIfSmall(groupId: string | undefined) {
    if (!groupId) return
    const left: THREE.Object3D[] = []
    meshes.forEach((mesh) => {
      if ((mesh.userData as SceneObjectState).group_id === groupId) left.push(mesh)
    })
    if (left.length >= 2) return
    for (const mesh of left) delete (mesh.userData as SceneObjectState).group_id
  }

  function paintObjectMaterials(obj: THREE.Object3D, hex: string) {
    obj.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return
      const mat = child.material
      if (Array.isArray(mat)) {
        for (const item of mat) {
          if (item instanceof THREE.MeshStandardMaterial) item.color.set(hex)
        }
        return
      }
      if (mat instanceof THREE.MeshStandardMaterial) mat.color.set(hex)
    })
  }

  function replaceObjectLabel(group: THREE.Object3D, hex: string) {
    const data = group.userData as SceneObjectState
    if (!data.label) return
    const existing = findLabelSprite(group)
    const sprite = makeLabelSprite(data.label, hex)
    if (!sprite) return
    if (existing) {
      sprite.position.copy(existing.position)
      sprite.visible = existing.visible
      const mat = existing.material
      if (mat instanceof THREE.SpriteMaterial) {
        mat.map?.dispose()
        mat.dispose()
      }
      existing.removeFromParent()
    } else {
      sprite.position.y = (data.size?.[1] ?? 1) / 2 + 0.28
    }
    group.add(sprite)
    syncLabels(group, labelsVisible())
  }

  function setObjectColor(uid: string, hex: string, opts?: { emit?: boolean }) {
    const group = meshes.get(uid)
    if (!group) return
    ;(group.userData as SceneObjectState).color = hex
    paintObjectMaterials(group, hex)
    if (!opts?.emit) return
    replaceObjectLabel(group, hex)
    emitObjects()
  }

  function disposeVisual(obj: THREE.Object3D) {
    obj.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return
      child.geometry.dispose()
      const mat = child.material
      if (Array.isArray(mat)) mat.forEach((item) => item.dispose())
      else mat.dispose()
    })
  }

  function mountCharacterVisual(group: THREE.Object3D, figure: unknown) {
    const data = group.userData as SceneObjectState
    const size = data.size ?? [1, 1, 1]
    const color = data.color ?? PRIMITIVE_COLOR
    const existing = group.children.find((child) => child.name === "characterVisual")
    if (existing) {
      group.remove(existing)
      disposeVisual(existing)
    }
    group.add(
      createCharacterVisual(size, color, {
        figure,
        onUpgrade: () => {
          const painted = (group.userData as SceneObjectState).color ?? color
          paintObjectMaterials(group, painted)
          if (selectedUid === data.uid) applyHighlight(group, true)
        },
      }),
    )
    if (selectedUid === data.uid) applyHighlight(group, true)
  }

  function setObjectFigure(uid: string, figure: SceneCharacterFigure) {
    const group = meshes.get(uid)
    if (!group) return
    const data = group.userData as SceneObjectState
    if (data.kind !== "character") return
    if (data.figure === figure) return
    if (data.figure == null && figure === "humanoid") return
    data.figure = figure
    mountCharacterVisual(group, figure)
    emitObjects()
  }

  function addObject(state: SceneObjectState) {
    const shape: ScenePrimitiveShape = state.shape ?? (state.kind === "character" ? "cylinder" : "box")
    const size = state.size ?? [1, 1, 1]
    const color = state.color ?? PRIMITIVE_COLOR
    const group = new THREE.Group()
    group.userData = {
      ...state,
      shape,
      ...(state.path ? { path: cloneObjectPath(state.path) } : {}),
    }
    if (state.kind === "character") {
      mountCharacterVisual(group, state.figure)
    } else {
      group.add(createPrimitiveMesh(shape, size, color))
    }
    if (state.label) {
      const sprite = makeLabelSprite(state.label, color)
      if (sprite) {
        sprite.position.y = size[1] / 2 + 0.28
        group.add(sprite)
      }
    }
    const position = state.position ?? [0, size[1] / 2, 0]
    const rotation = state.rotation ?? [0, 0, 0]
    const scale = state.scale ?? [1, 1, 1]
    group.position.set(...position)
    group.rotation.set(...rotation)
    group.scale.set(...scale)
    syncLabels(group, labelsVisible())
    objectsRoot.add(group)
    meshes.set(state.uid, group)
    emitObjects()
    selectObject(state.uid)
  }

  function disposeSceneObject(obj: THREE.Object3D) {
    obj.removeFromParent()
    obj.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose()
        const mat = child.material
        if (Array.isArray(mat)) mat.forEach((m) => m.dispose())
        else mat.dispose()
      } else if (child instanceof THREE.Sprite) {
        const mat = child.material
        mat.map?.dispose()
        mat.dispose()
      }
    })
  }

  function removeObject(uid: string) {
    if (groupRotateHeld) releaseGroupRotate()
    const obj = meshes.get(uid)
    if (!obj) return
    const groupId = (obj.userData as SceneObjectState).group_id
    if (selectedUids.includes(uid)) {
      replaceSelection(
        selectedUids.filter((item) => item !== uid),
        { clearLight: false },
      )
    }
    disposeSceneObject(obj)
    meshes.delete(uid)
    const pathUi = objectPathUid === uid || previewObjectPathUid === uid
    if (objectPathUid === uid) objectPathUid = null
    if (previewObjectPathUid === uid) previewObjectPathUid = null
    dissolveGroupIfSmall(groupId)
    if (pathUi) {
      emitPathEditUi()
      syncPathOverlay()
    }
    if (!suppressEmit) dropObserveTarget(uid)
    emitObjects()
  }

  function removeObjects(uids: readonly string[]) {
    if (groupRotateHeld) releaseGroupRotate()
    const removing = uids.filter((uid, index) => meshes.has(uid) && uids.indexOf(uid) === index)
    if (removing.length === 0) return
    const groupIds: string[] = []
    let pathUi = false
    suppressEmit = true
    try {
      for (const uid of removing) {
        const obj = meshes.get(uid)
        if (!obj) continue
        const groupId = (obj.userData as SceneObjectState).group_id
        if (groupId && !groupIds.includes(groupId)) groupIds.push(groupId)
        disposeSceneObject(obj)
        meshes.delete(uid)
        if (objectPathUid === uid || previewObjectPathUid === uid) pathUi = true
        if (objectPathUid === uid) objectPathUid = null
        if (previewObjectPathUid === uid) previewObjectPathUid = null
        dropObserveTarget(uid)
      }
      replaceSelection(
        selectedUids.filter((uid) => !removing.includes(uid)),
        { clearLight: false },
      )
    } finally {
      suppressEmit = false
    }
    for (const groupId of groupIds) dissolveGroupIfSmall(groupId)
    emitObjects()
    if (pathUi) {
      emitPathEditUi()
      syncPathOverlay()
    }
  }

  function dropObserveTarget(uid: string) {
    if (pathLook.mode !== "observe" || pathLook.object_uid !== uid) return
    pathLook = { mode: "observe", object_uid: "" }
    pathObserveArmed = false
    clearObserveHover()
    const points = currentPoints()
    if (points.length > 0) {
      commitAuthoring(points)
      return
    }
    if (cameraPath.authoring) {
      cameraPath = {
        ...cameraPath,
        authoring: { ...cameraPath.authoring, look: { mode: "observe", object_uid: "" } },
      }
      onCameraPathChange?.(cameraPath)
    }
    emitPathEditUi()
  }

  function clearObserveHover() {
    const uid = observeHoverUid
    observeHoverUid = null
    if (!uid || selectedUids.includes(uid) || !meshes.has(uid)) return
    applyHighlight(meshes.get(uid)!, false)
  }

  function syncObserveHover(e: PointerEvent) {
    if (!pathObserveArmed || draggingLook || playing) {
      clearObserveHover()
      return
    }
    setPointerRay(e)
    const uid = pickFromEventPosition()
    if (uid === observeHoverUid) return
    clearObserveHover()
    if (!uid || selectedUids.includes(uid) || !meshes.has(uid)) return
    observeHoverUid = uid
    applyHighlight(meshes.get(uid)!, true)
  }

  function clearObjects() {
    Array.from(meshes.keys()).forEach((uid) => removeObject(uid))
  }

  function currentCameraState(): SceneCameraState {
    const { yaw, pitch } = yawPitchFromCamera(camera)
    return {
      pos: [camera.position.x, camera.position.y, camera.position.z],
      target: [orbit.target.x, orbit.target.y, orbit.target.z],
      yaw,
      pitch,
      fov: camera.fov,
      mode: navMode,
    }
  }

  const dropFrustum = new THREE.Frustum()
  const dropProjScreen = new THREE.Matrix4()
  const dropLook = new THREE.Vector3()
  const dropFloorPoint = new THREE.Vector3()

  function dropViewHint(): DropViewHint {
    camera.updateMatrixWorld()
    dropProjScreen.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse)
    dropFrustum.setFromProjectionMatrix(dropProjScreen)
    camera.getWorldDirection(dropLook)

    let lookX = dropLook.x
    let lookZ = dropLook.z
    const lookLen = Math.hypot(lookX, lookZ)
    if (lookLen > 1e-6) {
      lookX /= lookLen
      lookZ /= lookLen
    } else {
      lookX = 0
      lookZ = -1
    }

    let preferX: number
    let preferZ: number
    if (Math.abs(dropLook.y) > 1e-6) {
      const t = -camera.position.y / dropLook.y
      if (t > 0) {
        preferX = camera.position.x + dropLook.x * t
        preferZ = camera.position.z + dropLook.z * t
      } else {
        preferX = camera.position.x + lookX * 4
        preferZ = camera.position.z + lookZ * 4
      }
    } else {
      preferX = camera.position.x + lookX * 4
      preferZ = camera.position.z + lookZ * 4
    }

    const visibleSectorKeys = new Set<string>()
    const platform = platformDropState?.platform
    if (platform && platformIsSet(platform)) {
      const cells = platformDropState?.cells ?? []
      for (const sector of walkableFloorSectors(platform, cells)) {
        const [cx, cz] = sectorWorldCenter(platform, sector.x, sector.z)
        dropFloorPoint.set(cx, 0.05, cz)
        if (dropFrustum.containsPoint(dropFloorPoint)) visibleSectorKeys.add(`${sector.x}:${sector.z}`)
      }
    }

    return {
      preferX,
      preferZ,
      visibleSectorKeys,
      camX: camera.position.x,
      camZ: camera.position.z,
      lookX,
      lookZ,
    }
  }

  function applyCameraPose(pos: [number, number, number], yaw: number, pitch: number, fov?: number) {
    axisHold = false
    camera.up.set(0, 1, 0)
    camera.position.set(...pos)
    lastGoodPos.copy(camera.position)
    vel.set(0, 0, 0)
    flyYaw = yaw
    flyPitch = pitch
    tYaw = yaw
    tPitch = pitch
    camera.rotation.set(pitch, yaw, 0)
    if (fov != null) {
      camera.fov = clamp(fov, 25, 100)
      camera.updateProjectionMatrix()
    }
  }

  function syncCameraScale(span: number) {
    const near = cameraNearForSpan(span)
    if (Math.abs(camera.near - near) > 1e-8) {
      camera.near = near
      camera.updateProjectionMatrix()
    }
    if (!(span > 0)) {
      fittedSpan = 0
      return
    }
    const spanChanged = spanShiftNeedsReframe(fittedSpan, span)
    const firstFit =
      !(fittedSpan > 0) &&
      cameraPoseMatchesDefault({
        pos: [camera.position.x, camera.position.y, camera.position.z],
        yaw: flyYaw,
        pitch: flyPitch,
      })
    if (!playing && !recording && (firstFit || spanChanged)) {
      const pose = cameraPoseForSpan(span)
      applyCameraPose(pose.pos, pose.yaw, pose.pitch)
      if (pose.target) orbit.target.set(...pose.target)
    }
    maybeFitLight(span, spanChanged)
    fittedSpan = span
  }

  function lightPositionsClose(a: readonly number[], b: readonly number[]): boolean {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i += 1) {
      if (Math.abs(a[i] - b[i]) > 0.05) return false
    }
    return true
  }

  function maybeFitLight(span: number, spanChanged: boolean) {
    if (playing || recording) return
    const preset = lightState.preset
    if (preset === "custom") {
      const incoming = lightState.position
      const stock = defaultLightPosition(CAMERA_REFERENCE_SPAN)
      const fitted = defaultLightPosition(span)
      const echo = lightFitFrom != null && lightPositionsClose(incoming, lightFitFrom)
      const stillDefault = lightPositionsClose(incoming, stock) || lightPositionsClose(incoming, fitted) || echo
      if (!stillDefault) {
        lightFitFrom = null
        return
      }
      if (lightPositionsClose(incoming, fitted)) return
      lightFitFrom = [incoming[0], incoming[1], incoming[2]]
      applyLightPose(
        {
          preset: "custom",
          position: fitted,
          ...(typeof lightState.intensity === "number" ? { intensity: lightState.intensity } : {}),
        },
        { retainIntensityIfOmitted: true },
      )
      return
    }
    if (preset !== "north" && preset !== "east" && preset !== "south" && preset !== "west" && preset !== "top") return
    const incoming = lightState.position
    const stock = lightPosition(preset, CAMERA_REFERENCE_SPAN)
    const fitted = lightPosition(preset, span)
    const echo = lightFitFrom != null && lightPositionsClose(incoming, lightFitFrom)
    if (!lightPositionsClose(incoming, stock) && !spanChanged && !echo) return
    if (lightPositionsClose(incoming, fitted)) return
    lightFitFrom = [incoming[0], incoming[1], incoming[2]]
    applyLightPose(
      {
        preset,
        position: fitted,
        ...(typeof lightState.intensity === "number" ? { intensity: lightState.intensity } : {}),
      },
      { retainIntensityIfOmitted: true },
    )
  }

  function setNavMode(mode: SceneNavMode) {
    if (playing) stopPlayback()
    if (axisHold) releaseAxisHold()
    // Orbit UI removed — always fly (legacy "orbit" payloads map here).
    navMode = mode === "orbit" ? "fly" : mode
    orbit.enabled = false
    const yp = yawPitchFromCamera(camera)
    flyYaw = yp.yaw
    flyPitch = yp.pitch
    tYaw = yp.yaw
    tPitch = yp.pitch
    vel.set(0, 0, 0)
  }

  function setCameraHeight(y: number) {
    if (playing) stopPlayback()
    camera.position.y = Math.max(flyMinY(platformLayer.span()), y)
  }

  const LOOK_PIVOT_FALLBACK_M = 6

  /** Точка на текущем луче взгляда. Не середина объектов и не центр платформы. */
  function viewPivot(): THREE.Vector3 {
    camera.getWorldDirection(fwd)
    const toTarget = orbit.target.clone().sub(camera.position)
    const ahead = fwd.dot(toTarget) > 0 && toTarget.lengthSq() > 1e-4
    const dist = ahead ? toTarget.length() : LOOK_PIVOT_FALLBACK_M
    return camera.position.clone().addScaledVector(fwd, dist)
  }

  const VIEW_ANIM_MS = 550
  /** Inside this phi the clamped fly pitch cannot hold the look. Use lookAt plus a horizontal up. */
  const AXIS_HOLD_PHI = Math.acos(Math.sin(PITCH_MAX))

  function easeInOut(t: number): number {
    return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2
  }

  function finishPoseAnimation() {
    const done = poseAnim?.onDone
    poseAnim = null
    done?.()
  }

  function cancelViewAnimation() {
    viewAnim = null
    finishPoseAnimation()
    cancelPathRewind()
  }

  function holdAxisLook(focus: THREE.Vector3, theta: number) {
    axisHoldUp.set(-Math.sin(theta), 0, -Math.cos(theta))
    if (axisHoldUp.lengthSq() < 1e-8) axisHoldUp.set(0, 0, -1)
    axisHoldFocus.copy(focus)
    axisHold = true
    camera.up.copy(axisHoldUp)
    camera.lookAt(focus)
    lastGoodPos.copy(camera.position)
    vel.set(0, 0, 0)
    orbit.target.copy(focus)
  }

  function releaseAxisHold() {
    if (!axisHold) return
    const theta = Math.atan2(-axisHoldUp.x, -axisHoldUp.z)
    const pitch = camera.position.y >= axisHoldFocus.y ? -PITCH_MAX : PITCH_MAX
    flyYaw = theta
    flyPitch = pitch
    tYaw = theta
    tPitch = pitch
    axisHold = false
    camera.up.set(0, 1, 0)
    camera.rotation.set(pitch, theta, 0)
  }

  function commitLookAtFocus(focus: THREE.Vector3) {
    axisHold = false
    camera.up.set(0, 1, 0)
    camera.lookAt(focus)
    const yp = yawPitchFromCamera(camera)
    const pitch = clamp(yp.pitch, -PITCH_MAX, PITCH_MAX)
    flyYaw = yp.yaw
    flyPitch = pitch
    tYaw = yp.yaw
    tPitch = pitch
    camera.rotation.set(pitch, yp.yaw, 0)
    lastGoodPos.copy(camera.position)
    vel.set(0, 0, 0)
    orbit.target.copy(focus)
  }

  function clampCameraToTravel() {
    const bound = sceneTravelBound(platformLayer.span())
    camera.position.x = clamp(camera.position.x, -bound, bound)
    camera.position.z = clamp(camera.position.z, -bound, bound)
    camera.position.y = clamp(camera.position.y, flyMinY(platformLayer.span()), bound)
  }

  function sphericalFromOffset(offset: THREE.Vector3): { theta: number; phi: number; radius: number } {
    const radius = Math.max(offset.length(), 0.05)
    const phi = Math.acos(clamp(offset.y / radius, -1, 1))
    const theta = Math.atan2(offset.x, offset.z)
    return { theta, phi, radius }
  }

  function placeOrbit(focus: THREE.Vector3, theta: number, phi: number, radius: number) {
    const clampedPhi = clamp(phi, 0, Math.PI)
    const sinPhi = Math.sin(clampedPhi)
    camera.position.set(
      focus.x + radius * sinPhi * Math.sin(theta),
      focus.y + radius * Math.cos(clampedPhi),
      focus.z + radius * sinPhi * Math.cos(theta),
    )
    clampCameraToTravel()
    if (clampedPhi <= AXIS_HOLD_PHI || clampedPhi >= Math.PI - AXIS_HOLD_PHI) {
      holdAxisLook(focus, theta)
    } else {
      commitLookAtFocus(focus)
    }
  }

  function orbitBasis(): { focus: THREE.Vector3; theta: number; phi: number; radius: number } {
    const focus = axisHold ? axisHoldFocus.clone() : viewPivot()
    const offset = camera.position.clone().sub(focus)
    if (offset.lengthSq() < 1e-4) {
      camera.getWorldDirection(fwd)
      offset.copy(fwd).negate().multiplyScalar(LOOK_PIVOT_FALLBACK_M)
    }
    const spherical = sphericalFromOffset(offset)
    if (axisHold) {
      const horizontal = Math.hypot(offset.x, offset.z)
      if (horizontal < spherical.radius * 0.02) {
        spherical.theta = Math.atan2(-axisHoldUp.x, -axisHoldUp.z)
      }
    }
    return { focus, ...spherical }
  }

  function animateToView(preset: SceneViewPreset) {
    if (exporting || recording || recordArmed || lookRecording || lookRewriteArmed) return
    if (playing) stopPlayback()
    if (pathLookThrough) {
      clearLookPreview()
      lookThroughReturn = null
      pathLookThrough = false
      emitPathEditUi()
      syncPathOverlay()
    }
    cancelPathRewind()
    finishPoseAnimation()
    const dir = VIEW_PRESET_DIRS[preset]
    const targetPhi = Math.acos(clamp(dir[1], -1, 1))
    const targetTheta = Math.atan2(dir[0], dir[2])
    const basis = preset === "iso" ? isoBasis() : orbitBasis()
    let dTheta = targetTheta - basis.theta
    dTheta = Math.atan2(Math.sin(dTheta), Math.cos(dTheta))
    viewAnim = {
      t0: performance.now(),
      theta0: basis.theta,
      phi0: basis.phi,
      dTheta,
      dPhi: targetPhi - basis.phi,
      radius0: basis.radius,
      radius1: preset === "iso" ? defaultFrameDistance() : basis.radius,
      focus: basis.focus,
    }
  }

  function sceneCenter(): THREE.Vector3 {
    const target = cameraPoseForSpan(platformLayer.span()).target ?? [0, 0.5, 0]
    return new THREE.Vector3(0, target[1], 0)
  }

  function defaultFrameDistance(): number {
    const pose = cameraPoseForSpan(platformLayer.span())
    const target = pose.target ?? [0, 0.5, 0]
    return Math.hypot(pose.pos[0] - target[0], pose.pos[1] - target[1], pose.pos[2] - target[2])
  }

  function isoBasis(): { focus: THREE.Vector3; theta: number; phi: number; radius: number } {
    const focus = sceneCenter()
    const offset = camera.position.clone().sub(focus)
    if (offset.length() < 1) {
      camera.getWorldDirection(fwd)
      offset.copy(fwd).negate().multiplyScalar(defaultFrameDistance())
    }
    return { focus, ...sphericalFromOffset(offset) }
  }

  /**
   * Keep the current viewing side. Distance fits the bounds to about a sixth of the
   * frame so the object stays in context (default fov is 30°).
   */
  function frameObject(uid: string) {
    if (exporting || deviceLookActive) return
    const mesh = meshes.get(uid)
    if (!mesh) return
    selectObject(uid)
    const box = new THREE.Box3().setFromObject(mesh)
    if (box.isEmpty()) return
    const center = box.getCenter(new THREE.Vector3())
    const radius = Math.max(box.getBoundingSphere(new THREE.Sphere()).radius, 0.25)
    const halfFov = Math.max((camera.fov * Math.PI) / 360, 0.05)
    const dist = (radius / Math.tan(halfFov)) * 6
    const offset = camera.position.clone().sub(center)
    if (offset.lengthSq() < 1e-6) {
      camera.getWorldDirection(fwd)
      offset.copy(fwd).negate()
    }
    offset.normalize().multiplyScalar(dist)
    const pos = center.clone().add(offset)
    const bound = sceneTravelBound(platformLayer.span())
    pos.x = clamp(pos.x, -bound, bound)
    pos.z = clamp(pos.z, -bound, bound)
    pos.y = clamp(pos.y, flyMinY(platformLayer.span()), bound)
    const look = center.clone().sub(pos)
    const yp = yawPitchFromDirection([look.x, look.y, look.z])
    animateToCameraState({
      pos: [pos.x, pos.y, pos.z],
      yaw: yp.yaw,
      pitch: clamp(yp.pitch, -PITCH_MAX, PITCH_MAX),
      fov: camera.fov,
      target: [center.x, center.y, center.z],
    })
  }

  function animateToCameraState(cam: SceneCameraState, onDone?: () => void) {
    if (exporting) {
      onDone?.()
      return
    }
    if (playing) stopPlayback()
    cancelPathRewind()
    viewAnim = null
    finishPoseAnimation()
    const normalized = normalizeCameraState(cam)
    let dYaw = normalized.yaw - flyYaw
    dYaw = Math.atan2(Math.sin(dYaw), Math.cos(dYaw))
    poseAnim = {
      t0: performance.now(),
      pos0: new THREE.Vector3().copy(camera.position),
      pos1: new THREE.Vector3(...normalized.pos),
      yaw0: flyYaw,
      dYaw,
      pitch0: flyPitch,
      dPitch: normalized.pitch - flyPitch,
      fov0: camera.fov,
      fov1: normalized.fov,
      target: normalized.target ?? null,
      mode: normalized.mode ?? null,
      onDone,
    }
  }

  function stepViewAnimation(now: number) {
    const anim = viewAnim
    if (!anim) return
    const t = Math.min(1, (now - anim.t0) / VIEW_ANIM_MS)
    const eased = easeInOut(t)
    const radius = anim.radius0 + (anim.radius1 - anim.radius0) * eased
    placeOrbit(anim.focus, anim.theta0 + anim.dTheta * eased, anim.phi0 + anim.dPhi * eased, radius)
    if (t >= 1) viewAnim = null
  }

  function stepPoseAnimation(now: number) {
    const anim = poseAnim
    if (!anim) return
    const t = Math.min(1, (now - anim.t0) / VIEW_ANIM_MS)
    const eased = easeInOut(t)
    poseLerp.lerpVectors(anim.pos0, anim.pos1, eased)
    applyCameraPose(
      [poseLerp.x, poseLerp.y, poseLerp.z],
      anim.yaw0 + anim.dYaw * eased,
      anim.pitch0 + anim.dPitch * eased,
      anim.fov0 + (anim.fov1 - anim.fov0) * eased,
    )
    if (t < 1) return
    if (anim.target) orbit.target.set(...anim.target)
    if (anim.mode) setNavMode(anim.mode)
    finishPoseAnimation()
  }

  function orbitAroundFocus(dx: number, dy: number) {
    if (inputMode() === "lookThrough") releaseLookThroughToFreeView()
    if (exporting || inputMode() !== "edit") return
    viewAnim = null
    cancelPathRewind()
    finishPoseAnimation()
    const basis = orbitBasis()
    placeOrbit(
      basis.focus,
      basis.theta - dx * VIEW_CUBE_ORBIT_RAD_PER_PX,
      basis.phi - dy * VIEW_CUBE_ORBIT_RAD_PER_PX,
      basis.radius,
    )
  }

  function emitViewOrientation() {
    if (!onViewOrientation) return
    camera.getWorldDirection(fwd)
    orientDir[0] = -fwd.x
    orientDir[1] = -fwd.y
    orientDir[2] = -fwd.z
    const widget = sceneToWidget(orientDir)
    widgetDir[0] = widget[0]
    widgetDir[1] = widget[1]
    widgetDir[2] = widget[2]
    widgetUp[0] = camera.up.x
    widgetUp[1] = -camera.up.z
    widgetUp[2] = camera.up.y
    onViewOrientation(widgetDir, widgetUp, matchViewPreset(orientDir))
  }

  function syncRecordGizmo() {
    if (trajectoryMode) {
      if (groupRotateHeld) releaseGroupRotate()
      detachTransform()
      return
    }
    transform.enabled = !pointerLookOnly()
  }

  function pointerLookOnly() {
    return recordArmed || recording
  }

  function cameraIsMoving() {
    if (playing || deviceLookActive) return true
    if (keys.size > 0 || pointerKeys.size > 0) return true
    if (analogStrafe !== 0 || analogVertical !== 0 || lookYawRate !== 0) return true
    if (viewAnim || poseAnim || returnGlide || pathRewind) return true
    return vel.lengthSq() > 1e-8
  }

  function disarmRecording() {
    if (!recordArmed) return
    recordArmed = false
    syncRecordGizmo()
    onRecordArmChange?.(false)
  }

  // «Взвод» записи: таймер НЕ идёт, пока камера не полетела, — иначе в начале
  // каждого пролёта была бы мёртвая пауза. Фактический старт — beginRecording()
  // по первой клавише движения (W) в onKeyDown.
  function startRecording(opts?: { overwrite?: boolean }) {
    cancelReturnHome()
    if (lookRecording) {
      stopLookPass()
      return
    }
    cancelLookRecording()
    lookRewriteArmed = false
    lookRewriteDown = false
    cancelPathRewind()
    if (cameraPlaceMode) setCameraPlaceMode(false)
    if (trajectoryMode) setTrajectoryMode(false)
    if (recording || recordArmed) return
    if (!activeCameraPathTakeId) {
      if (cameraPathTakesFull(cameraPathTakes)) {
        onCameraPathTakesFull?.()
        return
      }
      const takeId = addCameraPathTake(cameraPathTakeName(nextCameraPathTakeNumber(cameraPathTakes)))
      if (!takeId) {
        onCameraPathTakesFull?.()
        return
      }
    } else if (!opts?.overwrite && hasCameraPath(cameraPath)) {
      onRecordOverwriteNeeded?.()
      return
    }
    if (playing) stopPlayback()
    if (navMode !== "fly") setNavMode("fly")
    recordArmed = true
    if (selectedUids.length > 0) {
      if (objectDragging) finishObjectDrag()
      selectObject(null, { clearLight: false })
    }
    syncRecordGizmo()
    onRecordArmChange?.(true)
  }

  function dropPathFit() {
    cancelPathFitJob()
    cancelPathFitJob = () => {}
    pathFit.cancel()
  }

  function schedulePathFit(path: CameraPath, rewind: boolean, history?: CameraPathChangeOpts) {
    cancelPathFitJob()
    cancelPathFitJob = () => {}
    if (!pathNeedsFit(path) || destroyed) return
    const token = pathFit.begin(rewind)
    const snapshot = cloneCameraPath(path)
    const fov = camera.fov
    const epsilon = pathFitEpsilon()
    const cancel = startRecordedPathFit(snapshot, fov, epsilon, (fitted) => {
      if (cancelPathFitJob === cancel) cancelPathFitJob = () => {}
      const decision = pathFit.finish(token, playing, exporting)
      if (destroyed || !decision.apply || !fitted) return
      cameraPath = fitted
      onCameraPathChange?.(cameraPath, history)
      adoptPathSettings()
      syncPathOverlay()
      if (decision.rewind) beginPathRewind()
    })
    cancelPathFitJob = cancel
  }

  function cancelReturnHome() {
    returnHome = null
    returnGlide = false
    finishPoseAnimation()
  }

  function armReturnHome() {
    const first = cameraPath.samples[0]
    if (!first) return
    pathLookThrough = true
    emitPathEditUi()
    syncPathOverlay()
    returnHome = {
      pos: [first.pos[0], first.pos[1], first.pos[2]],
      yaw: first.yaw,
      pitch: first.pitch,
      fov: first.fov,
    }
  }

  function beginRecording() {
    cancelReturnHome()
    dropPathFit()
    cancelLookRecording()
    disarmRecording()
    cameraPath = emptyCameraPath()
    recording = true
    syncRecordGizmo()
    const { yaw, pitch } = yawPitchFromCamera(camera)
    recordMotionClock = createRecordMotionClock(performance.now(), {
      pos: [camera.position.x, camera.position.y, camera.position.z],
      yaw,
      pitch,
    })
    onRecordingChange?.(true)
    onCameraPathChange?.(cameraPath)
    syncPathOverlay()
  }

  // Стоп гасит и взведённую, и идущую запись: у кнопки и пробела одно действие «отмена».
  function stopRecording(opts?: { returnHome?: boolean }) {
    if (lookRecording) {
      stopLookPass()
      return
    }
    disarmRecording()
    if (!recording) return
    recording = false
    recordMotionClock = null
    onRecordingChange?.(false)
    syncRecordGizmo()
    syncPathOverlay()
    if (pathNeedsFit(cameraPath)) onCameraPathChange?.(cameraPath, { pendingFit: true })
    schedulePathFit(cameraPath, false)
    if (opts?.returnHome) armReturnHome()
  }

  function cancelPathRewind() {
    pathRewind = null
  }

  function beginPathRewind() {
    if (destroyed) return
    const path = shownCameraPath()
    if (path.samples.length < 2) return
    const fromMs = cameraPathDurationMs(path)
    if (!(fromMs > 0)) return
    viewAnim = null
    finishPoseAnimation()
    pathRewind = { t0: 0, fromMs, started: false }
  }

  function finishPathRewind() {
    pathRewind = null
    pathTimeMs = 0
    pathLookThrough = true
    const sample = sampleAt(0)
    if (sample) {
      applyCameraPose(sample.pos, sample.yaw, sample.pitch, sample.fov)
      reanchorOrbitTarget()
    }
    onPathTimeChange?.(0)
    emitPathEditUi()
    syncPathOverlay()
  }

  function stepPathRewind(now: number) {
    const anim = pathRewind
    if (!anim?.started) return
    const t = Math.min(1, (now - anim.t0) / VIEW_ANIM_MS)
    const eased = easeInOut(t)
    const ms = anim.fromMs * (1 - eased)
    pathTimeMs = ms
    const sample = sampleAt(ms)
    if (sample) applyCameraPose(sample.pos, sample.yaw, sample.pitch, sample.fov)
    onPathTimeChange?.(ms)
    if (t >= 1) finishPathRewind()
  }

  function cancelLookPass() {
    if (!lookRecording) return
    cancelLookRecording()
    if (playing) stopPlayback()
    emitPathEditUi()
    syncPathOverlay()
  }

  function shownCameraPath(): CameraPath {
    return clipBrokenCameraPath(cameraPath)
  }

  function cancelLookRecording() {
    const wasRecording = lookRecording
    lookRecording = false
    lookRecorded = []
    lookRecordBase = []
    lookRecordLastT = -Infinity
    if (wasRecording) onRecordingChange?.(false)
  }

  function currentLookKeys(path: CameraPath): PathLookKey[] {
    if (path.look_keys && path.look_keys.length > 0) return path.look_keys.map((key) => ({ ...key }))
    return path.samples.map((sample) => ({ t: lookTimeMs(path, sample.t), yaw: sample.yaw, pitch: sample.pitch }))
  }

  function finishLookRecording() {
    if (!lookRecording) return
    const t = lookTimeMs(cameraPath, pathTimeMs)
    const last = lookRecorded[lookRecorded.length - 1]
    if (!last || Math.abs(last.t - t) > 1e-3) {
      lookRecorded.push({ t, yaw: tYaw, pitch: tPitch })
    } else {
      last.yaw = tYaw
      last.pitch = tPitch
    }
    const merged = replaceLookRange(lookRecordBase, lookRecorded, lookTimeMs(cameraPath, lookRecordFrom), t)
    cancelLookRecording()
    pathLook = { mode: "recorded" }
    cameraPath = applyLookKeys(cameraPath, merged)
    if (cameraPath.authoring) {
      cameraPath = { ...cameraPath, authoring: { ...cameraPath.authoring, look: { mode: "recorded" } } }
    }
    onCameraPathChange?.(cameraPath)
    emitPathEditUi()
    syncPathOverlay()
  }

  function stopLookPass() {
    if (!lookRecording) return
    if (playing) stopPlayback()
    else finishLookRecording()
  }

  function clearLookPreview() {
    lookPreview = null
  }

  /** Превью после прохода не пишется в путь. Вид из камеры остаётся на записанных ключах. */
  function discardLookPreview() {
    if (!lookPreview) return
    clearLookPreview()
    if (!pathLookThrough) {
      syncPathOverlay()
      return
    }
    const sample = lookThroughSample()
    if (sample) {
      applyCameraPose(sample.pos, sample.yaw, sample.pitch, sample.fov)
      reanchorOrbitTarget()
    }
    syncPathOverlay()
  }

  /** Пауза «Вид из камеры»: уйти в свободный просмотр, не откатывая камеру к позе до режима. */
  function releaseLookThroughToFreeView() {
    if (!pathLookThrough || playing || exporting || lookRecording || lookRewriteArmed) return false
    clearLookPreview()
    lookThroughReturn = null
    pathLookThrough = false
    emitPathEditUi()
    syncPathOverlay()
    return true
  }

  type SceneInputMode = "edit" | "lookThrough" | "lookRewrite"

  function inputMode(): SceneInputMode {
    if (lookRecording || lookRewriteArmed) return "lookRewrite"
    if (pathLookThrough) return "lookThrough"
    return "edit"
  }

  function previewLook(yaw: number, pitch: number): { yaw: number; pitch: number } {
    if (!lookPreview) return { yaw, pitch }
    return {
      yaw: yaw + lookPreview.yaw,
      pitch: clamp(pitch + lookPreview.pitch, -PITCH_MAX, PITCH_MAX),
    }
  }

  function releaseDraggingLook() {
    draggingLook = false
  }

  function steerLookThrough(dx: number, dy: number) {
    if (dx === 0 && dy === 0) return
    if (!lookPreview) lookPreview = { yaw: 0, pitch: 0 }
    lookPreview.yaw -= dx * 0.0034
    lookPreview.pitch -= dy * 0.0034
    const sample = lookThroughSample()
    if (sample) {
      const posed = previewLook(sample.yaw, sample.pitch)
      tYaw = posed.yaw
      tPitch = posed.pitch
      axisHold = false
      camera.up.set(0, 1, 0)
      flyYaw = posed.yaw
      flyPitch = posed.pitch
      camera.rotation.set(posed.pitch, posed.yaw, 0)
    }
    if (!playing) syncPathOverlay()
  }

  function beginLookPass() {
    clearLookPreview()
    if (exporting || recording || recordArmed) return
    if (shownCameraPath().samples.length < 2) return
    if (playing) stopPlayback()
    pathLookThrough = true
    playPath()
    if (!playing) {
      emitPathEditUi()
      return
    }
    lookRecording = true
    lookRecordBase = currentLookKeys(cameraPath)
    lookRecorded = []
    lookRecordLastT = -Infinity
    lookRecordFrom = playFromMs
    onRecordingChange?.(true)
    emitPathEditUi()
    syncPathOverlay()
  }

  function armLookRewrite() {
    if (exporting || recording || recordArmed || lookRecording || playing) return
    if (shownCameraPath().samples.length < 2) return
    pathLook = { mode: "recorded" }
    const points = currentPoints()
    if (points.length > 0) commitAuthoring(points)
    else emitPathEditUi()
    placeAtPathStart()
    lookRewriteArmed = true
    lookRewriteDown = false
    emitPathEditUi()
  }

  function commitLookRewrite() {
    if (!lookRewriteArmed) return
    lookRewriteArmed = false
    lookRewriteDown = false
    emitPathEditUi()
    beginLookPass()
  }

  function captureLookKey(timeMs: number) {
    const t = lookTimeMs(cameraPath, timeMs)
    if (lookRecorded.length > 0 && t - lookRecordLastT < SAMPLE_INTERVAL_MS) return
    lookRecorded.push({ t, yaw: tYaw, pitch: tPitch })
    lookRecordLastT = t
  }

  function applyCameraPosition(pos: [number, number, number], fov?: number) {
    camera.position.set(...pos)
    lastGoodPos.copy(camera.position)
    vel.set(0, 0, 0)
    if (fov != null) {
      camera.fov = clamp(fov, 25, 100)
      camera.updateProjectionMatrix()
    }
  }

  function playPath(range?: PathPlayRange) {
    cancelReturnHome()
    if (lookRewriteArmed) {
      lookRewriteArmed = false
      lookRewriteDown = false
      emitPathEditUi()
    }
    const duration = sceneClockNow()
    if (!sceneMotionNow() || duration < 50) return
    if (recording || recordArmed) stopRecording()
    cancelPathRewind()
    playFromMs = clamp(range?.fromMs ?? 0, 0, duration)
    playToMs = clamp(range?.toMs ?? duration, playFromMs, duration)
    if (playToMs - playFromMs < 50) return
    selectObject(null, { clearLight: false })
    if (cameraPlaceMode) setCameraPlaceMode(false)
    playing = true
    playStartedAt = performance.now()
    syncRecordGizmo()
    syncPathOverlay()
    orbit.enabled = false
    onPlaybackChange?.(true)
    syncTransformAttachment()
  }

  /** После телепорта камеры — якорим orbit.target по взгляду (для пресетов). */
  function reanchorOrbitTarget() {
    camera.getWorldDirection(fwd)
    orbit.target.copy(camera.position).addScaledVector(fwd, 6)
  }

  function stopPlayback() {
    if (!playing) return
    if (lookRecording) finishLookRecording()
    playing = false
    const yp = yawPitchFromCamera(camera)
    flyYaw = yp.yaw
    flyPitch = yp.pitch
    tYaw = yp.yaw
    tPitch = yp.pitch
    onPlaybackChange?.(false)
    const finish = exportFinish
    exportFinish = null
    finish?.()
    syncPathOverlay()
    syncTransformAttachment()
  }

  function isPathAtEnd() {
    const duration = sceneClockNow()
    if (!sceneMotionNow() || duration <= 0) return false
    return duration - pathTimeMs < 50
  }

  function togglePathPlayback() {
    if (playing) {
      stopPlayback()
      return
    }
    const duration = sceneClockNow()
    if (!sceneMotionNow() || duration < 50) return
    const fromMs = duration - pathTimeMs < 50 ? 0 : pathTimeMs
    playPath({ fromMs })
  }

  function seekPath(ms: number) {
    const duration = sceneClockNow()
    if (duration <= 0 && !sceneMotionNow()) return
    cancelPathRewind()
    if (playing) stopPlayback()
    const t = clamp(ms, 0, duration)
    pathTimeMs = t
    if (pathLookThrough) {
      const sample = sampleAt(t)
      if (sample) {
        const posed = previewLook(sample.yaw, sample.pitch)
        applyCameraPose(sample.pos, posed.yaw, posed.pitch, sample.fov)
        reanchorOrbitTarget()
      }
    }
    onPathTimeChange?.(t)
    syncPathOverlay()
    syncTransformAttachment()
  }

  function placeAtPathStart() {
    if (shownCameraPath().samples.length < 2) return
    cancelPathRewind()
    if (lookRecording) stopLookPass()
    else if (playing) stopPlayback()
    pathTimeMs = 0
    clearLookPreview()
    const sample = sampleAt(0)
    if (!sample) return
    applyCameraPose(sample.pos, sample.yaw, sample.pitch)
    reanchorOrbitTarget()
    onPathTimeChange?.(0)
    onCameraPoseCommit?.()
    syncPathOverlay()
  }

  function replacePath(path: CameraPath, history?: Pick<CameraPathChangeOpts, "skipHistory" | "clearRedo">) {
    cancelReturnHome()
    if (recording) stopRecording()
    dropPathFit()
    if (playing) stopPlayback()
    cameraPath = path
    pathTimeMs = 0
    onPathTimeChange?.(0)
    if (pathNeedsFit(path)) {
      syncPathOverlay()
      if (history) onCameraPathChange?.(cameraPath, history)
      schedulePathFit(cameraPath, false, history ? { skipHistory: true } : undefined)
      return
    }
    onCameraPathChange?.(cameraPath, history)
    adoptPathSettings()
  }

  function recordIfNeeded() {
    if (!recording || !recordMotionClock) return
    const now = performance.now()
    const { yaw, pitch } = yawPitchFromCamera(camera)
    const pose = {
      pos: [camera.position.x, camera.position.y, camera.position.z] as [number, number, number],
      yaw,
      pitch,
      fov: camera.fov,
    }
    const epsPos = recordMotionEpsPos(effectiveFlySpeed(flySpeed, platformLayer.span()))
    const tick = tickRecordMotionClock(recordMotionClock, now, pose, epsPos)
    recordMotionClock = tick.clock
    const elapsed = tick.motionMs

    if (elapsed >= MAX_PATH_DURATION_MS) {
      const capped = appendCameraSample(cameraPath, pose, MAX_PATH_DURATION_MS)
      if (capped !== cameraPath) {
        cameraPath = capped
        recordMotionClock = markSampleAppended(recordMotionClock, pose)
      }
      onRecordTick?.(MAX_PATH_DURATION_MS)
      stopRecording({ returnHome: true })
      return
    }

    // Tick UI even while frozen so the label stays at the last motion-ms.
    onRecordTick?.(elapsed)
    if (!tick.shouldAppend) return
    const next = appendCameraSample(cameraPath, pose, elapsed)
    if (next !== cameraPath) {
      cameraPath = next
      recordMotionClock = markSampleAppended(recordMotionClock, pose)
    }
  }

  function stillSize(maxEdge?: number): { width: number; height: number } {
    const full = sceneStillExportSize(frameFormat)
    if (maxEdge == null || maxEdge < 2) return full
    const long = Math.max(full.width, full.height)
    if (long <= maxEdge) return full
    const scale = maxEdge / long
    const even = (n: number) => {
      const v = Math.max(2, Math.round(n))
      return v % 2 === 0 ? v : v - 1
    }
    return { width: even(full.width * scale), height: even(full.height * scale) }
  }

  async function captureStillBlob(opts?: { includeLightMarker?: boolean; maxEdge?: number }): Promise<Blob | null> {
    if (destroyed) return null
    const hadMarker = lightMarker.visible
    const gizmoTarget = transform.object ?? null
    const prevExporting = exporting
    exporting = true
    capturingStill = true
    syncPathOverlay()
    meshes.forEach((obj) => syncLabels(obj, false))
    lightMarker.visible = Boolean(opts?.includeLightMarker) && Boolean(platformLayer.span())
    transform.detach()
    transform.getHelper().visible = false
    const { width: exportW, height: exportH } = stillSize(opts?.maxEdge)
    // Clone so live camera.aspect stays stage-sized (avoids one-frame viewport squash).
    const shotCam = camera.clone()
    shotCam.aspect = exportW / exportH
    shotCam.updateProjectionMatrix()
    const target = new THREE.WebGLRenderTarget(exportW, exportH, {
      colorSpace: THREE.SRGBColorSpace,
    })
    let out: HTMLCanvasElement | null = null
    try {
      skyDome.syncToCamera(shotCam)
      themeRig?.syncToCamera(shotCam)
      renderer.setRenderTarget(target)
      renderer.clear()
      withObjectsAboveFloor(() => {
        renderer.render(scene, shotCam)
      })
      renderer.setRenderTarget(null)

      const buffer = new Uint8Array(exportW * exportH * 4)
      renderer.readRenderTargetPixels(target, 0, 0, exportW, exportH, buffer)
      const canvas2d = document.createElement("canvas")
      canvas2d.width = exportW
      canvas2d.height = exportH
      const ctx = canvas2d.getContext("2d")
      if (!ctx) return null
      const imageData = ctx.createImageData(exportW, exportH)
      for (let y = 0; y < exportH; y++) {
        const srcRow = (exportH - y - 1) * exportW * 4
        const dstRow = y * exportW * 4
        imageData.data.set(buffer.subarray(srcRow, srcRow + exportW * 4), dstRow)
      }
      ctx.putImageData(imageData, 0, 0)
      out = canvas2d
    } finally {
      target.dispose()
      exporting = prevExporting
      capturingStill = false
      lightMarker.visible = hadMarker
      if (gizmoTarget) {
        transform.attach(gizmoTarget)
        transform.getHelper().visible = true
        syncTransformAxisLocks()
      }
      const showLabels = labelsVisible()
      meshes.forEach((obj) => syncLabels(obj, showLabels))
      skyDome.syncToCamera(camera)
      themeRig?.syncToCamera(camera)
      syncPathOverlay()
    }
    if (!out) return null
    const encoded = out
    return await new Promise<Blob | null>((resolve) => {
      encoded.toBlob((blob) => resolve(blob), "image/jpeg", 0.92)
    })
  }

  async function exportPathVideo(range?: PathPlayRange & { restoreView?: boolean }): Promise<Blob> {
    const restoreView = range?.restoreView !== false
    if (exporting) throw new Error("Export already in progress")
    if (!sceneMotionNow()) throw new Error("No camera path to export")
    if (typeof MediaRecorder === "undefined" || typeof canvas.captureStream !== "function") {
      throw new Error("Video export is not supported in this browser")
    }
    cancelReturnHome()
    if (recording) stopRecording()
    pathFit.suppressRewind()
    await pathFit.settled()
    if (destroyed || !sceneMotionNow()) {
      throw new Error("No camera path to export")
    }
    cancelPathRewind()
    if (playing) stopPlayback()
    selectObject(null)
    if (cameraPlaceMode) setCameraPlaceMode(false)
    exporting = true
    syncPathOverlay()
    const hadMarkerLayer = camera.layers.isEnabled(LIGHT_MARKER_LAYER)
    lightMarker.visible = false
    camera.layers.disable(LIGHT_MARKER_LAYER)
    const { width: exportW, height: exportH } = previzExportSize(frameFormat)
    // Fixed buffer (dpr=1): phone visualViewport must not change capture aspect mid-stream.
    renderer.setPixelRatio(1)
    skyDome.setPixelRatio(1)
    renderer.setSize(exportW, exportH, false)
    camera.aspect = exportW / exportH
    camera.updateProjectionMatrix()
    // Contain-fit CSS so tall/wide buffer is not stretched across full-bleed stage.
    const fit = containFitSize(exportW, exportH, stage.clientWidth, stage.clientHeight)
    canvas.style.position = "absolute"
    canvas.style.left = "50%"
    canvas.style.top = "50%"
    canvas.style.transform = "translate(-50%, -50%)"
    canvas.style.width = `${fit.width}px`
    canvas.style.height = `${fit.height}px`
    try {
      return await new Promise<Blob>((resolve, reject) => {
        const stream = canvas.captureStream(30)
        const mime = ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"].find((m) =>
          MediaRecorder.isTypeSupported(m),
        )
        let recorder: MediaRecorder
        try {
          recorder = new MediaRecorder(stream, {
            ...(mime ? { mimeType: mime } : {}),
            videoBitsPerSecond: 8_000_000,
          })
        } catch (e) {
          stream.getTracks().forEach((track) => track.stop())
          reject(e instanceof Error ? e : new Error(String(e)))
          return
        }
        const chunks: Blob[] = []
        let settled = false
        const settleReject = (err: Error) => {
          if (settled) return
          settled = true
          reject(err)
        }
        const settleResolve = (blob: Blob) => {
          if (settled) return
          settled = true
          resolve(blob)
        }
        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) chunks.push(event.data)
        }
        recorder.onerror = () => {
          exportFinish = null
          exportReject = null
          exportRecorder = null
          exportStream = null
          stream.getTracks().forEach((track) => track.stop())
          settleReject(new Error("Video recording failed"))
        }
        recorder.onstop = () => {
          exportFinish = null
          exportReject = null
          exportRecorder = null
          exportStream = null
          stream.getTracks().forEach((track) => track.stop())
          const blob = new Blob(chunks, { type: mime ?? "video/webm" })
          if (blob.size === 0) {
            settleReject(new Error("Video export produced an empty file"))
            return
          }
          settleResolve(blob)
        }
        exportStream = stream
        exportRecorder = recorder
        exportReject = settleReject
        exportFinish = () => {
          if (recorder.state !== "inactive") recorder.stop()
        }
        recorder.start(250)
        playPath(range)
        if (!playing) {
          exportFinish = null
          exportReject = null
          exportRecorder = null
          exportStream = null
          recorder.onstop = () => {
            stream.getTracks().forEach((track) => track.stop())
          }
          if (recorder.state !== "inactive") recorder.stop()
          else stream.getTracks().forEach((track) => track.stop())
          settleReject(new Error("Video export could not start playback"))
        }
      })
    } finally {
      exporting = false
      syncPathOverlay()
      if (hadMarkerLayer) camera.layers.enable(LIGHT_MARKER_LAYER)
      lightMarker.visible = lightMarkerAllowed()
      exportFinish = null
      exportReject = null
      exportRecorder = null
      exportStream = null
      if (!exportDetached && restoreView) {
        // Force applyResize even if stage box unchanged — clear export canvas CSS.
        lastResizeW = 0
        lastResizeH = 0
        resize()
      } else if (!exportDetached) {
        canvas.style.visibility = "hidden"
      }
    }
  }

  function abortExport(reason: string) {
    if (!exporting && exportReject == null && exportFinish == null) return
    const reject = exportReject
    const recorder = exportRecorder
    const stream = exportStream
    exportFinish = null
    exportReject = null
    exportRecorder = null
    exportStream = null
    if (playing) {
      playing = false
      onPlaybackChange?.(false)
    }
    if (recorder && recorder.state !== "inactive") {
      recorder.ondataavailable = null
      recorder.onerror = null
      recorder.onstop = () => {
        stream?.getTracks().forEach((track) => track.stop())
        reject?.(new Error(reason))
      }
      recorder.stop()
      return
    }
    stream?.getTracks().forEach((track) => track.stop())
    reject?.(new Error(reason))
  }

  function applyLightPose(light: SceneLightState, opts?: { retainIntensityIfOmitted?: boolean }) {
    // During drag, React onVisual can echo stale light after clearSelection — don't snap back.
    if (lightDragging) return
    const span = platformLayer.span()
    const minY = lightMarkerMinY(span)
    const y = Math.max(light.position[1], minY)
    let intensity: number | undefined
    if (typeof light.intensity === "number" && Number.isFinite(light.intensity) && light.intensity > 0) {
      intensity = light.intensity
    } else if (
      opts?.retainIntensityIfOmitted &&
      typeof lightState.intensity === "number" &&
      Number.isFinite(lightState.intensity) &&
      lightState.intensity > 0
    ) {
      intensity = lightState.intensity
    }
    lightState = {
      preset: light.preset,
      position: [light.position[0], y, light.position[2]],
      ...(intensity != null ? { intensity } : {}),
    }
    point.position.set(...lightState.position)
    applyPointLightParams(span)
    lightMarker.position.set(...lightState.position)
    lightMarker.scale.setScalar(lightMarkerRadius(span))
    lightMarker.visible = lightMarkerAllowed()
    markCameraPlaceMonitorDirty()
  }

  function queueSurfaceFacePending(e: PointerEvent): boolean {
    if (!hasFaceSelection() || e.button !== 0 || playing || deviceLookActive) return false
    platformFacePending = null
    const hit = platformLayer.hitAt(e.clientX, e.clientY)
    if (hit?.kind === "opening") {
      const tile = { faceKey: hit.faceKey, u: hit.u, v: hit.v }
      platformFacePending = { faceKey: hit.faceKey, tile, shift: e.shiftKey }
      // Tile marquee/paint only in tiles mode; wall mode keeps UV for drill-down commit.
      if (platformSurfaceGrid) {
        if (!e.shiftKey) onPlatformFaceTiles?.([], false)
        startOpeningPaintFromPending()
      }
      canvas.setPointerCapture(e.pointerId)
      return true
    }
    if (hit?.kind === "face") {
      platformFacePending = { faceKey: hit.faceKey, tile: null, shift: e.shiftKey }
      canvas.setPointerCapture(e.pointerId)
      return true
    }
    const objectFace = objectWallAt(e)
    if (objectFace) {
      platformFacePending = { faceKey: objectFace, tile: null, shift: e.shiftKey }
      canvas.setPointerCapture(e.pointerId)
      return true
    }
    return false
  }

  function startOpeningPaintFromPending() {
    const pending = platformFacePending
    if (!pending?.tile) return false
    platformFacePending = null
    platformPaint = {
      kind: "opening",
      anchor: pending.tile,
      current: pending.tile,
      shift: pending.shift,
    }
    platformLayer.previewOpenings(new Set([`${pending.tile.faceKey}#${pending.tile.u}:${pending.tile.v}`]))
    return true
  }

  function commitSurfaceFacePending() {
    const pending = platformFacePending
    platformFacePending = null
    if (!pending) return
    clearPlatformPaintPreviews()
    selectObject(null)
    if (pending.tile) onPlatformFaceTiles?.([pending.tile], pending.shift)
    else onPlatformFace?.(pending.faceKey, pending.shift)
  }

  function anchorLightDrag(e: PointerEvent) {
    lightDragStartY = lightMarker.position.y
    lightDragStartClientX = e.clientX
    lightDragStartClientY = e.clientY
    lightDragStartPos.copy(lightMarker.position)
    lightDragShift = e.shiftKey
    setPointerRay(e)
    if (rayToGroundY(lightDragHit, lightDragStartY, lightMarker.position)) {
      lightDragOffset.copy(lightDragHit).sub(lightMarker.position)
    } else {
      lightDragOffset.set(0, 0, 0)
    }
  }

  function beginLightDrag(e: PointerEvent) {
    clearPlatformSelection()
    selectObject(null, { clearLight: false })
    setLightSelected(true)
    lightDragging = true
    lightDragMoved = false
    lightDragPointerId = e.pointerId
    lightDragPending = false
    anchorLightDrag(e)
    canvas.setPointerCapture(e.pointerId)
    canvas.focus()
  }

  function updateLightDrag(e: PointerEvent) {
    if (!lightDragging) return
    if (e.shiftKey !== lightDragShift) anchorLightDrag(e)

    const span = platformLayer.span()
    const bound = sceneTravelBound(span)
    const minY = lightMarkerMinY(span)
    const maxY = bound
    const drag = lightDragMetersPerPx(span)

    if (lightDragShift) {
      // Shift: freeze Y; screen X/Y → camera-right / camera-forward on the XZ plane.
      camera.getWorldDirection(fwd)
      fwd.y = 0
      if (fwd.lengthSq() < 1e-6) fwd.set(0, 0, 1)
      else fwd.normalize()
      rgt.crossVectors(fwd, worldUp)
      if (rgt.lengthSq() < 1e-6) rgt.set(1, 0, 0)
      else rgt.normalize()
      const dx = (e.clientX - lightDragStartClientX) * drag
      const dy = (lightDragStartClientY - e.clientY) * drag
      lightMarker.position.copy(lightDragStartPos).addScaledVector(rgt, dx).addScaledVector(fwd, dy)
      lightMarker.position.y = lightDragStartY
    } else {
      const y = clamp(lightDragStartY + (lightDragStartClientY - e.clientY) * drag, minY, maxY)
      // XZ on start-Y plane so changing height does not push the light into depth.
      setPointerRayAt(e.clientX, lightDragStartClientY)
      if (rayToGroundY(lightDragHit, lightDragStartY, lightMarker.position)) {
        lightMarker.position.copy(lightDragHit).sub(lightDragOffset)
      }
      lightMarker.position.y = y
    }

    lightMarker.position.x = clamp(lightMarker.position.x, -bound, bound)
    lightMarker.position.z = clamp(lightMarker.position.z, -bound, bound)
    point.position.copy(lightMarker.position)
    lightDragMoved = true
  }

  function finishLightDrag() {
    if (!lightDragging) return
    lightDragging = false
    if (lightDragPointerId != null) {
      try {
        canvas.releasePointerCapture(lightDragPointerId)
      } catch {
        // already released
      }
    }
    lightDragPointerId = null
    if (lightDragMoved) {
      const next: SceneLightState = {
        preset: "custom",
        position: [lightMarker.position.x, lightMarker.position.y, lightMarker.position.z],
        ...(typeof lightState.intensity === "number" ? { intensity: lightState.intensity } : {}),
      }
      applyLightPose(next)
      onLightChange?.(next)
    }
    lightDragMoved = false
  }

  function pickLightMarker(e: PointerEvent): boolean {
    if (!lightMarker.visible || !canEdit) return false
    const rect = canvas.getBoundingClientRect()
    pointerNdc.x = ((e.clientX - rect.left) / Math.max(rect.width, 1)) * 2 - 1
    pointerNdc.y = -((e.clientY - rect.top) / Math.max(rect.height, 1)) * 2 + 1
    raycaster.setFromCamera(pointerNdc, camera)
    const hits = raycaster.intersectObject(lightMarker, false)
    return hits.length > 0
  }

  function applyFlyLook(dt: number) {
    const s = Math.min(1, dt * 14)
    flyYaw += (tYaw - flyYaw) * s
    flyPitch += (tPitch - flyPitch) * s
    camera.rotation.set(flyPitch, flyYaw, 0)
  }

  function updateFly(dt: number) {
    if (axisHold) {
      camera.up.copy(axisHoldUp)
      camera.lookAt(axisHoldFocus)
      lastGoodPos.copy(camera.position)
      return
    }
    if (lookYawRate !== 0) tYaw += lookYawRate * dt
    applyFlyLook(dt)

    camera.getWorldDirection(fwd)
    rgt.crossVectors(fwd, worldUp)
    // Near ±90° pitch: fwd ∥ up → zero cross; normalize() → NaN → camera flies to void.
    if (rgt.lengthSq() < 1e-8) {
      rgt.set(-Math.sin(flyYaw), 0, -Math.cos(flyYaw))
    } else {
      rgt.normalize()
    }
    want.set(0, 0, 0)
    const flyDown = (axis: SceneFlyAxis) => keys.has(axis) || pointerKeys.has(axis)
    if (flyDown("f")) want.add(fwd)
    if (flyDown("b")) want.sub(fwd)
    if (flyDown("r")) want.add(rgt)
    if (flyDown("l")) want.sub(rgt)
    if (flyDown("u")) want.y += 1
    if (flyDown("d")) want.y -= 1
    const baseSpeed = effectiveFlySpeed(flySpeed, platformLayer.span())
    if (want.lengthSq() > 0) {
      want.normalize().multiplyScalar(baseSpeed)
    }
    // Analog axes (mobile left stick) — do not normalize with binary keys.
    if (analogStrafe !== 0) want.addScaledVector(rgt, analogStrafe * (baseSpeed / Math.max(flySpeed, 0.01)))
    if (analogVertical !== 0) want.y += analogVertical * (baseSpeed / Math.max(flySpeed, 0.01))
    // Snap velocity to input — lerp accel/coast wrote soft tails into previz paths.
    vel.copy(want)
    camera.position.addScaledVector(vel, dt)

    const bound = sceneTravelBound(platformLayer.span())
    camera.position.x = clamp(camera.position.x, -bound, bound)
    camera.position.z = clamp(camera.position.z, -bound, bound)
    camera.position.y = clamp(camera.position.y, flyMinY(platformLayer.span()), bound)

    if (
      !Number.isFinite(camera.position.x) ||
      !Number.isFinite(camera.position.y) ||
      !Number.isFinite(camera.position.z) ||
      !Number.isFinite(vel.x) ||
      !Number.isFinite(vel.y) ||
      !Number.isFinite(vel.z)
    ) {
      vel.set(0, 0, 0)
      analogStrafe = 0
      analogVertical = 0
      lookYawRate = 0
      if (Number.isFinite(lastGoodPos.x) && Number.isFinite(lastGoodPos.y) && Number.isFinite(lastGoodPos.z)) {
        camera.position.copy(lastGoodPos)
        flyYaw = tYaw
        flyPitch = tPitch
      } else {
        const pose = cameraPoseForSpan(platformLayer.span())
        camera.position.set(...pose.pos)
        flyYaw = pose.yaw
        flyPitch = pose.pitch
        tYaw = pose.yaw
        tPitch = pose.pitch
        if (pose.target) orbit.target.set(...pose.target)
      }
      if (
        !Number.isFinite(flyYaw) ||
        !Number.isFinite(flyPitch) ||
        !Number.isFinite(tYaw) ||
        !Number.isFinite(tPitch)
      ) {
        const pose = cameraPoseForSpan(platformLayer.span())
        flyYaw = pose.yaw
        flyPitch = pose.pitch
        tYaw = pose.yaw
        tPitch = pose.pitch
      }
      camera.rotation.set(flyPitch, flyYaw, 0)
    } else {
      lastGoodPos.copy(camera.position)
    }
  }

  function pickFromEvent(e: PointerEvent) {
    const rect = canvas.getBoundingClientRect()
    pointerNdc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1
    pointerNdc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1
    raycaster.setFromCamera(pointerNdc, camera)
    const hits = raycaster.intersectObjects(objectsRoot.children, true)
    for (const hit of hits) {
      let node: THREE.Object3D | null = hit.object
      while (node && node.parent !== objectsRoot) node = node.parent
      if (node && meshes.has(node.userData.uid)) return node.userData.uid as string
    }
    return null
  }

  /** Short click on empty canvas (not mesh / label) clears object selection. */
  function deselectObjectIfEmptyClick(e: PointerEvent, moved: boolean) {
    if (moved || transformDragging || objectDragging) return
    if (pickFromEvent(e) != null) return
    if (selectedUid != null) selectObject(null)
  }

  function maybeBeginFromMotion() {
    if (recordArmed) beginRecording()
  }

  function viewFollowsPath() {
    return exporting || (playing && (pathLookThrough || deviceLookFollowsPath))
  }

  /** Dolly along look (fly replacement for OrbitControls zoom / trackpad pinch). */
  function dollyFly(deltaAlongLook: number) {
    if (inputMode() === "lookThrough" && !releaseLookThroughToFreeView()) return
    if (returnGlide) cancelReturnHome()
    if (viewFollowsPath() || transformDragging || objectDragging) return
    if (navMode !== "fly") return
    if (!Number.isFinite(deltaAlongLook) || Math.abs(deltaAlongLook) < 1e-6) return
    if (recordArmed) maybeBeginFromMotion()
    camera.getWorldDirection(fwd)
    camera.position.addScaledVector(fwd, deltaAlongLook)
    const bound = sceneTravelBound(platformLayer.span())
    camera.position.x = clamp(camera.position.x, -bound, bound)
    camera.position.z = clamp(camera.position.z, -bound, bound)
    camera.position.y = clamp(camera.position.y, flyMinY(platformLayer.span()), bound)
    lastGoodPos.copy(camera.position)
  }

  const activePointers = new Map<number, { x: number; y: number }>()
  let pinching = false
  let pinchLastDist = 0
  /** Skip object-select / look-click after a pinch gesture ends. */
  let ignoreClickAfterPinch = false
  const PINCH_DOLLY_PER_PX = 0.045
  const WHEEL_DOLLY_SCALE = 0.012
  const WHEEL_CTRL_DOLLY_SCALE = 0.05

  function endPinch() {
    if (pinching) ignoreClickAfterPinch = true
    pinching = false
    pinchLastDist = 0
  }

  function pinchDistance() {
    const pts = [...activePointers.values()]
    if (pts.length < 2) return 0
    return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y)
  }

  function syncPinchFromPointers() {
    if (activePointers.size < 2) {
      endPinch()
      return
    }
    const dist = pinchDistance()
    if (!pinching) {
      pinching = true
      pinchLastDist = dist
      releaseDraggingLook()
      canvas.classList.remove("dragging")
      objectDragPendingUid = null
      if (objectDragging) finishObjectDrag()
      return
    }
    const delta = dist - pinchLastDist
    pinchLastDist = dist
    dollyFly(delta * PINCH_DOLLY_PER_PX)
  }

  function onWheel(e: WheelEvent) {
    if (viewFollowsPath() || transformDragging || objectDragging) return
    // Stage listens in bubble before React root; skip UI that owns the wheel (history list, etc.).
    if (e.target instanceof Element && e.target.closest("[data-scene-wheel-scroll]")) return
    e.preventDefault()
    const scale = e.ctrlKey ? WHEEL_CTRL_DOLLY_SCALE : WHEEL_DOLLY_SCALE
    const speedScale = Math.max(0.35, effectiveFlySpeed(flySpeed, platformLayer.span()) * 0.15)
    dollyFly(-e.deltaY * scale * speedScale)
  }

  /** Capture on stage so phone-fly overlay still receives pinch/wheel. */
  function isDeviceFlyChromeTarget(target: EventTarget | null) {
    return target instanceof Element && Boolean(target.closest("[data-device-fly-stick], [data-device-fly-record]"))
  }

  function onPinchPointerDown(e: PointerEvent) {
    // Dual sticks / record: fingers on fly chrome must not enter the pinch tracker or
    // capture-phase stopPropagation will kill the other stick's pointer stream.
    // Skip in modal too (deviceLookActive false) — same chrome, same bug.
    if (isDeviceFlyChromeTarget(e.target)) return
    activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (activePointers.size >= 2) {
      syncPinchFromPointers()
      e.stopPropagation()
    }
  }

  function onPinchPointerMove(e: PointerEvent) {
    if (!activePointers.has(e.pointerId)) return
    activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pinching || activePointers.size >= 2) {
      syncPinchFromPointers()
      e.preventDefault()
      e.stopPropagation()
    }
  }

  function onPinchPointerUp(e: PointerEvent) {
    if (!activePointers.has(e.pointerId)) return
    activePointers.delete(e.pointerId)
    if (activePointers.size < 2) endPinch()
  }

  function shortestAngleDelta(from: number, to: number) {
    let d = to - from
    while (d > Math.PI) d -= Math.PI * 2
    while (d < -Math.PI) d += Math.PI * 2
    return d
  }

  function applyDeviceLookPreview(yaw: number, pitch: number) {
    const sample = sampleAt(Math.min(pathTimeMs, cameraPathDurationMs(shownCameraPath())))
    if (!sample) return
    lookPreview = {
      yaw: shortestAngleDelta(sample.yaw, yaw),
      pitch: pitch - sample.pitch,
    }
    const posed = previewLook(sample.yaw, sample.pitch)
    axisHold = false
    camera.up.set(0, 1, 0)
    flyYaw = posed.yaw
    flyPitch = posed.pitch
    tYaw = posed.yaw
    tPitch = posed.pitch
    camera.rotation.set(posed.pitch, posed.yaw, 0)
  }

  function applyDeviceLookAbsolute(yaw: number, pitch: number) {
    const nextPitch = clamp(pitch, -DEVICE_LOOK_PITCH_MAX, DEVICE_LOOK_PITCH_MAX)
    axisHold = false
    camera.up.set(0, 1, 0)
    flyYaw = yaw
    flyPitch = nextPitch
    tYaw = yaw
    tPitch = nextPitch
    camera.rotation.set(nextPitch, yaw, 0)
  }

  function setFlyLook(yaw: number, pitch: number) {
    if (inputMode() === "lookThrough") {
      if (deviceLookActive && !lookRecording && !exporting && playing && deviceLookFollowsPath) {
        applyDeviceLookAbsolute(yaw, pitch)
      } else if (deviceLookActive && !lookRecording && !viewFollowsPath()) {
        applyDeviceLookPreview(yaw, pitch)
      }
      return
    }
    if (returnGlide) cancelReturnHome()
    // Пролёт взгляда едет по траектории, но yaw/pitch пишет гироскоп.
    if (viewFollowsPath() && !lookRecording) return
    if (axisHold) releaseAxisHold()
    const pitchMax = deviceLookActive ? DEVICE_LOOK_PITCH_MAX : PITCH_MAX
    const nextPitch = clamp(pitch, -pitchMax, pitchMax)
    if (
      deviceLookActive &&
      recordArmed &&
      (Math.abs(shortestAngleDelta(tYaw, yaw)) > DEVICE_LOOK_RECORD_EPS ||
        Math.abs(nextPitch - tPitch) > DEVICE_LOOK_RECORD_EPS)
    ) {
      maybeBeginFromMotion()
    }
    tYaw = yaw
    tPitch = nextPitch
  }

  const FLY_INPUT_KEYS = {
    forward: "f",
    backward: "b",
    left: "l",
    right: "r",
    up: "u",
    down: "d",
  } as const

  function setFlyKey(code: string, down: boolean) {
    if (down) keys.add(code)
    else keys.delete(code)
  }

  function setFlyInput(input: SceneFlyInput) {
    if (viewFollowsPath() || navMode !== "fly") {
      clearFlyInput()
      return
    }
    const moving =
      input.forward === true ||
      input.backward === true ||
      input.left === true ||
      input.right === true ||
      input.up === true ||
      input.down === true
    if (recordArmed && moving) maybeBeginFromMotion()
    if (input.forward !== undefined) setFlyKey(FLY_INPUT_KEYS.forward, input.forward)
    if (input.backward !== undefined) setFlyKey(FLY_INPUT_KEYS.backward, input.backward)
    if (input.left !== undefined) setFlyKey(FLY_INPUT_KEYS.left, input.left)
    if (input.right !== undefined) setFlyKey(FLY_INPUT_KEYS.right, input.right)
    if (input.up !== undefined) setFlyKey(FLY_INPUT_KEYS.up, input.up)
    if (input.down !== undefined) setFlyKey(FLY_INPUT_KEYS.down, input.down)
  }

  function clearFlyInput() {
    setFlyKey(FLY_INPUT_KEYS.forward, false)
    setFlyKey(FLY_INPUT_KEYS.backward, false)
    setFlyKey(FLY_INPUT_KEYS.left, false)
    setFlyKey(FLY_INPUT_KEYS.right, false)
    setFlyKey(FLY_INPUT_KEYS.up, false)
    setFlyKey(FLY_INPUT_KEYS.down, false)
    pointerKeys.clear()
    lookYawRate = 0
    analogStrafe = 0
    analogVertical = 0
    vel.set(0, 0, 0)
  }

  function setFlyHeld(axis: SceneFlyAxis, held: boolean) {
    if (!FLY_HELD_AXES.has(axis)) return
    if (!held) {
      pointerKeys.delete(axis)
      return
    }
    if (viewFollowsPath()) return
    if (navMode !== "fly") setNavMode("fly")
    pointerKeys.add(axis)
    if (recordArmed) maybeBeginFromMotion()
  }

  function setFlyAnalogAxes(axes: { strafe?: number; vertical?: number }) {
    if (viewFollowsPath() || navMode !== "fly") {
      analogStrafe = 0
      analogVertical = 0
      return
    }
    if (axes.strafe !== undefined) {
      analogStrafe = Number.isFinite(axes.strafe) ? axes.strafe : 0
    }
    if (axes.vertical !== undefined) {
      analogVertical = Number.isFinite(axes.vertical) ? axes.vertical : 0
    }
    if (recordArmed && (analogStrafe !== 0 || analogVertical !== 0)) {
      maybeBeginFromMotion()
    }
  }

  function setFlyThrust(forward: boolean) {
    setFlyInput({ forward })
  }

  function setLookYawRate(rateRadPerSec: number) {
    const next = Number.isFinite(rateRadPerSec) ? rateRadPerSec : 0
    if (recordArmed && Math.abs(next) > LOOK_YAW_RATE_RECORD_EPS) maybeBeginFromMotion()
    lookYawRate = next
  }

  function setDeviceLookActive(active: boolean) {
    deviceLookActive = active
    if (active) {
      if (playing) stopPlayback()
      setNavMode("fly")
      clearFlyInput()
      releaseDraggingLook()
      canvas.classList.remove("dragging")
      selectObject(null)
    } else {
      clearFlyInput()
      releaseDraggingLook()
      canvas.classList.remove("dragging")
    }
  }

  function setDeviceLookFollowsPath(active: boolean) {
    deviceLookFollowsPath = active
  }

  function objectFaceFromHit(hit: THREE.Intersection, uid: string): string | null {
    if (!hit.face) return null
    const normal = hit.face.normal.clone().transformDirection(hit.object.matrixWorld)
    const ax = Math.abs(normal.x)
    const ay = Math.abs(normal.y)
    const az = Math.abs(normal.z)
    let face = "pz"
    if (ay >= ax && ay >= az) face = normal.y >= 0 ? "py" : "ny"
    else if (ax >= az) face = normal.x >= 0 ? "px" : "nx"
    else face = normal.z >= 0 ? "pz" : "nz"
    return objectFaceKey(uid, face)
  }

  function objectWallAt(e: PointerEvent): string | null {
    const rect = canvas.getBoundingClientRect()
    pointerNdc.x = ((e.clientX - rect.left) / Math.max(rect.width, 1)) * 2 - 1
    pointerNdc.y = -((e.clientY - rect.top) / Math.max(rect.height, 1)) * 2 + 1
    raycaster.setFromCamera(pointerNdc, camera)
    const hits = raycaster.intersectObjects(objectsRoot.children, true)
    for (const hit of hits) {
      let node: THREE.Object3D | null = hit.object
      while (node && node.parent !== objectsRoot) node = node.parent
      const uid = node?.userData.uid as string | undefined
      if (uid && meshes.has(uid)) return objectFaceFromHit(hit, uid)
    }
    return null
  }

  function activatePlatformTool(tool: "sector" | "surface") {
    platformTool = tool
    onPlatformToolActivated?.(tool)
  }

  function applyPlatformUpEffect(effect: PlatformUpEffect, _screen?: { x: number; y: number }) {
    if (effect.type === "clear") {
      clearPlatformSelection()
      dismissEnvironmentChrome()
      return
    }
    if (effect.type === "noop") return
    if (effect.type === "activateEnvironment") return
    if (effect.type === "sectors") {
      clearPlatformPaintPreviews()
      dismissEnvironmentChrome()
      onPlatformSectors?.(effect.sectors, effect.additive)
      return
    }
    if (effect.type === "activate") {
      clearPlatformPaintPreviews()
      dismissEnvironmentChrome()
      if (effect.target.kind === "sector") {
        selectObject(null)
        activatePlatformTool("sector")
        onPlatformSectors?.([{ x: effect.target.x, z: effect.target.z }], effect.shift)
        return
      }
      if (effect.target.kind === "object") {
        if (clearOnlyIfCrossType("object")) return
        selectObject(effect.target.uid, { shift: effect.shift })
        return
      }
      selectObject(null)
      activatePlatformTool("surface")
      if (effect.target.tile) onPlatformFaceTiles?.([effect.target.tile], effect.shift)
      else onPlatformFace?.(effect.target.faceKey, effect.shift)
    }
  }

  function sectorCellAt(clientX: number, clientY: number): { x: number; z: number } | null {
    // Prefer mesh top hit so extruded platforms pick under the cursor (y=0 plane is offset).
    const hit = platformLayer.hitAt(clientX, clientY)
    if (hit?.kind === "sector") return { x: hit.x, z: hit.z }
    if (hit?.kind === "opening" || hit?.kind === "face") {
      const parsed = parseCellFaceKey(hit.faceKey)
      if (parsed) return { x: parsed.x, z: parsed.z }
    }
    return platformLayer.sectorOnPlane(clientX, clientY)
  }

  function classifyPlatformFromEvent(e: PointerEvent, objectUid?: string | null) {
    const hit = platformLayer.hitAt(e.clientX, e.clientY)
    const meshHit = hit?.kind === "sector" || hit?.kind === "face" || hit?.kind === "opening" ? hit.kind : "miss"
    const cell = hit?.kind === "sector" ? { x: hit.x, z: hit.z } : sectorCellAt(e.clientX, e.clientY)
    const objectWallFaceKey = objectWallAt(e)
    return classifyPlatformDown({
      button: e.button,
      shift: e.shiftKey,
      canEdit,
      playing,
      deviceLookActive,
      recordArmed,
      recording,
      cameraMoving: cameraIsMoving(),
      hasSectorSelection: hasSectorSelection(),
      hasFaceSelection: hasFaceSelection(),
      meshHit,
      cell,
      faceKey: hit?.kind === "face" || hit?.kind === "opening" ? hit.faceKey : undefined,
      tile: hit?.kind === "opening" ? { faceKey: hit.faceKey, u: hit.u, v: hit.v } : undefined,
      objectWallFaceKey,
      objectUid,
      cameraPlace: cameraPlaceMode
        ? { active: true, sectorIndex: cameraPlaceIndexUnderCursor(e.clientX, e.clientY, hit) }
        : undefined,
    })
  }

  const pathHandleHit = new THREE.Vector3()

  function storedLook(): PathLook {
    return clonePathLook(pathLook)
  }

  function emitPathEditUi() {
    onPathEditUi?.({
      active: trajectoryMode,
      draw: pathDraw,
      observeArmed: pathObserveArmed,
      smooth: pathSmooth,
      durationMs: pathDurationMs,
      look: pathLook,
      lookRecorded: (cameraPath.look_keys?.length ?? 0) > 0,
      lookRecording,
      lookRewriteArmed,
      lookThrough: pathLookThrough,
      objectPathUid: pathTargetsObject() ? objectPathUid : null,
      previewObjectPathUid: pathPreviewing() ? previewObjectPathUid : null,
    })
  }

  function objectCarriers(): ObjectPathCarrier[] {
    const out: ObjectPathCarrier[] = []
    meshes.forEach((mesh, uid) => {
      const data = mesh.userData as SceneObjectState
      const position =
        data.position ?? ([mesh.position.x, mesh.position.y, mesh.position.z] as [number, number, number])
      out.push({
        uid,
        group_id: data.group_id,
        position: [...position] as [number, number, number],
        path: data.path,
      })
    })
    return out
  }

  function sceneClockNow(): number {
    return sceneClockMs(cameraPathDurationMs(shownCameraPath()), objectCarriers())
  }

  function sceneMotionNow(): boolean {
    if (hasCameraPath(shownCameraPath())) return true
    return objectCarriers().some((obj) => objectPathHasContent(obj.path))
  }

  function poseObjectMeshes(ms: number) {
    const objects = objectCarriers()
    const follows = objectPathFollows(objects)
    for (const obj of objects) {
      const mesh = meshes.get(obj.uid)
      if (!mesh) continue
      const pos = posedObjectPosition(obj, ms, objects, follows)
      mesh.position.set(pos[0], pos[1], pos[2])
    }
    objectPosesApplied = true
  }

  function restoreObjectMeshes() {
    if (!objectPosesApplied) return
    meshes.forEach((mesh) => {
      const position = (mesh.userData as SceneObjectState).position
      if (!position) return
      mesh.position.set(position[0], position[1], position[2])
    })
    objectPosesApplied = false
  }

  function objectPathRecord(uid: string): ObjectPath | undefined {
    return (meshes.get(uid)?.userData as SceneObjectState | undefined)?.path
  }

  function objectCenter(uid: string): PathVec3 | null {
    const mesh = meshes.get(uid)
    if (!mesh) return null
    const box = new THREE.Box3().setFromObject(mesh)
    if (box.isEmpty()) return null
    const center = box.getCenter(new THREE.Vector3())
    return [center.x, center.y, center.z]
  }

  function posedSample(sample: CameraPathSample): CameraPathSample {
    const look = cameraPath.authoring?.look
    if (look?.mode === "recorded") {
      if (recordedLookHasTarget(look)) {
        const aimed = aimSampleAtLook(sample, look, (uid) => objectCenter(uid), lookTimeMs(cameraPath, sample.t))
        if (aimed) return aimed
      }
      const keys = cameraPath.look_keys
      const yp = keys && keys.length > 0 ? sampleLookKeys(keys, lookTimeMs(cameraPath, sample.t)) : null
      if (!yp) return sample
      return { ...sample, yaw: yp.yaw, pitch: yp.pitch }
    }
    if (!look || look.mode !== "observe") return sample
    const center = objectCenter(look.object_uid)
    if (!center) return sample
    const yp = lookYawPitch(sample.pos, [0, 0, 0], look, center)
    return { ...sample, yaw: yp.yaw, pitch: yp.pitch }
  }

  function sampleAt(ms: number): CameraPathSample | null {
    const path = shownCameraPath()
    const sample = interpolateCameraPath(path, ms)
    if (!sample) return null
    const authoring = path.authoring
    if (!authoring || authoring.points.length < 2) return posedSample(sample)
    const duration = cameraPathDurationMs(path)
    const fraction = duration > 0 ? lookTimeMs(path, sample.t) / duration : 0
    const at = authoredPositionAt(authoring, fraction)
    if (!at) return posedSample(sample)
    const pos = clampAbovePlatform(at.pos)
    if (authoring.look.mode !== "tangent") return posedSample({ ...sample, pos })
    const yp = splineSampleLook(authoring, pos, at.dir)
    return posedSample({ ...sample, pos, yaw: yp.yaw, pitch: yp.pitch })
  }

  /** Поставленная камера без пролёта: первая точка и сохранённый курс. */
  function placedCameraSample(): CameraPathSample | null {
    const shown = shownCameraPath()
    const point = shown.authoring?.points[0]
    if (!point) return null
    const aim = cameraPlaceAim ?? shown.authoring?.placed_aim ?? { yaw: 0, pitch: 0 }
    return { t: 0, pos: point.pos, yaw: aim.yaw, pitch: aim.pitch, fov: camera.fov }
  }

  function lookThroughSample(): CameraPathSample | null {
    const shown = shownCameraPath()
    return sampleAt(Math.min(pathTimeMs, cameraPathDurationMs(shown))) ?? placedCameraSample()
  }

  function pathIcon(): { pos: PathVec3; dir: PathVec3 } | null {
    const placed = cameraPlaceShownPose()
    if (placed) return { pos: placed.pos, dir: directionFromYawPitch(placed.yaw, placed.pitch) }
    if (pathLookThrough || deviceLookActive) return null
    const shown = shownCameraPath()
    const points = shown.authoring?.points
    const look = shown.authoring?.look
    const recorded = look?.mode === "recorded"
    if ((playing || pathTimeMs > 0 || recorded) && shown.samples.length > 0) {
      const sample = sampleAt(Math.min(pathTimeMs, cameraPathDurationMs(shown)))
      if (!sample) return null
      return { pos: sample.pos, dir: directionFromYawPitch(sample.yaw, sample.pitch) }
    }
    if (points && points.length > 0) {
      const pos = points[0].pos
      const next = points[1]?.pos
      const along: PathVec3 = next ? [next[0] - pos[0], next[1] - pos[1], next[2] - pos[2]] : [0, 0, -1]
      const tangent = Math.hypot(along[0], along[1], along[2]) < 1e-4 ? ([0, 0, -1] as PathVec3) : along
      const authoring = shown.authoring
      const idleLook = authoring?.look ?? { mode: "tangent" as const }
      const center = idleLook.mode === "observe" ? objectCenter(idleLook.object_uid) : null
      const yp =
        authoring && idleLook.mode === "tangent"
          ? splineSampleLook(authoring, pos, tangent)
          : lookYawPitch(pos, tangent, idleLook, center)
      return { pos, dir: directionFromYawPitch(yp.yaw, yp.pitch) }
    }
    if (shown.samples.length === 0) return null
    const sample = sampleAt(0)
    if (!sample) return null
    return { pos: sample.pos, dir: directionFromYawPitch(sample.yaw, sample.pitch) }
  }

  function pathOverlayChrome(): number {
    const platform = platformDropState?.platform
    if (!platform || platform.unit !== "cm") return 1
    const span = platformLayer.span()
    if (!(span > 0)) return 1
    return Math.min(1, span / CAMERA_REFERENCE_SPAN)
  }

  function pathFitEpsilon(): number {
    return RECORDED_FIT_EPSILON_M * pathOverlayChrome()
  }

  function overlayCameraMoved(): boolean {
    const x = camera.position.x
    const y = camera.position.y
    const z = camera.position.z
    const dx = x - overlaySyncX
    const dy = y - overlaySyncY
    const dz = z - overlaySyncZ
    if (dx * dx + dy * dy + dz * dz < 1e-6) return false
    overlaySyncX = x
    overlaySyncY = y
    overlaySyncZ = z
    return true
  }

  function overlayPath(): CameraPath {
    const uid = focusedPathUid()
    if (!uid) return shownCameraPath()
    const path = objectPathRecord(uid)
    if (!path) return emptyCameraPath()
    return objectPathAsCameraPath(path)
  }

  function ghostPolylines(): PathVec3[][] {
    const ghosts: PathVec3[][] = []
    const focusUid = focusedPathUid()
    if (focusUid) {
      const camera = shownCameraPath()
      if (camera.authoring && camera.authoring.points.length >= 2) {
        ghosts.push(curvePolyline(camera.authoring).map((sample) => sample.pos))
      } else if (camera.samples.length >= 2) {
        ghosts.push(camera.samples.map((sample) => sample.pos))
      }
    }
    meshes.forEach((mesh, uid) => {
      if (uid === focusUid) return
      const path = (mesh.userData as SceneObjectState).path
      if (!path || !objectPathHasContent(path)) return
      const line = objectPathPolyline(path)
      if (line.length >= 2) ghosts.push(line)
    })
    return ghosts
  }

  function cameraPlaceOverlayPath(path: CameraPath): CameraPath {
    const preview = cameraPlacePreview
    if (!preview) return path
    const existing = path.authoring?.points ?? []
    const placed = { pos: [preview.pos[0], preview.pos[1], preview.pos[2]] as PathVec3, tangent: null }
    const points = existing.length === 0 ? [placed] : existing.map((point, index) => (index === 0 ? placed : point))
    const authoring = path.authoring
    return {
      ...path,
      authoring: {
        smooth: authoring?.smooth ?? true,
        look: authoring?.look ?? { mode: "tangent" },
        points,
      },
    }
  }

  function syncPathOverlay() {
    if (!pathOverlayMounted) return
    const editingObject = pathTargetsObject()
    const previewing = pathPreviewing()
    const path = cameraPlaceOverlayPath(overlayPath())
    const floors = cameraPlacePreview
      ? (path.authoring?.points ?? []).map((point) => floorTopAt(point.pos[0], point.pos[2]))
      : pathFloors()
    pathOverlay.sync({
      path,
      editing: pathEditActive() && !playing && !exporting && !capturingStill,
      solid: previewing && !pathEditActive(),
      selectedIndex: pathEditActive() ? selectedPathPoint : null,
      hidden: exporting || capturingStill || recording || pathLookThrough,
      theme: environmentState.theme,
      cameraPosition: [camera.position.x, camera.position.y, camera.position.z],
      icon: editingObject || previewing ? null : pathIcon(),
      floors,
      fov: camera.fov,
      surfaceY: platformSurfaceY,
      chrome: pathOverlayChrome(),
      insertHover: pathEditActive() ? pathInsertHover : null,
      ghosts: ghostPolylines(),
    })
    syncCameraPlaceColor()
    syncCameraPlaceMarkersVisible()
  }

  function commitObjectAuthoring(points: PathPoint[]) {
    const uid = objectPathUid
    const mesh = uid ? meshes.get(uid) : undefined
    if (!mesh) return
    const data = mesh.userData as SceneObjectState
    if (points.length === 0) {
      delete data.path
    } else if (points.length === 1) {
      data.path = {
        version: 1,
        duration_ms: pathDurationMs,
        samples: [],
        authoring: {
          smooth: true,
          points: points.map((point) => ({
            pos: [...point.pos] as [number, number, number],
            tangent: point.tangent ? ([...point.tangent] as [number, number, number]) : null,
          })),
        },
      }
      data.position = [...points[0].pos] as [number, number, number]
      if (!objectPosesApplied) mesh.position.set(points[0].pos[0], points[0].pos[1], points[0].pos[2])
    } else {
      const duration = clampSplineDuration(
        pathDurationMs >= MIN_SPLINE_DURATION_MS
          ? pathDurationMs
          : defaultObjectPathDuration(data.path, cameraPathDurationMs(shownCameraPath())),
      )
      pathDurationMs = duration
      const previous = data.path?.speed
      const fromMs = data.path && data.path.duration_ms > 0 ? data.path.duration_ms : duration
      const speed = previous ? scaleSpeedProfile(previous, fromMs, duration) : null
      const baked = bakeObjectPath(points, duration, speed)
      data.path = baked
      const start = objectPathStart(baked)
      if (start) {
        data.position = [...start] as [number, number, number]
        if (!objectPosesApplied) mesh.position.set(start[0], start[1], start[2])
      }
    }
    emitObjects()
    emitPathEditUi()
    syncPathOverlay()
  }

  function writeObjectPath(uid: string, durationMs: number, speed: CameraSpeedProfile | null) {
    const mesh = meshes.get(uid)
    if (!mesh) return
    const data = mesh.userData as SceneObjectState
    const points = data.path?.authoring?.points
    if (!points || points.length < 2) return
    const duration = clampSplineDuration(durationMs)
    const baked = bakeObjectPath(points, duration, speed)
    data.path = baked
    const start = objectPathStart(baked)
    if (start) {
      data.position = [...start] as [number, number, number]
      if (!objectPosesApplied) mesh.position.set(start[0], start[1], start[2])
    }
    if (objectPathUid === uid) pathDurationMs = duration
    emitObjects()
    emitPathEditUi()
    syncPathOverlay()
  }

  function commitAuthoring(points: PathPoint[], nextSpeed?: CameraSpeedProfile, placedAim?: PlacedAim | null) {
    if (pathTargetsObject()) {
      commitObjectAuthoring(points)
      return
    }
    const previousKeys = cameraPath.look_keys
    const previousDuration = cameraPath.duration_ms
    const previousSpeed = cameraPath.speed
    const keptAim = placedAim === undefined ? cameraPath.authoring?.placed_aim : placedAim
    if (points.length === 0) {
      cameraPath = emptyCameraPath()
      selectedPathPoint = null
    } else {
      const nextDuration = clampSplineDuration(pathDurationMs)
      const fromMs = previousDuration > 0 ? previousDuration : nextDuration
      const speedSource = nextSpeed ?? previousSpeed
      const speed = speedSource ? scaleSpeedProfile(speedSource, fromMs, nextDuration) : null
      const look = scaleLookTargets(storedLook(), fromMs, nextDuration)
      pathLook = look
      const baked = bakeSplinePath(
        {
          smooth: pathSmooth,
          look,
          points,
          ...(keptAim ? { placed_aim: { yaw: keptAim.yaw, pitch: keptAim.pitch } } : {}),
        },
        { durationMs: nextDuration, fov: camera.fov, speed },
      )
      const clampedSamples = baked.samples.map((sample) => ({ ...sample, pos: clampAbovePlatform(sample.pos) }))
      const aimed = aimSamplesAtLook(clampedSamples, look, baked.duration_ms, speed, (uid) => objectCenter(uid))
      if (aimed) {
        cameraPath = {
          ...baked,
          samples: aimed.samples,
          look_keys: aimed.keys,
          authoring: baked.authoring ? { ...baked.authoring, look } : baked.authoring,
        }
      } else {
        const keys =
          previousKeys && previousKeys.length > 0
            ? remapStoredLookKeys(previousKeys, previousSpeed, previousDuration, speed, baked.duration_ms)
            : undefined
        cameraPath = attachLookKeys({ ...baked, samples: clampedSamples }, keys)
      }
    }
    onCameraPathChange?.(cameraPath)
    emitPathEditUi()
    syncPathOverlay()
  }

  function setCameraSpeed(profile: CameraSpeedProfile) {
    const points = currentPoints()
    if (points.length >= 2) {
      commitAuthoring(points, profile)
      return
    }
    if (cameraPath.samples.length < 2) return
    cameraPath = retimeCameraPath(cameraPath, profile)
    onCameraPathChange?.(cameraPath)
    emitPathEditUi()
    syncPathOverlay()
  }

  function adoptPathSettings() {
    if (cameraPath.authoring) {
      pathSmooth = cameraPath.authoring.smooth
      pathLook = cameraPath.authoring.look
      if (pathLook.mode === "observe" && pathLook.object_uid && !meshes.has(pathLook.object_uid)) {
        pathLook = { mode: "observe", object_uid: "" }
        cameraPath = { ...cameraPath, authoring: { ...cameraPath.authoring, look: pathLook } }
      }
      if (cameraPath.duration_ms >= MIN_SPLINE_DURATION_MS) pathDurationMs = cameraPath.duration_ms
    }
    const count = cameraPath.authoring?.points.length ?? 0
    if (selectedPathPoint != null && (selectedPathPoint < 0 || selectedPathPoint >= count)) {
      selectedPathPoint = count > 0 ? count - 1 : null
    }
    emitPathEditUi()
    syncPathOverlay()
  }

  function previewPoints(points: PathPoint[]) {
    if (pathTargetsObject()) {
      const mesh = meshes.get(objectPathUid!)
      if (!mesh) return
      const data = mesh.userData as SceneObjectState
      const prev = data.path
      data.path = {
        version: prev?.version ?? 1,
        duration_ms: prev?.duration_ms ?? pathDurationMs,
        samples: prev?.samples ?? [],
        authoring: { smooth: true, points },
        ...(prev?.speed ? { speed: prev.speed } : {}),
      }
      const start = points[0]?.pos
      if (start && !objectPosesApplied) {
        data.position = [...start] as [number, number, number]
        mesh.position.set(start[0], start[1], start[2])
      }
      syncPathOverlay()
      return
    }
    cameraPath = {
      ...cameraPath,
      authoring: {
        smooth: pathSmooth,
        look: storedLook(),
        points,
        ...(cameraPath.authoring?.placed_aim
          ? {
              placed_aim: {
                yaw: cameraPath.authoring.placed_aim.yaw,
                pitch: cameraPath.authoring.placed_aim.pitch,
              },
            }
          : {}),
      },
    }
    syncPathOverlay()
  }

  function cameraPlaceRadius(): number {
    return CAMERA_PLACE_DOT_RADIUS * pathOverlayChrome()
  }

  function cameraPlaceFloorKey(): string {
    const platform = platformDropState?.platform
    if (!platform) return ""
    const raised = (platformDropState?.cells ?? [])
      .filter((cell) => cell.height > 0)
      .map((cell) => `${cell.x}:${cell.z}:${cell.height}`)
      .join(",")
    return `${platform.sectors_x}:${platform.sectors_z}:${platform.sector_size}:${platform.unit}:${raised}`
  }

  function disposeCameraPlaceMesh() {
    if (!cameraPlaceMesh) return
    scene.remove(cameraPlaceMesh)
    cameraPlaceMesh.geometry.dispose()
    const material = cameraPlaceMesh.material
    if (material instanceof THREE.Material) material.dispose()
    cameraPlaceMesh = null
    cameraPlaceOpacity = null
  }

  function cameraPlaceIndexForFirstPoint(): number {
    const point = cameraPath.authoring?.points[0]
    if (!point) return -1
    return cameraPlaceMarkersState.findIndex(
      (marker) => Math.abs(marker.pos[0] - point.pos[0]) < 1e-3 && Math.abs(marker.pos[2] - point.pos[2]) < 1e-3,
    )
  }

  function cameraPlaceDotIsSolid(index: number): boolean {
    return index === cameraPlaceHover || index === cameraPlaceSelected
  }

  function writeCameraPlaceMatrices() {
    const mesh = cameraPlaceMesh
    if (!mesh) return
    const radius = cameraPlaceRadius()
    for (let i = 0; i < cameraPlaceMarkersState.length; i += 1) {
      const pos = cameraPlaceMarkersState[i]?.pos
      if (!pos) continue
      const solid = cameraPlaceDotIsSolid(i)
      const scale = radius * (solid ? CAMERA_PLACE_HOVER_SCALE : 1)
      cameraPlaceDummy.position.set(pos[0], pos[1], pos[2])
      cameraPlaceDummy.scale.setScalar(scale)
      cameraPlaceDummy.rotation.set(0, 0, 0)
      cameraPlaceDummy.updateMatrix()
      mesh.setMatrixAt(i, cameraPlaceDummy.matrix)
      cameraPlaceOpacity?.setX(i, solid ? 1 : CAMERA_PLACE_DOT_OPACITY)
    }
    mesh.instanceMatrix.needsUpdate = true
    if (cameraPlaceOpacity) cameraPlaceOpacity.needsUpdate = true
  }

  function syncCameraPlaceColor() {
    const material = cameraPlaceMesh?.material
    if (!(material instanceof THREE.MeshBasicMaterial)) return
    material.color.set(pathCurveHex(environmentState.theme))
  }

  function syncCameraPlaceMarkersVisible() {
    if (!cameraPlaceMesh) return
    cameraPlaceMesh.visible = !pathLookThrough
  }

  function hideCameraPlaceMarkers() {
    const hadPreview = cameraPlacePreview != null
    cameraPlaceHover = -1
    cameraPlaceSelected = -1
    cameraPlacePending = null
    cameraPlaceDrag = null
    cameraPlacePreview = null
    cameraPlaceKey = ""
    cameraPlaceMarkersState = []
    disposeCameraPlaceMesh()
    if (hadPreview) {
      onCameraPlacePreview?.(false)
      syncPathOverlay()
    }
  }

  function rebuildCameraPlaceMarkers() {
    const platform = platformDropState?.platform
    const cells = platformDropState?.cells ?? []
    const key = cameraPlaceFloorKey()
    const markers = cameraPlaceMarkers(platform, cells, CAMERA_PLACE_DOT_LIFT * pathOverlayChrome())
    if (key === cameraPlaceKey && cameraPlaceMesh && cameraPlaceMesh.count === markers.length) {
      syncCameraPlaceColor()
      syncCameraPlaceMarkersVisible()
      return
    }
    cameraPlaceKey = key
    cameraPlaceMarkersState = markers
    cameraPlaceHover = -1
    cameraPlaceSelected = cameraPlaceIndexForFirstPoint()
    disposeCameraPlaceMesh()
    if (!cameraPlaceMode || markers.length === 0) return
    const geometry = new THREE.SphereGeometry(1, 10, 8)
    cameraPlaceOpacity = new THREE.InstancedBufferAttribute(new Float32Array(markers.length), 1)
    geometry.setAttribute("instanceOpacity", cameraPlaceOpacity)
    const material = new THREE.MeshBasicMaterial({
      color: pathCurveHex(environmentState.theme),
      transparent: true,
      opacity: 1,
      depthWrite: true,
      toneMapped: false,
    })
    material.customProgramCacheKey = () => "camera-place-dot"
    material.onBeforeCompile = (shader) => {
      shader.vertexShader = shader.vertexShader
        .replace(
          "#include <common>",
          "#include <common>\nattribute float instanceOpacity;\nvarying float vInstanceOpacity;",
        )
        .replace("#include <begin_vertex>", "#include <begin_vertex>\nvInstanceOpacity = instanceOpacity;")
      shader.fragmentShader = shader.fragmentShader
        .replace("#include <common>", "#include <common>\nvarying float vInstanceOpacity;")
        .replace("#include <dithering_fragment>", "gl_FragColor.a *= vInstanceOpacity;\n#include <dithering_fragment>")
    }
    const mesh = new THREE.InstancedMesh(geometry, material, markers.length)
    mesh.count = markers.length
    mesh.frustumCulled = false
    mesh.userData.ignorePick = true
    cameraPlaceMesh = mesh
    scene.add(mesh)
    writeCameraPlaceMatrices()
    syncCameraPlaceMarkersVisible()
  }

  function cameraPlaceSectorFromMesh(hit: PlatformHit | null): { x: number; z: number } | null {
    if (!hit) return null
    if (hit.kind === "sector") return { x: hit.x, z: hit.z }
    if (hit.kind !== "opening" && hit.kind !== "face") return null
    const parsed = parseCellFaceKey(hit.faceKey)
    if (!parsed || parsed.face === "top" || parsed.face === "bottom") return null
    const height =
      (platformDropState?.cells ?? []).find((cell) => cell.x === parsed.x && cell.z === parsed.z)?.height ?? 0
    if (height <= 0) return null
    return { x: parsed.x, z: parsed.z }
  }

  function cameraPlaceIndexUnderCursor(clientX: number, clientY: number, meshHit?: PlatformHit | null): number | null {
    const platform = platformDropState?.platform
    if (!cameraPlaceMode || !platform || cameraPlaceMarkersState.length === 0) return null
    const cells = platformDropState?.cells ?? []
    const mesh = meshHit === undefined ? platformLayer.hitAt(clientX, clientY) : meshHit
    const column = cameraPlaceSectorFromMesh(mesh)
    if (column) return cameraPlaceMarkerIndex(cameraPlaceMarkersState, column.x, column.z)
    const ray = raycaster.ray
    const hit = rayHorizontalPlaneHit(
      [ray.origin.x, ray.origin.y, ray.origin.z],
      [ray.direction.x, ray.direction.y, ray.direction.z],
      0,
    )
    if (!hit) return null
    return cameraPlaceHitIndex(platform, cells, cameraPlaceMarkersState, hit.x, hit.z)
  }

  function updateCameraPlaceHover(e: PointerEvent) {
    if (!cameraPlaceMode || draggingLook) {
      if (cameraPlaceHover !== -1 && cameraPlacePending == null) {
        cameraPlaceHover = -1
        writeCameraPlaceMatrices()
      }
      return
    }
    if (cameraPlacePending != null) return
    setPointerRay(e)
    const index = cameraPlaceIndexUnderCursor(e.clientX, e.clientY) ?? -1
    if (index === cameraPlaceHover) return
    cameraPlaceHover = index
    writeCameraPlaceMatrices()
  }

  function clearCameraPlaceSelection() {
    cameraPlaceHover = -1
    cameraPlaceSelected = -1
    writeCameraPlaceMatrices()
  }

  function beginCameraPlaceDrag(index: number, e: PointerEvent) {
    const view = yawPitchFromCamera(camera)
    const yaw = cameraPlaceAim?.yaw ?? view.yaw
    const pitch = cameraPlaceAim?.pitch ?? view.pitch
    const marker = cameraPlaceMarkersState[index]
    const previous = cameraPath.authoring?.points[0]
    const previousY = previous?.pos[1] ?? pathDrawFallbackY()
    const previousSurfaceY = previous ? (floorTopAt(previous.pos[0], previous.pos[2]) ?? 0) : 0
    const y = cameraPlaceEyeY(previousY, previousSurfaceY, marker?.surfaceY ?? 0)
    cameraPlaceDrag = {
      index,
      yaw0: yaw,
      pitch0: pitch,
      y0: y,
      x0: e.clientX,
      yClient0: e.clientY,
      shift: e.shiftKey,
    }
    cameraPlacePending = index
    cameraPlaceHover = index
    cameraPlaceSelected = index
    writeCameraPlaceMatrices()
    showCameraPlacePreview(index, yaw, pitch, y)
  }

  function applyCameraPlacePointerUp(effect: PlatformUpEffect, e: PointerEvent | null) {
    if (effect.type === "cameraPlaceCancel") {
      cameraPlaceDrag = null
      cameraPlacePending = null
      clearCameraPlacePreview()
      clearCameraPlaceSelection()
      return
    }
    if (effect.type === "clearObjects") {
      cameraPlaceDrag = null
      cameraPlacePending = null
      if (e) deselectObjectIfEmptyClick(e, false)
      return
    }
    if (effect.type !== "cameraPlace") return
    const drag = cameraPlaceDrag
    cameraPlaceDrag = null
    cameraPlacePending = null
    if (!drag || !e) {
      clearCameraPlacePreview()
      clearCameraPlaceSelection()
      return
    }
    const moved = Math.hypot(e.clientX - pointerDownX, e.clientY - pointerDownY) > OBJECT_DRAG_CLICK_PX
    const pose = moved ? cameraPlacePoseFromDrag(drag, e) : { yaw: drag.yaw0, pitch: drag.pitch0, y: drag.y0 }
    const placed = placeCameraOnMarker(drag.index, pose, { yaw: drag.yaw0, pitch: drag.pitch0 })
    if (!placed) clearCameraPlaceSelection()
    else if (effect.clearObjects) deselectObjectIfEmptyClick(e, false)
  }

  function showCameraPlacePreview(index: number, yaw: number, pitch: number, y: number) {
    const marker = cameraPlaceMarkersState[index]
    if (!marker) return
    const started = cameraPlacePreview == null
    const pos = clampAbovePlatform([marker.pos[0], y, marker.pos[2]])
    cameraPlacePreview = { pos, yaw, pitch }
    if (started) onCameraPlacePreview?.(true)
    syncPathOverlay()
  }

  function cameraPlacePoseFromDrag(
    drag: NonNullable<typeof cameraPlaceDrag>,
    e: PointerEvent,
  ): { yaw: number; pitch: number; y: number } {
    const dx = e.clientX - drag.x0
    const dy = e.clientY - drag.yClient0
    const aim = cameraPlaceAimFromDrag({ yaw: drag.yaw0, pitch: drag.pitch0 }, dx, drag.shift ? 0 : dy, PITCH_MAX)
    const marker = cameraPlaceMarkersState[drag.index]
    let y = drag.y0
    if (drag.shift && marker) {
      const meters = lightDragMetersPerPx(platformLayer.span())
      y = drag.y0 + (drag.yClient0 - e.clientY) * meters
      y = clampAbovePlatform([marker.pos[0], y, marker.pos[2]])[1]
    }
    return { yaw: aim.yaw, pitch: aim.pitch, y }
  }

  function anchorCameraPlaceDrag(e: PointerEvent) {
    const drag = cameraPlaceDrag
    if (!drag) return
    const pose = cameraPlacePoseFromDrag(drag, e)
    cameraPlaceDrag = {
      index: drag.index,
      yaw0: pose.yaw,
      pitch0: pose.pitch,
      y0: pose.y,
      x0: e.clientX,
      yClient0: e.clientY,
      shift: e.shiftKey,
    }
  }

  function updateCameraPlaceDrag(e: PointerEvent) {
    const drag = cameraPlaceDrag
    if (!drag) return
    if (e.shiftKey !== drag.shift) anchorCameraPlaceDrag(e)
    const current = cameraPlaceDrag
    if (!current) return
    if (Math.hypot(e.clientX - pointerDownX, e.clientY - pointerDownY) <= OBJECT_DRAG_CLICK_PX) return
    const pose = cameraPlacePoseFromDrag(current, e)
    showCameraPlacePreview(current.index, pose.yaw, pose.pitch, pose.y)
  }

  function clearCameraPlacePreview() {
    if (!cameraPlacePreview) return
    cameraPlacePreview = null
    onCameraPlacePreview?.(false)
    syncPathOverlay()
  }

  function placeCameraOnMarker(
    index: number,
    aim: { yaw: number; pitch: number; y: number },
    pressed: { yaw: number; pitch: number },
  ): boolean {
    const marker = cameraPlaceMarkersState[index]
    if (!marker || !canEdit) {
      clearCameraPlacePreview()
      return false
    }
    if (!activeCameraPathTakeId) {
      if (cameraPathTakesFull(cameraPathTakes)) {
        onCameraPathTakesFull?.()
        clearCameraPlacePreview()
        return false
      }
      const takeId = addCameraPathTake(cameraPathTakeName(nextCameraPathTakeNumber(cameraPathTakes)))
      if (!takeId) {
        onCameraPathTakesFull?.()
        clearCameraPlacePreview()
        return false
      }
    }
    const pose = cameraPoseAtPlace(marker, aim.y, { yaw: aim.yaw, pitch: aim.pitch, fov: camera.fov })
    cameraPlaceAim = { yaw: aim.yaw, pitch: aim.pitch }
    const hadPreview = cameraPlacePreview != null
    cameraPlacePreview = null
    if (hadPreview) onCameraPlacePreview?.(false)
    onCameraPlaced?.(pose)
    const nextAim = { yaw: aim.yaw, pitch: aim.pitch }
    const previous = cameraPath.authoring?.placed_aim
    const changed = cameraPlaceAimChanged(pressed, nextAim)
    const sameAsSaved = previous != null && !cameraPlaceAimChanged(previous, nextAim)
    let placedAim: { yaw: number; pitch: number } | null | undefined
    if (changed) placedAim = nextAim
    else if (sameAsSaved) placedAim = undefined
    else placedAim = null
    commitAuthoring(replaceFirstPathPoint(currentPoints(), pose.pos), undefined, placedAim)
    return true
  }

  function setCameraPlaceAim(aim: { yaw: number; pitch: number } | null) {
    cameraPlaceAim = aim
    syncPathOverlay()
  }

  function setCameraPlaceMode(active: boolean): boolean {
    if (active === cameraPlaceMode) return true
    if (active) {
      if (!canEdit || !pathEditAllowed || deviceLookActive || recording || recordArmed || exporting || playing) {
        return false
      }
      if (trajectoryMode) setTrajectoryMode(false)
      finishObjectDrag()
      finishLightDrag()
      objectDragPendingUid = null
      lightDragPending = false
      platformPending = null
      platformFacePending = null
      platformPaint = null
      platformConsumed = false
      clearPlatformPaintPreviews()
      clearAllSelection()
      detachTransform()
      cameraPlaceMode = true
      rebuildCameraPlaceMarkers()
      onCameraPlaceModeChange?.(true)
      return true
    }
    cameraPlaceMode = false
    hideCameraPlaceMarkers()
    onCameraPlaceModeChange?.(false)
    return true
  }

  function setPathEditAllowed(allowed: boolean) {
    pathEditAllowed = allowed
    if (!allowed && trajectoryMode) setTrajectoryMode(false)
    if (!allowed && cameraPlaceMode) setCameraPlaceMode(false)
  }

  function setTrajectoryMode(active: boolean) {
    if (active) {
      if (!pathEditAllowed || !canEdit || deviceLookActive || recording || recordArmed || exporting) return
      if (cameraPlaceMode) setCameraPlaceMode(false)
      previewObjectPathUid = null
      objectPathUid = null
      trajectoryMode = true
      if (cameraPath.authoring) {
        pathSmooth = cameraPath.authoring.smooth
        pathLook = cameraPath.authoring.look
        if (cameraPath.duration_ms >= MIN_SPLINE_DURATION_MS) pathDurationMs = cameraPath.duration_ms
      }
      pathDraw = trajectoryDrawWithSeed(cameraPath.authoring?.points.length ?? 0, cameraPath.samples.length)
      pathObserveArmed = false
      clearObserveHover()
      selectedPathPoint = null
      clearAllSelection()
    } else {
      trajectoryMode = false
      if (lookRecording) finishLookRecording()
      pathObserveArmed = false
      if (isPathPending(platformPending)) platformPending = null
      pathPointDragIndex = null
      selectedPathPoint = null
      pathInsertHover = null
      clearObserveHover()
      lookRewriteArmed = false
      lookRewriteDown = false
    }
    syncRecordGizmo()
    emitPathEditUi()
    syncPathOverlay()
  }

  function currentPoints(): PathPoint[] {
    const source = pathTargetsObject()
      ? objectPathRecord(objectPathUid!)?.authoring?.points
      : cameraPath.authoring?.points
    return (source ?? []).map((point) => ({
      pos: [...point.pos] as PathVec3,
      tangent: point.tangent ? ([...point.tangent] as PathVec3) : null,
    }))
  }

  function addPathPoint(pos: PathVec3): boolean {
    const points = currentPoints()
    if (points.length >= MAX_PATH_POINTS) return false
    points.push({ pos, tangent: null })
    selectedPathPoint = points.length - 1
    commitAuthoring(points)
    return true
  }

  function floorTopAt(worldX: number, worldZ: number): number | null {
    const state = platformDropState
    const platform = state?.platform
    if (!platform || !platformIsSet(platform)) return null
    const sector = worldToSectorIndex(platform, worldX, worldZ)
    if (!sector) return null
    const height = state.cells.find((entry) => entry.x === sector.x && entry.z === sector.z)?.height ?? 0
    if (height <= 0) return 0
    return height * sectorSizeMeters(platform)
  }

  function pathFloors(): Array<number | null> {
    return currentPoints().map((point) => floorTopAt(point.pos[0], point.pos[2]))
  }

  function pathDrawFallbackY(): number {
    const span = platformLayer.span()
    if (!(span > 0)) return 5
    return (5 * span) / CAMERA_REFERENCE_SPAN
  }

  function overPlatform(x: number, z: number): boolean {
    const platform = platformDropState?.platform
    if (!platform || !platformIsSet(platform)) return true
    return worldToSectorIndex(platform, x, z) != null
  }

  function drawHitFromRay(): PathVec3 | null {
    const ray = raycaster.ray
    return drawPointFromClick(
      currentPoints(),
      [ray.origin.x, ray.origin.y, ray.origin.z],
      [ray.direction.x, ray.direction.y, ray.direction.z],
      pathDrawFallbackY(),
    )
  }

  function clampAbovePlatform(pos: PathVec3): PathVec3 {
    const minY = platformSurfaceY(pos[0], pos[2])
    if (pos[1] >= minY) return pos
    return [pos[0], minY, pos[2]]
  }

  function addPointFromRay(): boolean {
    const points = currentPoints()
    if (points.length >= MAX_PATH_POINTS) return false
    if (pathTargetsObject()) return addObjectPointFromRay()
    const pos = drawHitFromRay()
    if (!pos || !overPlatform(pos[0], pos[2])) return false
    return addPathPoint(clampAbovePlatform(pos))
  }

  function addObjectPointFromRay(): boolean {
    const uid = objectPathUid
    const mesh = uid ? meshes.get(uid) : undefined
    if (!mesh) return false
    const ray = raycaster.ray
    const hit = rayHorizontalPlaneHit(
      [ray.origin.x, ray.origin.y, ray.origin.z],
      [ray.direction.x, ray.direction.y, ray.direction.z],
      0,
    )
    if (!hit || !overPlatform(hit.x, hit.z)) return false
    const data = mesh.userData as SceneObjectState
    const sizeY = data.size?.[1] ?? 1
    const y = platformSurfaceY(hit.x, hit.z) + (sizeY * Math.abs(mesh.scale.y || 1)) / 2
    return addPathPoint(clampAbovePlatform([hit.x, y, hit.z]))
  }

  function closestCurveHit(): { segment: number; pos: PathVec3 } | null {
    const points = currentPoints()
    const authoring = pathTargetsObject()
      ? points.length >= 2
        ? { smooth: true, look: { mode: "tangent" as const }, points }
        : null
      : cameraPath.authoring
    if (!authoring || authoring.points.length < 2) return null
    const ray = raycaster.ray
    const closest = closestOnCurve(
      authoring,
      [ray.origin.x, ray.origin.y, ray.origin.z],
      [ray.direction.x, ray.direction.y, ray.direction.z],
    )
    if (!closest) return null
    const camDist = camera.position.distanceTo(new THREE.Vector3(closest.pos[0], closest.pos[1], closest.pos[2]))
    if (closest.rayDistance > Math.max(0.4 * pathOverlayChrome(), camDist * 0.04)) return null
    return closest
  }

  function insertAtRay() {
    const closest = closestCurveHit()
    if (!closest) return
    const points = currentPoints()
    if (points.length < 2) return
    const next = insertPoint(points, closest.segment, clampAbovePlatform(closest.pos))
    if (!next) return
    selectedPathPoint = closest.segment + 1
    commitAuthoring(next)
  }

  function movePathAxis(index: number, axis: "x" | "y" | "z") {
    const points = currentPoints()
    const point = points[index]
    if (!point) return
    const dir: PathVec3 = axis === "x" ? [1, 0, 0] : axis === "y" ? [0, 1, 0] : [0, 0, 1]
    const ray = raycaster.ray
    camera.getWorldDirection(fwd)
    const delta = axisDragOffset(
      [ray.origin.x, ray.origin.y, ray.origin.z],
      [ray.direction.x, ray.direction.y, ray.direction.z],
      point.pos,
      dir,
      [fwd.x, fwd.y, fwd.z],
    )
    if (delta == null) return
    const pos: PathVec3 = [point.pos[0], point.pos[1], point.pos[2]]
    if (axis === "x") pos[0] += delta
    else if (axis === "y") pos[1] += delta
    else pos[2] += delta
    points[index] = { ...point, pos: clampAbovePlatform(pos) }
    previewPoints(points)
  }

  function anchorPathPointDrag(e: PointerEvent, index: number) {
    const point = currentPoints()[index]
    if (!point) return
    pathPointDragIndex = index
    pathPointDragShift = e.shiftKey
    pathPointDragStartClientY = e.clientY
    pathPointDragStartPos.set(point.pos[0], point.pos[1], point.pos[2])
  }

  /** Курсор по сцене: XZ на высоте начала жеста. */
  function movePathPointOnScene(index: number) {
    const points = currentPoints()
    const point = points[index]
    if (!point) return
    if (!rayToGroundY(pathHandleHit, pathPointDragStartPos.y, pathPointDragStartPos)) return
    points[index] = {
      ...point,
      pos: clampAbovePlatform([pathHandleHit.x, pathPointDragStartPos.y, pathHandleHit.z]),
    }
    previewPoints(points)
  }

  /** Shift: вверх-вниз курсора меняет только высоту. */
  function movePathPointHeight(index: number, e: PointerEvent) {
    const points = currentPoints()
    const point = points[index]
    if (!point) return
    const drag = lightDragMetersPerPx(platformLayer.span())
    const y = pathPointDragStartPos.y + (pathPointDragStartClientY - e.clientY) * drag
    points[index] = {
      ...point,
      pos: clampAbovePlatform([pathPointDragStartPos.x, y, pathPointDragStartPos.z]),
    }
    previewPoints(points)
  }

  function movePathPoint(index: number, e: PointerEvent) {
    if (pathPointDragIndex !== index || e.shiftKey !== pathPointDragShift) anchorPathPointDrag(e, index)
    if (e.shiftKey) movePathPointHeight(index, e)
    else movePathPointOnScene(index)
  }

  function bendNewPointOnPlane(index: number) {
    const points = currentPoints()
    const point = points[index]
    if (!point) return
    const ray = raycaster.ray
    const hit = rayHorizontalPlaneHit(
      [ray.origin.x, ray.origin.y, ray.origin.z],
      [ray.direction.x, ray.direction.y, ray.direction.z],
      point.pos[1],
    )
    if (!hit) return
    points[index] = {
      ...point,
      tangent: clampTangent([hit.x - point.pos[0], 0, hit.z - point.pos[2]]),
    }
    previewPoints(points)
  }

  function movePathHandle(index: number, side: 1 | -1) {
    const points = currentPoints()
    const point = points[index]
    if (!point) return
    const tangent =
      point.tangent ??
      displayedTangent(
        pathTargetsObject()
          ? { smooth: true, look: { mode: "tangent" }, points }
          : { smooth: pathSmooth, look: pathLook, points },
        index,
      )
    camera.getWorldDirection(fwd)
    const ray = raycaster.ray
    const next = handleDragTangent(
      point.pos,
      tangent,
      side,
      [ray.origin.x, ray.origin.y, ray.origin.z],
      [ray.direction.x, ray.direction.y, ray.direction.z],
      [fwd.x, fwd.y, fwd.z],
    )
    if (!next) return
    points[index] = { ...point, tangent: next }
    previewPoints(points)
  }

  function applyPathDrag(pending: PlatformPending, e: PointerEvent) {
    if (pending.kind === "pathAxis") movePathAxis(pending.index, pending.axis)
    else if (pending.kind === "pathBend") movePathHandle(pending.index, pending.side)
    else if (pending.kind === "pathMovePoint") movePathPoint(pending.index, e)
  }

  function applyPathUp(effect: PlatformUpEffect, e: PointerEvent) {
    if (effect.type === "pathSelect") {
      pathLastClickAt = performance.now()
      pathLastClickIndex = effect.index
      if (selectedPathPoint === effect.index) return
      selectedPathPoint = effect.index
      syncPathOverlay()
      return
    }
    if (effect.type === "pathPlaced") {
      selectedPathPoint = effect.index
      pathLastClickAt = performance.now()
      pathLastClickIndex = effect.index
      syncPathOverlay()
      return
    }
    if (effect.type === "pathCommit") {
      const points = currentPoints()
      if (points.length > 0) commitAuthoring(points)
      return
    }
    if (effect.type === "pathInsert") {
      setPointerRay(e)
      insertAtRay()
      return
    }
    if (effect.type === "pathPick") {
      setPointerRay(e)
      const uid = pickFromEventPosition()
      if (!uid) return
      pathLook = { mode: "observe", object_uid: uid }
      pathObserveArmed = false
      clearObserveHover()
      const points = currentPoints()
      if (points.length > 0) commitAuthoring(points)
      else emitPathEditUi()
      return
    }
  }

  /** Shift counts as camera motion, but a path control still owns the click. */
  function shiftGrabsTrajectoryControl(e: PointerEvent): boolean {
    if (!pathEditActive() || !e.shiftKey || playing || transformDragging || lookRewriteArmed) return false
    const classified = classifyTrajectoryPointer(e)
    if (classified.pathDown?.type === "resetTangent") return true
    const kind = classified.pending?.kind
    return kind === "pathPoint" || kind === "pathAxis" || kind === "pathBend"
  }

  function classifyTrajectoryPointer(e: PointerEvent) {
    setPointerRay(e)
    const hit = pathOverlay.hit(raycaster)
    const now = performance.now()
    const doubleClick =
      (hit?.kind === "point" || hit?.kind === "handle") &&
      hit.index === pathLastClickIndex &&
      now - pathLastClickAt < 320
    const placed = drawHitFromRay()
    const floorHit = placed != null && overPlatform(placed[0], placed[2])
    return classifyPlatformDown({
      button: e.button,
      shift: e.shiftKey,
      canEdit,
      playing,
      deviceLookActive,
      recordArmed,
      recording,
      cameraMoving: cameraIsMoving(),
      hasSectorSelection: false,
      hasFaceSelection: false,
      meshHit: "miss",
      cell: null,
      trajectory: {
        active: true,
        draw: pathDraw,
        alt: e.altKey,
        observeArmed: pathObserveArmed,
        doubleClick,
        floorHit,
        onCurve: closestCurveHit() != null,
        hit,
      },
    })
  }

  function pickFromEventPosition(): string | null {
    const hits = raycaster.intersectObjects(objectsRoot.children, true)
    for (const hit of hits) {
      let node: THREE.Object3D | null = hit.object
      while (node && node.parent !== objectsRoot) node = node.parent
      if (node && meshes.has(node.userData.uid)) return node.userData.uid as string
    }
    return null
  }

  const onPointerDown = (e: PointerEvent) => {
    pointerDownX = e.clientX
    pointerDownY = e.clientY
    if (pinching || activePointers.size >= 2) return
    if (deviceLookActive) return
    if (inputMode() === "lookThrough") {
      if (!exporting && e.button === 0 && !transformDragging) {
        canvas.setPointerCapture(e.pointerId)
        draggingLook = true
        lastX = e.clientX
        lastY = e.clientY
        canvas.classList.add("dragging")
        canvas.focus()
      }
      return
    }
    if (lookRecording && playing && !exporting && e.button === 0 && !transformDragging) {
      canvas.setPointerCapture(e.pointerId)
      draggingLook = true
      lastX = e.clientX
      lastY = e.clientY
      canvas.classList.add("dragging")
      canvas.focus()
      return
    }
    if (playing && !pathLookThrough && !exporting && e.button === 0 && !transformDragging) {
      canvas.setPointerCapture(e.pointerId)
      draggingLook = true
      lastX = e.clientX
      lastY = e.clientY
      canvas.classList.add("dragging")
      canvas.focus()
      return
    }
    if (cameraPlaceMode && !recording && !recordArmed && !exporting && !playing) {
      if (!(cameraIsMoving() && e.button === 0 && !transformDragging)) {
        if (e.button === 0 && !transformDragging) {
          setPointerRay(e)
          const classified = classifyPlatformFromEvent(e, pickFromEvent(e))
          platformPending = classified.pending
          platformFacePending = null
          platformPaint = null
          platformConsumed = classified.capture
          const pending = classified.pending
          if (pending?.kind === "cameraPlace") {
            beginCameraPlaceDrag(pending.index, e)
            canvas.setPointerCapture(e.pointerId)
            canvas.focus()
          } else if (classified.wantLook && navMode === "fly") {
            canvas.setPointerCapture(e.pointerId)
            draggingLook = true
            lastX = e.clientX
            lastY = e.clientY
            canvas.classList.add("dragging")
            canvas.focus()
          }
        }
        return
      }
    }
    if (e.button === 0 && !transformDragging && cameraIsMoving() && !shiftGrabsTrajectoryControl(e)) {
      platformPending = null
      platformFacePending = null
      platformPaint = null
      platformConsumed = false
      objectDragPendingUid = null
      lightDragPending = false
      if (navMode === "fly") {
        canvas.setPointerCapture(e.pointerId)
        draggingLook = true
        lastX = e.clientX
        lastY = e.clientY
        canvas.classList.add("dragging")
        canvas.focus()
      }
      return
    }
    if (pathEditActive() && e.button === 0) {
      if (lookRewriteArmed) {
        lookRewriteDown = true
        canvas.setPointerCapture(e.pointerId)
        canvas.focus()
        return
      }
      if (playing) return
      if (transformDragging) return
      const classified = classifyTrajectoryPointer(e)
      if (classified.pathDown?.type === "resetTangent") {
        const points = currentPoints()
        if (points.length > 0) commitAuthoring(resetPointTangent(points, classified.pathDown.index))
        pathLastClickAt = 0
        selectedPathPoint = classified.pathDown.index
        canvas.setPointerCapture(e.pointerId)
        return
      }
      let pending = classified.pending
      if (classified.pathDown?.type === "placePoint") {
        const placed = addPointFromRay()
        pending =
          placed && pending?.kind === "pathBendNew" && selectedPathPoint != null
            ? { kind: "pathBendNew", index: selectedPathPoint }
            : { kind: "pathEmpty", alt: false }
      }
      platformPending = pending
      if (pending?.kind === "pathAxis" || pending?.kind === "pathBend" || pending?.kind === "pathPoint") {
        selectedPathPoint = pending.index
        if (pending.kind === "pathPoint") anchorPathPointDrag(e, pending.index)
        syncPathOverlay()
      }
      if (classified.capture) canvas.setPointerCapture(e.pointerId)
      if (pending?.kind === "pathPoint" || pending?.kind === "pathBend") {
        canvas.classList.add("scene-path-over-knot")
      }
      return
    }
    if (pointerLookOnly() && e.button === 0) {
      const classified = classifyPlatformFromEvent(e)
      platformPending = null
      platformFacePending = null
      platformPaint = null
      platformConsumed = false
      objectDragPendingUid = null
      lightDragPending = false
      if (classified.wantLook && navMode === "fly" && !transformDragging) {
        canvas.setPointerCapture(e.pointerId)
        draggingLook = true
        lastX = e.clientX
        lastY = e.clientY
        canvas.classList.add("dragging")
        canvas.focus()
      }
      return
    }
    if (e.button === 0 && pickLightMarker(e)) {
      platformPending = null
      platformFacePending = null
      platformPaint = null
      dismissEnvironmentChrome()
      // Defer light select to pointerup (cross-type: clear-only on short click).
      lightDragPending = true
      canvas.setPointerCapture(e.pointerId)
      return
    }
    // Mousedown elsewhere: drop light immediately so intensity slider hides.
    if (e.button === 0 && lightSelected) setLightSelected(false)
    // Prefer hero/prop/mesh pick over sector paint and platform activate.
    if (e.button === 0 && canEdit && !playing && !deviceLookActive) {
      const uid = pickFromEvent(e)
      if (uid) {
        platformPending = null
        platformFacePending = null
        platformPaint = null
        dismissEnvironmentChrome()
        const dragThis =
          !e.shiftKey &&
          canDirectObjectDrag() &&
          selectedUids.includes(uid) &&
          selectionPopoverAction(selectedUids, selectionObjects()) !== "group"
        if (dragThis) {
          objectDragPendingUid = uid
          if (navMode === "fly" && !transformDragging) {
            canvas.setPointerCapture(e.pointerId)
            releaseDraggingLook()
            canvas.focus()
          }
          return
        }
        objectDragPendingUid = null
        const classified = classifyPlatformFromEvent(e, uid)
        platformPending = classified.pending
        platformConsumed = classified.capture
        if (classified.wantLook && navMode === "fly" && !transformDragging) {
          canvas.setPointerCapture(e.pointerId)
          draggingLook = true
          lastX = e.clientX
          lastY = e.clientY
          canvas.classList.add("dragging")
          canvas.focus()
        }
        return
      }
    }
    if (queueSurfaceFacePending(e)) return
    objectDragPendingUid = null
    // Void + any selection: clear on down and start look (do not wait for pointerup / move).
    // Void + open env chrome: dismiss chrome and look (do not re-open / move chrome).
    if (e.button === 0 && !playing && !deviceLookActive && !transformDragging) {
      const hit = platformLayer.hitAt(e.clientX, e.clientY)
      const meshHit = hit?.kind === "sector" || hit?.kind === "face" || hit?.kind === "opening" ? hit.kind : "miss"
      const cell = hit?.kind === "sector" ? { x: hit.x, z: hit.z } : sectorCellAt(e.clientX, e.clientY)
      const objectWallFaceKey = objectWallAt(e)
      if (isPlatformVoidHit({ meshHit, cell, objectWallFaceKey })) {
        if (currentSelectionKind() != null) {
          platformPending = null
          platformFacePending = null
          platformPaint = null
          platformConsumed = false
          clearAllSelection()
          dismissEnvironmentChrome()
          if (navMode === "fly") {
            canvas.setPointerCapture(e.pointerId)
            draggingLook = true
            lastX = e.clientX
            lastY = e.clientY
            canvas.classList.add("dragging")
            canvas.focus()
          }
          return
        }
        if (environmentChromeOpen) {
          platformPending = null
          platformFacePending = null
          platformPaint = null
          platformConsumed = false
          dismissEnvironmentChrome()
          if (navMode === "fly") {
            canvas.setPointerCapture(e.pointerId)
            draggingLook = true
            lastX = e.clientX
            lastY = e.clientY
            canvas.classList.add("dragging")
            canvas.focus()
          }
          return
        }
      }
    }
    const classified = classifyPlatformFromEvent(e)
    platformPending = classified.pending
    platformConsumed = classified.capture
    if (classified.previewAnchor) {
      platformLayer.previewSectors(new Set([`${classified.previewAnchor.x}:${classified.previewAnchor.z}`]))
    }
    if (classified.capture) {
      canvas.setPointerCapture(e.pointerId)
      return
    }
    if (classified.wantLook && navMode === "fly" && e.button === 0 && !transformDragging) {
      canvas.setPointerCapture(e.pointerId)
      draggingLook = true
      lastX = e.clientX
      lastY = e.clientY
      canvas.classList.add("dragging")
      canvas.focus()
    }
  }
  function hoverInsertSame(a: PathVec3 | null, b: PathVec3 | null): boolean {
    if (!a || !b) return a === b
    return Math.abs(a[0] - b[0]) < 1e-4 && Math.abs(a[1] - b[1]) < 1e-4 && Math.abs(a[2] - b[2]) < 1e-4
  }

  function clearHoverInsert() {
    if (!pathInsertHover) return
    pathInsertHover = null
    syncPathOverlay()
  }

  function pathKnotPointerPending(): boolean {
    const kind = platformPending?.kind
    return kind === "pathPoint" || kind === "pathMovePoint" || kind === "pathBend"
  }

  function onCanvasPointerLeave() {
    if (cameraPlaceHover !== -1) {
      cameraPlaceHover = -1
      writeCameraPlaceMatrices()
    }
    if (!pathKnotPointerPending()) canvas.classList.remove("scene-path-over-knot")
    clearHoverInsert()
    clearObserveHover()
  }

  function updateHoverInsert(e: PointerEvent) {
    if (pathKnotPointerPending()) {
      canvas.classList.add("scene-path-over-knot")
      clearHoverInsert()
      return
    }
    if (
      !pathEditActive() ||
      !pathDraw ||
      draggingLook ||
      playing ||
      exporting ||
      capturingStill ||
      isPathPending(platformPending) ||
      pathObserveArmed ||
      lookRewriteArmed
    ) {
      canvas.classList.remove("scene-path-over-knot")
      clearHoverInsert()
      return
    }
    setPointerRay(e)
    const hit = pathOverlay.hit(raycaster)
    const overKnot = hit?.kind === "point" || hit?.kind === "handle"
    canvas.classList.toggle("scene-path-over-knot", overKnot)
    if (overKnot) {
      clearHoverInsert()
      return
    }
    const closest = closestCurveHit()
    const placed = closest ? null : drawHitFromRay()
    const next = closest?.pos ?? (placed && overPlatform(placed[0], placed[2]) ? clampAbovePlatform(placed) : null)
    if (hoverInsertSame(pathInsertHover, next)) return
    pathInsertHover = next ? [next[0], next[1], next[2]] : null
    syncPathOverlay()
  }

  const onPointerMove = (e: PointerEvent) => {
    if (platformPending?.kind === "cameraPlace") {
      updateCameraPlaceDrag(e)
      return
    }
    updateCameraPlaceHover(e)
    if (platformPending?.kind === "cameraPlaceMiss") {
      const movedPastClick = Math.hypot(e.clientX - pointerDownX, e.clientY - pointerDownY) > OBJECT_DRAG_CLICK_PX
      if (movedPastClick) {
        const effect = resolvePlatformMove({
          pending: platformPending,
          moved: true,
          hasSectorSelection: false,
          currentCell: null,
        })
        platformPending = effect.pending
        if (effect.type === "cameraPlaceLook" && navMode === "fly" && !transformDragging) {
          draggingLook = true
          lastX = e.clientX
          lastY = e.clientY
          canvas.classList.add("dragging")
        }
      }
      return
    }
    if (cameraPlaceMode && !draggingLook) return
    if (lookRewriteDown && !draggingLook) {
      if (Math.hypot(e.clientX - pointerDownX, e.clientY - pointerDownY) > OBJECT_DRAG_CLICK_PX) {
        draggingLook = true
        lastX = e.clientX
        lastY = e.clientY
        canvas.classList.add("dragging")
      } else {
        return
      }
    }
    updateHoverInsert(e)
    syncObserveHover(e)
    if (isPathPending(platformPending)) {
      const pending = platformPending
      if (pending.kind === "pathAxis" || pending.kind === "pathBend" || pending.kind === "pathMovePoint") {
        setPointerRay(e)
        applyPathDrag(pending, e)
        return
      }
      if (pending.kind === "pathBendNew") {
        if (Math.hypot(e.clientX - pointerDownX, e.clientY - pointerDownY) < 5) return
        setPointerRay(e)
        if (pathTargetsObject()) movePathHandle(pending.index, 1)
        else bendNewPointOnPlane(pending.index)
        return
      }
      if (Math.hypot(e.clientX - pointerDownX, e.clientY - pointerDownY) > OBJECT_DRAG_CLICK_PX) {
        const effect = resolvePlatformMove({
          pending,
          moved: true,
          hasSectorSelection: false,
          currentCell: null,
        })
        platformPending = effect.pending
        if (effect.type === "pathMovePoint" || effect.type === "pathBend") {
          setPointerRay(e)
          applyPathDrag(effect.pending, e)
        } else if (effect.type === "pathLook") {
          platformPending = null
          draggingLook = true
          lastX = e.clientX
          lastY = e.clientY
          canvas.classList.add("dragging")
        }
      }
      return
    }
    if (pinching || activePointers.size >= 2) return
    const movedPastClick = Math.hypot(e.clientX - pointerDownX, e.clientY - pointerDownY) > OBJECT_DRAG_CLICK_PX
    if (!platformPaint && platformPending) {
      const moveEffect = resolvePlatformMove({
        pending: platformPending,
        moved: movedPastClick,
        hasSectorSelection: hasSectorSelection(),
        currentCell: sectorCellAt(e.clientX, e.clientY),
      })
      if (moveEffect.type === "cancelActivate") {
        platformPending = null
      } else if (moveEffect.type === "idleStartLook") {
        platformPending = moveEffect.pending
        if (navMode === "fly" && !transformDragging && !deviceLookActive) {
          draggingLook = true
          lastX = e.clientX
          lastY = e.clientY
          canvas.classList.add("dragging")
        }
      } else if (moveEffect.type === "promoteMarquee") {
        platformPending = null
        if (moveEffect.prelude) {
          onPlatformSectors?.([], false, { prelude: true })
          platformSelectedSectorCount = 0
          if (platformDropState) {
            const cleared = { ...platformDropState, selectedSectors: [] }
            platformDropState = cleared
            platformLayer.setState(cleared)
          }
        }
        platformPaint = {
          kind: "sector",
          anchor: moveEffect.paint.anchor,
          current: moveEffect.paint.current,
          shift: moveEffect.paint.shift,
        }
        const rect = sectorRectangle(moveEffect.paint.anchor, moveEffect.paint.current)
        platformLayer.previewSectors(new Set(rect.map((item) => `${item.x}:${item.z}`)))
      } else {
        platformPending = moveEffect.pending
      }
    }
    if (platformFacePending && !platformPaint && movedPastClick) {
      if (platformFacePending.tile) startOpeningPaintFromPending()
    }
    if (lightDragPending && !lightDragging && movedPastClick) {
      beginLightDrag(e)
    }
    if (lightDragging) {
      updateLightDrag(e)
      e.preventDefault()
      return
    }
    if (
      objectDragPendingUid &&
      !objectDragging &&
      Math.hypot(e.clientX - pointerDownX, e.clientY - pointerDownY) > OBJECT_DRAG_CLICK_PX
    ) {
      beginObjectDrag(objectDragPendingUid, e)
      objectDragPendingUid = null
    }
    if (objectDragging) {
      updateObjectDrag(e)
      e.preventDefault()
      return
    }
    if (platformPaint) {
      if (platformPaint.kind === "sector") {
        const cell = sectorCellAt(e.clientX, e.clientY) ?? platformPaint.current
        platformPaint.current = cell
        const rect = sectorRectangle(platformPaint.anchor, cell)
        platformLayer.previewSectors(new Set(rect.map((item) => `${item.x}:${item.z}`)))
      } else {
        const hit = platformLayer.hitAt(e.clientX, e.clientY)
        if (hit?.kind === "opening") {
          const paintSide = faceSideFromKey(platformPaint.anchor.faceKey)
          const hitSide = faceSideFromKey(hit.faceKey)
          if (hit.faceKey === platformPaint.current.faceKey || (paintSide && paintSide === hitSide)) {
            platformPaint.current = { faceKey: hit.faceKey, u: hit.u, v: hit.v }
          }
        }
        const rect = faceTileRectangle(platformPaint.anchor, platformPaint.current)
        platformLayer.previewOpenings(new Set(rect.map((tile) => `${tile.faceKey}#${tile.u}:${tile.v}`)))
      }
      return
    }
    if (!draggingLook) return
    const dx = e.clientX - lastX
    const dy = e.clientY - lastY
    lastX = e.clientX
    lastY = e.clientY
    const steerInPlace =
      (pathLookThrough && !exporting && !lookRecording && !lookRewriteArmed) ||
      lookRecording ||
      lookRewriteArmed ||
      recordArmed ||
      recording ||
      e.shiftKey
    if (axisHold && steerInPlace && (dx !== 0 || dy !== 0)) releaseAxisHold()
    if (pathLookThrough && !exporting && !lookRecording && !lookRewriteArmed) {
      steerLookThrough(dx, dy)
      return
    }
    // Запись пролёта, запись взгляда и Shift — поворот на месте: орбита уводит камеру с траектории.
    if (lookRecording || lookRewriteArmed || recordArmed || recording || e.shiftKey) {
      tYaw -= dx * 0.0034
      tPitch = clamp(tPitch - dy * 0.0034, -PITCH_MAX, PITCH_MAX)
      return
    }
    orbitAroundFocus(dx, dy)
  }
  const onPointerUp = (e: PointerEvent) => {
    if (platformPending?.kind === "cameraPlace" || platformPending?.kind === "cameraPlaceMiss") {
      const pending = platformPending
      platformPending = null
      const moved = Math.hypot(e.clientX - pointerDownX, e.clientY - pointerDownY) > OBJECT_DRAG_CLICK_PX
      const effect = resolvePlatformUp({
        pending,
        moved,
        hasSectorSelection: false,
        cellAtUp: null,
        cancelled: e.type === "pointercancel",
      })
      try {
        canvas.releasePointerCapture(e.pointerId)
      } catch {
        // already released
      }
      releaseDraggingLook()
      canvas.classList.remove("dragging")
      applyCameraPlacePointerUp(effect, e)
      return
    }
    if (cameraPlaceMode) {
      releaseDraggingLook()
      canvas.classList.remove("dragging")
      if (canvas.hasPointerCapture(e.pointerId)) {
        try {
          canvas.releasePointerCapture(e.pointerId)
        } catch {
          // already released
        }
      }
      return
    }
    if (lookRewriteDown) {
      lookRewriteDown = false
      const moved =
        draggingLook || Math.hypot(e.clientX - pointerDownX, e.clientY - pointerDownY) > OBJECT_DRAG_CLICK_PX
      if (draggingLook) {
        releaseDraggingLook()
        canvas.classList.remove("dragging")
      }
      try {
        canvas.releasePointerCapture(e.pointerId)
      } catch {
        // already released
      }
      if (e.type !== "pointercancel" && !moved && lookRewriteArmed) commitLookRewrite()
      return
    }
    if (inputMode() === "lookThrough") {
      const wasLook = draggingLook
      releaseDraggingLook()
      canvas.classList.remove("dragging")
      if (canvas.hasPointerCapture(e.pointerId)) {
        try {
          canvas.releasePointerCapture(e.pointerId)
        } catch {
          // already released
        }
      }
      if (playing && wasLook) onCameraPoseCommit?.()
      return
    }
    if (cameraIsMoving() && !objectDragging && !lightDragging && !transformDragging) {
      const wasLook = draggingLook
      releaseDraggingLook()
      canvas.classList.remove("dragging")
      objectDragPendingUid = null
      platformPending = null
      platformFacePending = null
      platformPaint = null
      platformConsumed = false
      lightDragPending = false
      clearPlatformPaintPreviews()
      ignoreClickAfterPinch = false
      if (canvas.hasPointerCapture(e.pointerId)) {
        try {
          canvas.releasePointerCapture(e.pointerId)
        } catch {
          // already released
        }
      }
      if (playing && wasLook) onCameraPoseCommit?.()
      return
    }
    if (isPathPending(platformPending)) {
      pathPointDragIndex = null
      if (pinching || ignoreClickAfterPinch) {
        platformPending = null
        ignoreClickAfterPinch = false
      } else {
        const pending = platformPending
        const travel = Math.hypot(e.clientX - pointerDownX, e.clientY - pointerDownY)
        const moved = travel > OBJECT_DRAG_CLICK_PX
        if (pending.kind === "pathBendNew" && travel >= 5) {
          platformPending = null
          commitAuthoring(currentPoints())
        } else {
          const effect = resolvePlatformUp({
            pending,
            moved,
            hasSectorSelection: false,
            cellAtUp: null,
            cameraMoving: cameraIsMoving(),
          })
          platformPending = null
          applyPathUp(effect, e)
          if (effect.type === "noop" && pending.kind === "pathEmpty" && !pending.alt && !moved && !pathDraw) {
            if (selectedPathPoint != null) {
              selectedPathPoint = null
              syncPathOverlay()
            }
          }
        }
      }
      try {
        canvas.releasePointerCapture(e.pointerId)
      } catch {
        // already released
      }
      updateHoverInsert(e)
      return
    }
    if (pinching || ignoreClickAfterPinch) {
      ignoreClickAfterPinch = false
      objectDragPendingUid = null
      platformPending = null
      platformFacePending = null
      lightDragPending = false
      releaseDraggingLook()
      canvas.classList.remove("dragging")
      return
    }
    if (pointerLookOnly()) {
      releaseDraggingLook()
      canvas.classList.remove("dragging")
      platformPending = null
      platformFacePending = null
      platformPaint = null
      platformConsumed = false
      objectDragPendingUid = null
      lightDragPending = false
      if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId)
      return
    }
    const dx = e.clientX - pointerDownX
    const dy = e.clientY - pointerDownY
    if (lightDragging) {
      finishLightDrag()
      lightDragPending = false
      platformPending = null
      platformFacePending = null
      return
    }
    if (lightDragPending) {
      lightDragPending = false
      platformPending = null
      platformFacePending = null
      const moved = Math.hypot(dx, dy) > OBJECT_DRAG_CLICK_PX
      if (!moved) {
        if (clearOnlyIfCrossType("light")) return
        clearPlatformSelection()
        selectObject(null, { clearLight: false })
        setLightSelected(true)
      }
      return
    }
    if (objectDragging) {
      finishObjectDrag()
      objectDragPendingUid = null
      platformPending = null
      platformFacePending = null
      return
    }
    if (platformPending?.kind === "idleClear") {
      platformPending = null
      platformFacePending = null
      if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId)
      applyPlatformUpEffect({ type: "clear" })
      return
    }
    const wasLook = draggingLook
    releaseDraggingLook()
    canvas.classList.remove("dragging")
    if (platformPaint) {
      platformPending = null
      platformFacePending = null
      const finished = platformPaint
      platformPaint = null
      platformConsumed = false
      if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId)
      const paintMoved = Math.hypot(dx, dy) > OBJECT_DRAG_CLICK_PX
      if (finished.kind === "sector") {
        const cell = sectorCellAt(e.clientX, e.clientY) ?? finished.current
        const rect = sectorPaintCells(finished.anchor, cell)
        if (rect.length === 0) {
          clearPlatformPaintPreviews()
          clearPlatformSelection()
          deselectObjectIfEmptyClick(e, paintMoved)
          return
        }
        onPlatformSectors?.(rect, finished.shift)
        platformLayer.previewSectors(new Set())
      } else {
        const rect = faceTileRectangle(finished.anchor, finished.current)
        platformLayer.previewOpenings(new Set())
        onPlatformFaceTiles?.(rect, finished.shift)
      }
      deselectObjectIfEmptyClick(e, paintMoved)
      return
    }
    if (
      platformPending?.kind === "sectorPaint" ||
      platformPending?.kind === "activate" ||
      platformPending?.kind === "activateEnvironment"
    ) {
      const pending = platformPending
      platformPending = null
      platformFacePending = null
      platformConsumed = false
      if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId)
      const moved = Math.hypot(dx, dy) > OBJECT_DRAG_CLICK_PX
      const cellAtUp = sectorCellAt(e.clientX, e.clientY)
      if (pending.kind === "sectorPaint") platformLayer.previewSectors(new Set())
      applyPlatformUpEffect(
        resolvePlatformUp({
          pending,
          moved,
          hasSectorSelection: hasSectorSelection(),
          cellAtUp,
          cameraMoving: cameraIsMoving(),
        }),
        { x: e.clientX, y: e.clientY },
      )
      deselectObjectIfEmptyClick(e, moved)
      return
    }
    if (platformFacePending) {
      platformPending = null
      if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId)
      const faceMoved = Math.hypot(dx, dy) > OBJECT_DRAG_CLICK_PX
      commitSurfaceFacePending()
      deselectObjectIfEmptyClick(e, faceMoved)
      return
    }
    if (platformConsumed) {
      platformPending = null
      platformConsumed = false
      if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId)
      const consumedMoved = Math.hypot(dx, dy) > OBJECT_DRAG_CLICK_PX
      deselectObjectIfEmptyClick(e, consumedMoved)
      return
    }
    if (deviceLookActive || playing || transformDragging) {
      if (playing && wasLook) onCameraPoseCommit?.()
      objectDragPendingUid = null
      platformPending = null
      platformFacePending = null
      if (wasLook && canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId)
      return
    }
    if (objectDragPendingUid) {
      selectObject(objectDragPendingUid)
      objectDragPendingUid = null
      return
    }
    const clickMoved = Math.hypot(dx, dy) > OBJECT_DRAG_CLICK_PX
    // Look-drag: no object pick.
    if (wasLook && clickMoved) return
    const uid = pickFromEvent(e)
    if (uid && e.shiftKey) selectObject(uid, { shift: true })
    else selectObject(uid)
  }

  const onKeyDown = (e: KeyboardEvent) => {
    if (isTypingTarget(e.target)) return
    if (e.code === "Escape") {
      if (
        platformPending?.kind === "cameraPlace" ||
        platformPending?.kind === "cameraPlaceMiss" ||
        cameraPlaceDrag ||
        cameraPlacePreview
      ) {
        const pending =
          platformPending?.kind === "cameraPlace" || platformPending?.kind === "cameraPlaceMiss"
            ? platformPending
            : { kind: "cameraPlace" as const, index: cameraPlaceDrag?.index ?? 0, onObject: true }
        platformPending = null
        const effect = resolvePlatformUp({
          pending,
          moved: false,
          hasSectorSelection: false,
          cellAtUp: null,
          cancelled: true,
        })
        applyCameraPlacePointerUp(effect, null)
      }
      if (objectPathUid) {
        objectPathUid = null
        selectedPathPoint = null
        pathInsertHover = null
        pathPointDragIndex = null
        emitPathEditUi()
        syncPathOverlay()
        syncTransformAttachment()
      }
      dismissEnvironmentChrome()
      if (recordArmed && !recording) disarmRecording()
      if (pathObserveArmed) {
        pathObserveArmed = false
        clearObserveHover()
        emitPathEditUi()
      }
      if (lookRewriteArmed) {
        lookRewriteArmed = false
        lookRewriteDown = false
        emitPathEditUi()
      }
    }
    // Space — play/stop path; Shift+Space — arm/stop recording.
    // Во время записи обычный Space только останавливает запись (не запускает play
    // свежего path — иначе stop+play в одном нажатии).
    if (e.code === "KeyT" && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
      if (inputMode() !== "edit" || recording || recordArmed || exporting || deviceLookActive) return
      e.preventDefault()
      setTrajectoryMode(!trajectoryMode)
      return
    }
    if (e.code === "Space") {
      e.preventDefault()
      if (!e.shiftKey && lookRewriteArmed) {
        commitLookRewrite()
        return
      }
      if (e.shiftKey && trajectoryMode) setTrajectoryMode(false)
      if (e.shiftKey) {
        if (!canRecord) return
        if (recording || recordArmed) stopRecording({ returnHome: true })
        else startRecording()
        return
      }
      if (recording || recordArmed) {
        stopRecording({ returnHome: true })
        return
      }
      togglePathPlayback()
      return
    }
    if (e.code === "Delete" || e.code === "Backspace") {
      if (inputMode() !== "edit") return
      if (pathEditActive()) {
        e.preventDefault()
        if (selectedPathPoint != null && currentPoints().length > 0) {
          const next = deletePoint(currentPoints(), selectedPathPoint)
          selectedPathPoint = next.length > 0 ? Math.min(selectedPathPoint, next.length - 1) : null
          commitAuthoring(next)
        }
        return
      }
      if (isCompleteGroup(selectedUids, selectionObjects())) {
        removeObjects(selectedUids.slice())
        e.preventDefault()
      } else if (selectedUids.length === 1 && selectedUid) {
        removeObject(selectedUid)
        e.preventDefault()
      }
      return
    }
    if (inputMode() === "lookThrough" && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const moveKey = KEYMAP[e.code]
      if (moveKey) releaseLookThroughToFreeView()
    }
    if (inputMode() !== "edit" || navMode !== "fly" || viewFollowsPath()) return
    if (e.metaKey || e.ctrlKey || e.altKey) return
    const k = KEYMAP[e.code]
    if (k) {
      // Взведённая запись стартует с первым движением камеры.
      if (recordArmed) beginRecording()
      keys.add(k)
      e.preventDefault()
    }
  }
  const onKeyUp = (e: KeyboardEvent) => {
    const k = KEYMAP[e.code]
    if (k) keys.delete(k)
  }

  canvas.addEventListener("pointerdown", onPointerDown)
  canvas.addEventListener("pointermove", onPointerMove)
  canvas.addEventListener("pointerleave", onCanvasPointerLeave)
  canvas.addEventListener("pointerup", onPointerUp)
  canvas.addEventListener("pointercancel", onPointerUp)
  stage.addEventListener("pointerdown", onPinchPointerDown, true)
  stage.addEventListener("pointermove", onPinchPointerMove, true)
  stage.addEventListener("pointerup", onPinchPointerUp, true)
  stage.addEventListener("pointercancel", onPinchPointerUp, true)
  stage.addEventListener("wheel", onWheel, { passive: false })
  window.addEventListener("keydown", onKeyDown)
  window.addEventListener("keyup", onKeyUp)

  let lastResizeW = 0
  let lastResizeH = 0
  let resizeRaf = 0
  let raf = 0
  let prev = performance.now()
  let destroyed = false

  function resetCanvasCss() {
    canvas.style.objectFit = ""
    canvas.style.position = ""
    canvas.style.left = ""
    canvas.style.top = ""
    canvas.style.transform = ""
    canvas.style.width = "100%"
    canvas.style.height = "100%"
    canvas.style.visibility = ""
  }

  // Fill: stage aspect. Stage: wider view so the padded frame matches the stored fov.
  // Phone modals use stage the whole time they are open. Samples always keep camera.fov.
  function syncLiveProjection(): boolean {
    if (destroyed || exporting || capturingStill) return false
    const w = Math.round(stage.clientWidth)
    const h = Math.round(stage.clientHeight)
    if (w < 32 || h < 32) return false
    const stageFrame = deviceLookActive || recordArmed || recording || pathLookThrough
    const mode: "stage" | "fill" = stageFrame ? "stage" : "fill"
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const frameAspect = frameFormatRatio(normalizeFrameFormat(frameFormat))
    let resized = false

    if (lastResizeW !== w || lastResizeH !== h) {
      lastResizeW = w
      lastResizeH = h
      renderer.setPixelRatio(dpr)
      skyDome.setPixelRatio(renderer.getPixelRatio())
      // Keep CSS 100% — setSize(..., true) writes integer px and leaves a 1px seam.
      renderer.setSize(w, h, false)
      resetCanvasCss()
      pathOverlay.setResolution(w, h)
      resized = true
    }

    if (mode === "stage") {
      const rect = frameViewfinderRect(w, h, frameAspect)
      const saved = camera.fov
      const display = frameDisplayVFov(h, rect.height, saved)
      camera.aspect = w / h
      camera.fov = display
      camera.updateProjectionMatrix()
      camera.fov = saved
      return resized
    }

    camera.aspect = w / h
    camera.updateProjectionMatrix()
    return resized
  }

  function applyResize() {
    if (destroyed || exporting) return
    if (canvas.style.visibility === "hidden") {
      lastResizeW = 0
      lastResizeH = 0
    }
    if (!syncLiveProjection()) return
    syncPathOverlay()
    // setSize clears the drawing buffer; paint immediately so the stage bg does not flash
    skyDome.syncToCamera(camera)
    themeRig?.syncToCamera(camera)
    withObjectsAboveFloor(() => {
      renderer.render(scene, camera)
    })
  }

  function resize() {
    if (exporting) return
    if (resizeRaf) return
    resizeRaf = requestAnimationFrame(() => {
      resizeRaf = 0
      applyResize()
    })
  }

  const resizeObserver = new ResizeObserver(resize)
  resizeObserver.observe(stage)
  applyResize()

  function disposeCameraPlaceMonitor() {
    cameraPlaceMonitorTarget?.dispose()
    cameraPlaceMonitorTarget = null
    cameraPlaceMonitorPixels = null
    cameraPlaceMonitorImage = null
  }

  function cameraPlaceShownPose(): { pos: PathVec3; yaw: number; pitch: number } | null {
    if (!cameraPlaceMode) return null
    if (cameraPlacePreview) {
      return {
        pos: cameraPlacePreview.pos,
        yaw: cameraPlacePreview.yaw,
        pitch: cameraPlacePreview.pitch,
      }
    }
    const point = shownCameraPath().authoring?.points[0]
    if (!point) return null
    return {
      pos: point.pos,
      yaw: cameraPlaceAim?.yaw ?? 0,
      pitch: cameraPlaceAim?.pitch ?? 0,
    }
  }

  function cameraPlaceMonitorPose(): { pos: PathVec3; yaw: number; pitch: number } | null {
    if (!cameraPlaceMonitorCanvas) return null
    return cameraPlaceShownPose()
  }

  function paintCameraPlaceMonitor() {
    const pose = cameraPlaceMonitorPose()
    const monitor = cameraPlaceMonitorCanvas
    if (!pose || !monitor) return
    if (exporting || capturingStill) {
      cameraPlaceMonitorSceneDirty = true
      return
    }
    const cssW = monitor.clientWidth
    const cssH = monitor.clientHeight
    if (cssW < 2 || cssH < 2) return
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const width = Math.round(cssW * dpr * 2)
    const height = Math.round(cssH * dpr * 2)
    const frameKey = cameraPlaceMonitorFrameKey(pose, camera.fov, width, height)
    const bitmapMatches = monitor.width === width && monitor.height === height
    if (frameKey === cameraPlaceMonitorFrame && !cameraPlaceMonitorSceneDirty && bitmapMatches) return
    if (
      !cameraPlaceMonitorTarget ||
      cameraPlaceMonitorTarget.width !== width ||
      cameraPlaceMonitorTarget.height !== height
    ) {
      cameraPlaceMonitorTarget?.dispose()
      cameraPlaceMonitorTarget = new THREE.WebGLRenderTarget(width, height, {
        colorSpace: THREE.SRGBColorSpace,
      })
      cameraPlaceMonitorPixels = new Uint8Array(width * height * 4)
      cameraPlaceMonitorImage = null
    }
    const shot = cameraPlaceMonitorCam
    shot.fov = camera.fov
    shot.aspect = width / height
    shot.near = camera.near
    shot.far = camera.far
    shot.position.set(pose.pos[0], pose.pos[1], pose.pos[2])
    shot.rotation.set(pose.pitch, pose.yaw, 0)
    shot.updateProjectionMatrix()
    const target = cameraPlaceMonitorTarget
    const pixels = cameraPlaceMonitorPixels
    if (!target || !pixels) return
    const overlayWas = pathOverlay.group.visible
    const dotsWere = cameraPlaceMesh?.visible ?? false
    const helperWas = transform.getHelper().visible
    pathOverlay.group.visible = false
    if (cameraPlaceMesh) cameraPlaceMesh.visible = false
    transform.getHelper().visible = false
    meshes.forEach((obj) => syncLabels(obj, false))
    skyDome.syncToCamera(shot)
    themeRig?.syncToCamera(shot)
    // Теневая карта точечной лампы от камеры не зависит: этот кадр её уже построил основной вид.
    const shadowAutoUpdate = renderer.shadowMap.autoUpdate
    renderer.shadowMap.autoUpdate = false
    try {
      renderer.setRenderTarget(target)
      renderer.clear()
      withObjectsAboveFloor(() => {
        renderer.render(scene, shot)
      })
    } finally {
      renderer.shadowMap.autoUpdate = shadowAutoUpdate
      renderer.setRenderTarget(null)
      pathOverlay.group.visible = overlayWas
      if (cameraPlaceMesh) cameraPlaceMesh.visible = dotsWere
      transform.getHelper().visible = helperWas
      meshes.forEach((obj) => syncLabels(obj, labelsVisible()))
      skyDome.syncToCamera(camera)
      themeRig?.syncToCamera(camera)
    }
    renderer.readRenderTargetPixels(target, 0, 0, width, height, pixels)
    if (monitor.width !== width || monitor.height !== height) {
      monitor.width = width
      monitor.height = height
      cameraPlaceMonitorImage = null
    }
    const ctx = monitor.getContext("2d")
    if (!ctx) return
    const image = cameraPlaceMonitorImage ?? ctx.createImageData(width, height)
    cameraPlaceMonitorImage = image
    for (let row = 0; row < height; row += 1) {
      const src = (height - row - 1) * width * 4
      image.data.set(pixels.subarray(src, src + width * 4), row * width * 4)
    }
    ctx.putImageData(image, 0, 0)
    cameraPlaceMonitorFrame = frameKey
    cameraPlaceMonitorSceneDirty = false
  }

  function loop(now: number) {
    if (destroyed) return
    const dt = Math.min(0.05, (now - prev) / 1000)
    prev = now

    const userSteers =
      draggingLook ||
      deviceLookActive ||
      keys.size > 0 ||
      pointerKeys.size > 0 ||
      analogStrafe !== 0 ||
      analogVertical !== 0 ||
      lookYawRate !== 0
    if ((viewAnim || poseAnim) && userSteers) cancelViewAnimation()
    if (returnHome && !userSteers && !playing && !exporting && !poseAnim && !viewAnim) {
      const target = returnHome
      returnHome = null
      animateToCameraState(target, () => {
        returnGlide = false
      })
      returnGlide = true
    }
    if (pathRewind?.started && userSteers) cancelPathRewind()
    else if (pathRewind && !pathRewind.started && !userSteers) {
      pathRewind = { ...pathRewind, t0: now, started: true }
    }
    if (
      axisHold &&
      (deviceLookActive ||
        keys.size > 0 ||
        pointerKeys.size > 0 ||
        analogStrafe !== 0 ||
        analogVertical !== 0 ||
        lookYawRate !== 0)
    ) {
      releaseAxisHold()
    }

    let endPlayback = false
    if (playing) {
      const t = Math.min(playFromMs + (now - playStartedAt), playToMs)
      pathTimeMs = t
      poseObjectMeshes(t)
      if (lookRecording && !exporting) {
        const sample = sampleAt(t)
        if (sample) applyCameraPosition(sample.pos, sample.fov)
        applyFlyLook(dt)
        captureLookKey(t)
      } else if (exporting) {
        const sample = sampleAt(t)
        if (sample) applyCameraPose(sample.pos, sample.yaw, sample.pitch, sample.fov)
      } else if (pathLookThrough || deviceLookFollowsPath) {
        const sample = sampleAt(t)
        if (sample) {
          applyCameraPosition(sample.pos, sample.fov)
          if (!deviceLookFollowsPath) {
            const posed = previewLook(sample.yaw, sample.pitch)
            axisHold = false
            camera.up.set(0, 1, 0)
            flyYaw = posed.yaw
            flyPitch = posed.pitch
            tYaw = posed.yaw
            tPitch = posed.pitch
            camera.rotation.set(posed.pitch, posed.yaw, 0)
          }
        }
      } else {
        updateFly(dt)
      }
      onPathTimeChange?.(t)
      if (t >= playToMs) endPlayback = true
    } else if (viewAnim) {
      stepViewAnimation(now)
    } else if (poseAnim) {
      stepPoseAnimation(now)
    } else if (pathRewind?.started) {
      stepPathRewind(now)
    } else if (!exporting) {
      updateFly(dt)
    }
    if (!playing && pathTimeMs > 0) poseObjectMeshes(pathTimeMs)

    recordIfNeeded()
    syncLiveProjection()
    if (playing || pathRewind?.started || overlayCameraMoved()) syncPathOverlay()
    canvas.classList.toggle(
      "scene-path-draw",
      pathEditActive() && pathDraw && !draggingLook && !pathObserveArmed && !lookRewriteArmed,
    )
    canvas.classList.toggle("scene-path-pick", trajectoryMode && pathObserveArmed && !draggingLook)

    const holdExportFrame = exporting && !playing
    if (!capturingStill && !holdExportFrame) {
      const showLabels = labelsVisible()
      meshes.forEach((obj) => syncLabels(obj, showLabels))
      skyDome.syncToCamera(camera)
      themeRig?.syncToCamera(camera)
      withObjectsAboveFloor(() => {
        renderer.render(scene, camera)
      })
      paintCameraPlaceMonitor()
    }
    restoreObjectMeshes()
    if (endPlayback) stopPlayback()
    emitViewOrientation()
    raf = requestAnimationFrame(loop)
  }
  pathOverlayMounted = true
  syncPathOverlay()
  const appThemeObserver = new MutationObserver(() => syncPathOverlay())
  appThemeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] })
  raf = requestAnimationFrame(loop)

  function detachExport() {
    if (exportDetached || destroyed) return
    exportDetached = true
    uiMuted = true
    resizeObserver.disconnect()
    window.removeEventListener("keydown", onKeyDown)
    window.removeEventListener("keyup", onKeyUp)
    const holder = document.createElement("div")
    holder.setAttribute("data-previz-export", "")
    holder.style.cssText =
      "position:fixed;left:0;top:0;width:0;height:0;overflow:hidden;pointer-events:none;visibility:hidden"
    document.body.appendChild(holder)
    holder.appendChild(canvas)
    exportHolder = holder
  }

  function applyPathLookThrough(on: boolean) {
    if (!on && lookRecording) finishLookRecording()
    if (!on) clearLookPreview()
    if (on && !pathLookThrough) {
      lookThroughReturn = currentCameraState()
      finishObjectDrag()
      selectObject(null, { clearLight: true })
      clearPlatformSelection()
      detachTransform()
      selectedPathPoint = null
    }
    pathLookThrough = on
    if (on) {
      const sample = lookThroughSample()
      if (sample) {
        applyCameraPose(sample.pos, sample.yaw, sample.pitch, sample.fov)
        reanchorOrbitTarget()
      }
    } else if (lookThroughReturn) {
      const pose = lookThroughReturn
      lookThroughReturn = null
      applyCameraPose(pose.pos, pose.yaw, pose.pitch, pose.fov)
      if (pose.target) orbit.target.set(...pose.target)
    }
    emitPathEditUi()
    syncPathOverlay()
  }

  return {
    addObject,
    removeObject,
    removeObjects,
    setObjectColor,
    setObjectFigure,
    selectObject,
    groupSelection,
    ungroupSelection,
    frameObject,
    getSelectedUid: () => selectedUid,
    getSelectedUids: () => selectedUids.slice(),
    getSelectionGizmoAnchorScreen,
    getLightGizmoAnchorScreen,
    getPlatformSelectionToolbarAnchorScreen,
    getLightIntensity,
    setLightIntensity,
    getLightIntensityRange,
    setGizmoMode(mode) {
      gizmoMode = mode
      transform.setMode(mode)
      syncTransformAttachment()
    },
    getGizmoMode: () => gizmoMode,
    setNavMode,
    getNavMode: () => navMode,
    setFov(fov) {
      camera.fov = clamp(fov, 25, 100)
      camera.updateProjectionMatrix()
      syncLiveProjection()
      const points = cameraPath.authoring?.points
      if (points && points.length > 0) commitAuthoring(points)
    },
    getFov: () => camera.fov,
    setFlySpeed(speed) {
      flySpeed = clamp(speed, 0.5, 20)
    },
    getFlySpeed: () => flySpeed,
    setCameraHeight,
    getCameraHeight: () => camera.position.y,
    dropViewHint,
    captureStillBlob,
    animateToView,
    animateToCameraState,
    orbitAroundFocus,
    isViewAnimating: () => viewAnim != null || poseAnim != null || pathRewind?.started === true,
    setFlyLook,
    getFlyLook: () => {
      if (!axisHold) return { yaw: tYaw, pitch: tPitch }
      const theta = Math.atan2(-axisHoldUp.x, -axisHoldUp.z)
      const pitch = camera.position.y >= axisHoldFocus.y ? -PITCH_MAX : PITCH_MAX
      return { yaw: theta, pitch }
    },
    setFlyInput,
    setFlyHeld,
    clearFlyInput,
    setFlyAnalogAxes,
    setFlyThrust,
    setLookYawRate,
    setDeviceLookActive,
    setDeviceLookFollowsPath,
    setPlatformState(state) {
      platformLayer.setState(state)
      platformDropState = state
      platformSelectedSectorCount = state.selectedSectors.length
      platformSelectedFaceCount = state.selectedFaceTiles.length
      platformSurfaceGrid = state.surfaceGrid
      if (state.platform) {
        const span = platformLayer.span()
        syncOrbitTravelBound(span)
        applyLightPose(state.light, { retainIntensityIfOmitted: true })
        syncCameraScale(span)
        lightMarker.visible = lightMarkerAllowed()
      } else {
        syncCameraScale(0)
        syncOrbitTravelBound(0)
        lightMarker.visible = false
        lightMarker.scale.setScalar(lightMarkerRadius(FLY_SPEED_REFERENCE_SPAN))
        point.position.set(6, 12, 8)
        setLightSelected(false)
        applyPointLightParams(FLY_SPEED_REFERENCE_SPAN)
      }
      syncSceneTheme()
      if (cameraPlaceMode) rebuildCameraPlaceMarkers()
      markCameraPlaceMonitorDirty()
    },
    patchWallTileColors(tiles, hex) {
      platformLayer.patchWallTileColors(tiles, hex)
      const visual = platformDropState
      if (!visual?.platform) return
      platformDropState = {
        ...visual,
        faces: applyWallTileColors(visual.faces, tiles, hex, visual.cells, visual.platform),
      }
    },
    patchSolidFaceColors(faceKeys, hex) {
      platformLayer.patchSolidFaceColors(faceKeys, hex)
      const visual = platformDropState
      if (!visual) return
      const keySet = new Set(faceKeys)
      const faces = visual.faces.map((face) =>
        keySet.has(face.face_key) ? { ...face, solid_color: hex, status: "ready" as const } : face,
      )
      for (const faceKey of faceKeys) {
        if (faces.some((face) => face.face_key === faceKey)) continue
        faces.push({ face_key: faceKey, origin: "upload", status: "ready", solid_color: hex })
      }
      platformDropState = { ...visual, faces }
    },
    clearSolidFaceColors(faceKeys) {
      platformLayer.clearSolidFaceColors(faceKeys)
      const visual = platformDropState
      if (!visual) return
      const keySet = new Set(faceKeys)
      platformDropState = {
        ...visual,
        faces: visual.faces.map((face) => (keySet.has(face.face_key) ? { ...face, solid_color: null } : face)),
      }
    },
    setPlatformTool(tool) {
      if (tool === platformTool) return
      platformTool = tool
      platformPaint = null
      platformPending = null
      platformFacePending = null
    },
    setPlatformLight(side) {
      applyLightPose(
        {
          preset: side,
          position: lightPosition(side, platformLayer.span()),
        },
        { retainIntensityIfOmitted: true },
      )
    },
    setLightState(light) {
      applyLightPose(light)
    },
    applyEnvironment,
    getEnvironment: () => ({ ...environmentState }),
    getSceneState() {
      commitActiveTake()
      return {
        objects: collectObjects(),
        camera_state: currentCameraState(),
        camera_path: cameraPath,
        camera_paths: cameraPathTakes.map((take) => ({
          id: take.id,
          name: take.name,
          path: cloneCameraPath(take.path),
        })),
        active_camera_path_id: activeCameraPathTakeId,
        environment: { ...environmentState },
      }
    },
    applySceneState(state) {
      const normalized = normalizeSceneLayout(state)
      suppressEmit = true
      let fitting = false
      try {
        clearObjects()
        normalized.objects.forEach((obj) => addObject(obj))
        selectObject(null)
        objectPathUid = null
        previewObjectPathUid = null
        const cam = normalized.camera_state
        applyCameraPose(cam.pos, cam.yaw, cam.pitch, cam.fov)
        if (cam.target) orbit.target.set(...cam.target)
        if (cam.mode) setNavMode(cam.mode)
        dropPathFit()
        if ("camera_paths" in state) {
          cameraPathTakes = (normalized.camera_paths ?? []).map((take) => ({
            id: take.id,
            name: take.name,
            path: cloneCameraPath(take.path),
          }))
          activeCameraPathTakeId = normalized.active_camera_path_id ?? null
        }
        cameraPath = normalized.camera_path
        fitting = pathNeedsFit(cameraPath)
        if (fitting) schedulePathFit(cameraPath, false)
        adoptPathSettings()
        // Missing environment means "keep the live sky". normalizeSceneLayout would
        // otherwise fill the default, and undo (objects + camera only) would reset it.
        if (state.environment != null) applyEnvironment(normalizeSceneEnvironment(state.environment))
      } finally {
        suppressEmit = false
      }
      if (!fitting) onCameraPathChange?.(cameraPath)
      emitObjects()
      emitPathEditUi()
      syncPathOverlay()
      if (cameraPlaceMode) {
        const placeGesture =
          platformPending?.kind === "cameraPlace" ||
          platformPending?.kind === "cameraPlaceMiss" ||
          cameraPlaceDrag != null ||
          cameraPlacePreview != null
        if (placeGesture) {
          platformPending = null
          applyCameraPlacePointerUp({ type: "cameraPlaceCancel" }, null)
        } else {
          clearCameraPlaceSelection()
        }
      }
    },
    applyCameraState(cam) {
      const normalized = normalizeCameraState(cam)
      applyCameraPose(normalized.pos, normalized.yaw, normalized.pitch, normalized.fov)
      if (normalized.target) orbit.target.set(...normalized.target)
      if (normalized.mode) setNavMode(normalized.mode)
    },
    startRecording,
    stopRecording,
    isRecording: () => recording || lookRecording,
    isRecordingArmed: () => recordArmed,
    playPath,
    stopPlayback,
    togglePathPlayback,
    isPathAtEnd,
    isPlaying: () => playing,
    seekPath,
    placeAtPathStart,
    replacePath,
    getCameraPathTakes: () => {
      commitActiveTake()
      return cloneCameraPathTakeSet({ takes: cameraPathTakes, activeId: activeCameraPathTakeId })
    },
    addCameraPathTake,
    selectCameraPathTake,
    renameCameraPathTake,
    deleteCameraPathTake,
    restoreCameraPathTakeFromHistory,
    restoreCameraPathTakesFromHistory,
    exportPathVideo,
    clearPath() {
      cancelReturnHome()
      cancelLookRecording()
      if (recording) stopRecording()
      dropPathFit()
      if (playing) stopPlayback()
      cameraPath = emptyCameraPath()
      if (pathLook.mode === "recorded") pathLook = { mode: "tangent" }
      selectedPathPoint = null
      pathTimeMs = 0
      onCameraPathChange?.(cameraPath)
      emitPathEditUi()
      syncPathOverlay()
    },
    getCameraPath: () => cameraPath,
    whenPathFitSettled: () => pathFit.settled(),
    setTrajectoryMode,
    isTrajectoryMode: () => trajectoryMode,
    setCameraPlaceMode,
    setCameraPlaceAim,
    setCameraPlaceMonitor(canvas: HTMLCanvasElement | null) {
      cameraPlaceMonitorCanvas = canvas
      cameraPlaceMonitorFrame = ""
      cameraPlaceMonitorSceneDirty = true
      if (!canvas) disposeCameraPlaceMonitor()
    },
    isCameraPlaceMode: () => cameraPlaceMode,
    setObjectPathEdit(uid: string | null) {
      const next = uid && objectPathUid === uid ? null : uid
      if (!next) {
        objectPathUid = null
        pathPointDragIndex = null
        selectedPathPoint = null
        pathInsertHover = null
        emitPathEditUi()
        syncPathOverlay()
        syncTransformAttachment()
        return
      }
      if (!pathEditAllowed || !canEdit || deviceLookActive || recording || recordArmed || exporting) return
      if (!meshes.has(next)) return
      previewObjectPathUid = null
      if (trajectoryMode) setTrajectoryMode(false)
      objectPathUid = next
      const path = objectPathRecord(next)
      pathDraw = true
      pathObserveArmed = false
      lookRewriteArmed = false
      lookRewriteDown = false
      selectedPathPoint = null
      pathInsertHover = null
      pathDurationMs = defaultObjectPathDuration(path, cameraPathDurationMs(shownCameraPath()))
      const standing = objectPathRecord(next)?.authoring?.points ?? []
      if (standing.length === 0) {
        const mesh = meshes.get(next)
        const data = mesh?.userData as SceneObjectState | undefined
        const position = data?.position
        if (position) {
          commitObjectAuthoring([{ pos: [...position] as [number, number, number], tangent: null }])
        } else {
          emitPathEditUi()
          syncPathOverlay()
        }
      } else {
        emitPathEditUi()
        syncPathOverlay()
      }
      syncTransformAttachment()
    },
    getObjectPathEditUid: () => (pathTargetsObject() ? objectPathUid : null),
    showObjectPath(uid: string | null) {
      if (!uid || previewObjectPathUid === uid) {
        previewObjectPathUid = null
        emitPathEditUi()
        syncPathOverlay()
        return
      }
      if (!meshes.has(uid)) return
      const path = objectPathRecord(uid)
      if (!path || !objectPathHasContent(path)) return
      if (pathTargetsObject()) {
        objectPathUid = null
        pathPointDragIndex = null
        selectedPathPoint = null
        pathInsertHover = null
        syncTransformAttachment()
      }
      if (trajectoryMode) setTrajectoryMode(false)
      previewObjectPathUid = uid
      emitPathEditUi()
      syncPathOverlay()
      frameObject(uid)
    },
    hasSceneMotion: () => sceneMotionNow(),
    getSceneClockMs: () => sceneClockNow(),
    setPathEditAllowed,
    setPathDraw(draw) {
      pathDraw = draw
      if (!draw) {
        selectedPathPoint = null
        pathInsertHover = null
      }
      emitPathEditUi()
      if (!draw) syncPathOverlay()
    },
    setPathSmooth(smooth) {
      pathSmooth = smooth
      const points = currentPoints()
      if (points.length > 0) commitAuthoring(points)
      else emitPathEditUi()
    },
    setPathLook(look) {
      cancelLookRecording()
      lookRewriteArmed = false
      lookRewriteDown = false
      const keepTarget =
        look.mode === "recorded" &&
        !recordedLookHasTarget(look) &&
        pathLook.mode === "recorded" &&
        recordedLookHasTarget(pathLook)
      pathLook = keepTarget ? pathLook : look
      pathObserveArmed = false
      clearObserveHover()
      const points = currentPoints()
      if (points.length > 0) commitAuthoring(points)
      else emitPathEditUi()
    },
    setPathDuration(ms) {
      pathDurationMs = clampSplineDuration(ms)
      const points = currentPoints()
      if (points.length > 0) commitAuthoring(points)
      else emitPathEditUi()
    },
    setCameraSpeed,
    setObjectSpeed(uid, profile) {
      const path = objectPathRecord(uid)
      if (!path || !objectPathHasContent(path)) return
      writeObjectPath(uid, objectPathDurationMs(path), profile)
    },
    setObjectPathDuration(uid, ms) {
      const path = objectPathRecord(uid)
      if (!path || !objectPathHasContent(path)) return
      const next = clampSplineDuration(ms)
      const from = objectPathDurationMs(path)
      const speed = path.speed ? scaleSpeedProfile(path.speed, from, next) : null
      writeObjectPath(uid, next, speed)
    },
    clearObjectPath(uid) {
      const mesh = meshes.get(uid)
      if (!mesh) return
      const data = mesh.userData as SceneObjectState
      if (!data.path) return
      delete data.path
      if (objectPathUid === uid) {
        objectPathUid = null
        pathPointDragIndex = null
        selectedPathPoint = null
        pathInsertHover = null
        syncTransformAttachment()
      }
      if (previewObjectPathUid === uid) previewObjectPathUid = null
      emitObjects()
      emitPathEditUi()
      syncPathOverlay()
    },
    isPathLookThrough: () => pathLookThrough,
    discardLookPreview,
    setPathLookThrough: applyPathLookThrough,
    beginLookRecording() {
      beginLookPass()
    },
    armLookRewrite,
    startLookRewrite() {
      armLookRewrite()
      commitLookRewrite()
    },
    cancelLookPass,
    armObservePick() {
      if (pathLookThrough) applyPathLookThrough(false)
      const uid = pathLook.mode === "observe" ? pathLook.object_uid : ""
      pathLook = { mode: "observe", object_uid: uid }
      pathObserveArmed = true
      emitPathEditUi()
    },
    resize,
    isExporting: () => exporting || exportFinish != null || exportReject != null,
    isExportDetached: () => exportDetached,
    detachExport,
    destroy() {
      if (destroyed) return
      // Запись на отсоединённом холсте ещё идёт. Уничтожение оборвало бы ролик.
      if (exportDetached && (exporting || exportReject != null || exportFinish != null)) return
      destroyed = true
      hideCameraPlaceMarkers()
      disposeCameraPlaceMonitor()
      pathOverlay.dispose()
      deviceLookActive = false
      lookYawRate = 0
      keys.clear()
      pointerKeys.clear()
      releaseDraggingLook()
      cancelReturnHome()
      if (recording || recordArmed) stopRecording()
      dropPathFit()
      if (exporting || exportReject != null || exportFinish != null) {
        abortExport("Scene destroyed")
      } else if (playing) {
        stopPlayback()
      }
      cancelAnimationFrame(raf)
      if (resizeRaf) cancelAnimationFrame(resizeRaf)
      resizeRaf = 0
      appThemeObserver.disconnect()
      resizeObserver.disconnect()
      skyDome.dispose()
      themeRig?.dispose()
      scene.environment = null
      paletteEnvTexture.dispose()
      canvas.removeEventListener("pointerdown", onPointerDown)
      canvas.removeEventListener("pointermove", onPointerMove)
      canvas.removeEventListener("pointerleave", onCanvasPointerLeave)
      canvas.removeEventListener("pointerup", onPointerUp)
      canvas.removeEventListener("pointercancel", onPointerUp)
      stage.removeEventListener("pointerdown", onPinchPointerDown, true)
      stage.removeEventListener("pointermove", onPinchPointerMove, true)
      stage.removeEventListener("pointerup", onPinchPointerUp, true)
      stage.removeEventListener("pointercancel", onPinchPointerUp, true)
      stage.removeEventListener("wheel", onWheel)
      window.removeEventListener("keydown", onKeyDown)
      window.removeEventListener("keyup", onKeyUp)
      // Teardown must not emit onObjectsChange([]) — callers persist that and wipe saved scene.
      suppressEmit = true
      clearObjects()
      transform.disconnect()
      transform.dispose()
      orbit.dispose()
      platformLayer.dispose()
      lightMarker.geometry.dispose()
      ;(lightMarker.material as THREE.Material).dispose()
      scene.remove(groupPivot)
      scene.remove(lightMarkerGroup)
      scene.remove(markerKey)
      scene.remove(point)
      floor.geometry.dispose()
      ;(floor.material as THREE.Material).dispose()
      grid.geometry.dispose()
      const gridMat = grid.material
      if (Array.isArray(gridMat)) gridMat.forEach((m) => m.dispose())
      else gridMat.dispose()
      renderer.dispose()
      if (exportHolder) {
        exportHolder.remove()
        exportHolder = null
      } else if (!givenCanvas) {
        canvas.remove()
      }
    },
  }
}
